// Web CRM (Business plane) — SolutionBank suite static-UI translations.
// Split out of translations.js only for file size; merged into the same flat per-language lookup at
// load time (see translations.js). Same rules: STATIC UI CHROME ONLY, English default + fallback,
// never translate dynamic/user/backend content (solution titles/answers, generated FAQ text, tenant
// knowledge, API status enums used in logic, metric field names). Interpolated {values} are dynamic.
//
// Scope: components/Business/SolutionBank.jsx + SolutionBank/{KnowledgeBuilder,LiveAiKnowledgePanel,
// SolutionBankReview}.jsx. All keys prefixed `sb.*`. Common buttons/labels (Save, Cancel, Approve,
// Refresh, etc.) intentionally reuse the shared common.* set and are NOT redefined here.
//
// Every language below carries the identical key set; missing keys would simply fall back to English.

const solutionBankTranslations = {
  en: {
    // ---- SolutionBank.jsx: tabs ----
    'sb.tab.solutions': 'Solutions',
    'sb.tab.knowledgeBuilder': 'AI Knowledge Builder',
    'sb.tab.review': 'Review',
    'sb.tab.analytics': 'Analytics',
    'sb.tab.liveAi': 'Live AI Knowledge',
    'sb.tab.addSolution': 'Add Solution',
    'sb.tab.manageSolutions': 'Manage Solutions',
    // ---- SolutionBank.jsx: header status ----
    'sb.exporting': 'Exporting...',
    'sb.importing': 'Importing...',
    // ---- SolutionBank.jsx: search / filter ----
    'sb.searchPlaceholder': 'Search solutions...',
    'sb.filterSolutions': 'Filter Solutions',
    'sb.filterSolutionsDesc': 'Narrow down solutions by criteria',
    'sb.difficulty': 'Difficulty',
    'sb.allDifficulties': 'All Difficulties',
    'sb.easy': 'Easy',
    'sb.medium': 'Medium',
    'sb.hard': 'Hard',
    'sb.reset': 'Reset',
    'sb.apply': 'Apply',
    'sb.allCategories': 'All Categories',
    'sb.revoke.action': 'Revoke',
    'sb.revoke.confirm': 'Revoke this approved solution? It stops being used by AI until it is corrected and approved again.',
    'sb.revoke.done': 'Solution revoked',
    'sb.revoke.doneDesc': 'It is no longer used by AI. Edit it, then resubmit for review.',
    'sb.revoke.failed': 'Could not revoke',
    'sb.cat.choose': 'Choose a category',
    'sb.cat.noneConfigured': 'No categories configured',
    'sb.cat.noneConfiguredHint': 'Add categories in Category Management before authoring solutions.',
    // ---- SolutionBank.jsx: category labels (display only; 'all' key never translated) ----
    'sb.cat.technicalSupport': 'Technical Support',
    'sb.cat.billing': 'Billing',
    'sb.cat.productIssues': 'Product Issues',
    'sb.cat.accountManagement': 'Account Management',
    // ---- SolutionBank.jsx: stat cards ----
    'sb.totalSolutions': 'Total Solutions',
    'sb.avgSuccessRate': 'Avg Success Rate',
    'sb.totalUsage': 'Total Usage',
    'sb.categories': 'Categories',
    'sb.topPerforming': 'Top Performing Solutions',
    'sb.mostUsed': 'Most Used Solutions',
    // ---- SolutionBank.jsx: list / empty / loading ----
    'sb.loadingSolutions': 'Loading solutions...',
    'sb.loadingSolutionsSub': 'Please wait while we fetch your solution bank',
    'sb.noSolutionsFound': 'No solutions found',
    'sb.noSolutionsHint': 'Try adjusting your search criteria or use the Management tab to add solutions',
    'sb.mgmtFunctionsHint': 'Solution management functions are available in the Management tab',
    'sb.successRateBadge': '{rate}% Success Rate',
    'sb.usesBadge': '{count} Uses',
    'sb.usesLower': '{count} uses',
    'sb.updated': 'Updated: {date}',
    // ---- SolutionBank.jsx: Add Solution tab ----
    'sb.quickTips': 'Quick Tips',
    'sb.tip1': 'Be specific and clear in solution descriptions',
    'sb.tip2': 'Include step-by-step instructions',
    'sb.tip3': 'Tag solutions with relevant keywords',
    'sb.tip4': 'Reference related solutions for complex issues',
    'sb.recentActivity': 'Recent Activity',
    'sb.noRecentActivity': 'No recent activity yet.',
    'sb.solutionsAddedToday': 'Solutions added today:',
    'sb.pendingReviewLabel': 'Pending review:',
    'sb.successRateLabel': 'Success rate:',
    // ---- SolutionBank.jsx: Manage tab ----
    'sb.bulkOperations': 'Bulk Operations',
    'sb.importFromCsv': 'Import Solutions from CSV',
    'sb.exportAll': 'Export All Solutions',
    'sb.generateReport': 'Generate Performance Report',
    // ---- SolutionBank.jsx: import dialog ----
    'sb.importCsvDesc': 'Upload a CSV file to import multiple solutions. The CSV should have columns: title, description, category, tags, resolution, difficulty, success_rate, usage_count.',
    'sb.selectCsv': 'Select a CSV file to upload',
    'sb.selectedFile': 'Selected: {name}',
    'sb.importSolutionsBtn': 'Import Solutions',
    // ---- SolutionBank.jsx: export dialog ----
    'sb.exportAllDesc': 'Choose your preferred export format for all solutions',
    'sb.csvFile': 'CSV File',
    'sb.excelFile': 'Excel File',
    'sb.jsonData': 'JSON Data',
    'sb.pdfReport': 'PDF Report',
    // ---- SolutionBank.jsx: report dialog ----
    'sb.performanceReport': 'Solution Bank Performance Report',
    'sb.performanceReportDesc': 'AI-generated insights and performance analysis',
    'sb.aiAnalysisReport': 'AI Analysis Report',
    'sb.downloadReport': 'Download Report',
    // ---- SolutionBank.jsx: add / edit dialogs ----
    'sb.editSolution': 'Edit Solution',
    'sb.editSolutionDesc': 'Update the solution information',
    'sb.addNewSolutionTitle': 'Add New Solution',
    'sb.addNewSolutionDesc': 'Create a new solution for your solution bank',
    'sb.solutionTitle': 'Solution Title *',
    'sb.solutionTitlePlaceholder': 'Enter solution title',
    'sb.descriptionLabel': 'Description *',
    'sb.descriptionPlaceholder': 'Describe the problem this solution addresses',
    'sb.category': 'Category',
    'sb.tagsLabel': 'Tags (comma-separated)',
    'sb.tagsPlaceholder': 'password, reset, login',
    'sb.resolutionSteps': 'Resolution Steps',
    'sb.resolutionPlaceholder': 'Enter step-by-step resolution instructions',
    'sb.updateSolution': 'Update Solution',
    'sb.addSolutionBtn': 'Add Solution',
    // ---- SolutionBank.jsx: delete dialog ----
    'sb.deleteSolution': 'Delete Solution',
    'sb.deleteSolutionDesc': 'Are you sure you want to delete this solution? This action cannot be undone.',
    // ---- SolutionBank.jsx: toasts ----
    'sb.toast.loadFailed': 'Failed to load solutions',
    'sb.toast.missingInfo': 'Missing Information',
    'sb.toast.missingInfoDesc': 'Please fill in title and description',
    'sb.toast.submitted': 'Solution Submitted',
    'sb.toast.submittedDesc': 'Solution has been forwarded to the Manager for approval',
    'sb.toast.addFailed': 'Failed to add solution',
    'sb.toast.exportStarted': 'Export Started',
    'sb.toast.exportStartedDesc': 'Exporting solutions as {format}',
    'sb.toast.exportComplete': 'Export Complete',
    'sb.toast.exportCompleteDesc': 'Solutions exported successfully as {format}',
    'sb.toast.exportFailed': 'Export Failed',
    'sb.toast.exportFailedDesc': 'Failed to export solutions',
    'sb.toast.noFile': 'No File Selected',
    'sb.toast.noFileDesc': 'Please select a CSV file to import',
    'sb.toast.importStarted': 'Import Started',
    'sb.toast.importStartedDesc': 'Processing CSV file...',
    'sb.toast.importComplete': 'Import Complete',
    'sb.toast.importCompleteDesc': 'Solutions imported successfully',
    'sb.toast.importFailed': 'Import Failed',
    'sb.toast.importFailedDesc': 'Failed to import solutions from CSV',
    'sb.toast.invalidFile': 'Invalid File',
    'sb.toast.invalidFileDesc': 'Please select a valid CSV file',
    'sb.toast.reportGenerated': 'Report Generated',
    'sb.toast.reportGeneratedDesc': 'Performance report has been generated successfully',
    'sb.toast.reportFailed': 'Report Generation Failed',
    'sb.toast.reportFailedDesc': 'Failed to generate performance report',
    'sb.toast.updated': 'Solution Updated',
    'sb.toast.updatedDesc': 'Solution has been updated successfully',
    'sb.toast.updateFailed': 'Failed to update solution',
    'sb.toast.deleted': 'Solution Deleted',
    'sb.toast.deletedDesc': 'Solution has been deleted successfully',
    'sb.toast.deleteFailed': 'Failed to delete solution',
    // ---- KnowledgeBuilder.jsx: header ----
    'sb.kb.title': 'AI Knowledge Builder',
    'sb.kb.subtitle': 'Intake your business info → generate 50 draft FAQs → review & approve. Drafts stay internal until approved.',
    'sb.kb.newSession': 'New session',
    'sb.kb.buildTitle': 'Build your AI knowledge base',
    'sb.kb.buildBlurb': 'Answer what you know about your business. We will generate 50 draft/suggested FAQs. Nothing goes live to customers until a manager or admin approves it.',
    'sb.kb.start': 'Start Knowledge Builder',
    // ---- KnowledgeBuilder.jsx: tabs ----
    'sb.kb.tab.intake': 'Intake',
    'sb.kb.tab.validate': 'Validate',
    'sb.kb.tab.review': 'Generate & Review',
    'sb.kb.tab.coverage': 'Coverage',
    'sb.kb.tab.gaps': 'Gaps',
    // ---- KnowledgeBuilder.jsx: intake controls ----
    'sb.kb.skipHint': 'Skip anything unknown — unknown fields are never invented.',
    'sb.kb.saveProgress': 'Save progress',
    'sb.kb.blankUnknown': 'Leave blank if unknown',
    'sb.kb.blankUnknownTextarea': 'Leave blank if unknown — it won’t be invented',
    'sb.kb.unknownSkip': 'Unknown / skip',
    // ---- KnowledgeBuilder.jsx: section titles ----
    'sb.kb.sec.identity': 'Business Identity',
    'sb.kb.sec.profile': 'Business Profile',
    'sb.kb.sec.operations': 'Operations',
    'sb.kb.sec.payments': 'Payments',
    'sb.kb.sec.delivery': 'Shipping / Delivery',
    'sb.kb.sec.booking': 'Booking',
    'sb.kb.sec.returns': 'Returns / Refunds',
    'sb.kb.sec.support': 'Support',
    'sb.kb.sec.policies': 'Policies',
    'sb.kb.sec.extras': 'Customer Questions & Notes',
    // ---- KnowledgeBuilder.jsx: field labels ----
    'sb.kb.f.business_name': 'Business Name',
    'sb.kb.f.address': 'Address',
    'sb.kb.f.city': 'City',
    'sb.kb.f.province': 'Province / State',
    'sb.kb.f.country': 'Country',
    'sb.kb.f.phone': 'Phone',
    'sb.kb.f.email': 'Email',
    'sb.kb.f.website': 'Website',
    'sb.kb.f.social_media': 'Social Media',
    'sb.kb.f.business_description': 'Business description',
    'sb.kb.f.products': 'Products sold',
    'sb.kb.f.services': 'Services provided',
    'sb.kb.f.target_customers': 'Target customers',
    'sb.kb.f.industries_served': 'Industries served',
    'sb.kb.f.differentiator': 'Differentiator',
    'sb.kb.f.mission_vision': 'Mission / vision',
    'sb.kb.f.opening_days': 'Opening days',
    'sb.kb.f.operating_hours': 'Opening hours',
    'sb.kb.f.holiday_hours': 'Holiday hours',
    'sb.kb.f.appointment_required': 'Appointment required',
    'sb.kb.f.walk_ins_accepted': 'Walk-ins accepted',
    'sb.kb.f.delivery_available': 'Delivery available',
    'sb.kb.f.pickup_available': 'Pickup available',
    'sb.kb.f.online_orders': 'Online orders',
    'sb.kb.f.service_area': 'Service area',
    'sb.kb.f.payment_methods': 'Accepted payment methods',
    'sb.kb.f.delivery_locations': 'Delivery locations',
    'sb.kb.f.delivery_fees': 'Delivery fees',
    'sb.kb.f.delivery_timelines': 'Delivery timelines',
    'sb.kb.f.same_day_delivery': 'Same-day delivery',
    'sb.kb.f.international_shipping': 'International shipping',
    'sb.kb.f.booking_method': 'How appointments are booked',
    'sb.kb.f.cancellation_policy': 'Cancellation policy',
    'sb.kb.f.rescheduling_policy': 'Rescheduling policy',
    'sb.kb.f.late_arrival_policy': 'Late arrival policy',
    'sb.kb.f.ordering_method': 'How online orders are placed',
    'sb.kb.f.return_policy': 'Return policy',
    'sb.kb.f.refund_policy': 'Refund policy',
    'sb.kb.f.exchange_policy': 'Exchange policy',
    'sb.kb.f.contact_methods': 'Primary contact methods',
    'sb.kb.f.support_phone': 'Support phone',
    'sb.kb.f.support_whatsapp': 'WhatsApp',
    'sb.kb.f.support_email': 'Support email',
    'sb.kb.f.support_website': 'Support website',
    'sb.kb.f.support_in_person': 'In-person support',
    'sb.kb.f.privacy_policy': 'Privacy policy',
    'sb.kb.f.warranty_policy': 'Warranty policy',
    'sb.kb.f.terms_of_service': 'Terms of service',
    'sb.kb.f.service_guarantees': 'Service guarantees',
    'sb.kb.f.policies': 'General policies summary',
    'sb.kb.f.common_questions': 'Common questions customers ask',
    'sb.kb.f.special_instructions': 'What customers should always know',
    // ---- KnowledgeBuilder.jsx: payment options ----
    'sb.kb.pay.cash': 'Cash',
    'sb.kb.pay.debit': 'Debit',
    'sb.kb.pay.credit': 'Credit',
    'sb.kb.pay.bankTransfer': 'Bank transfer',
    'sb.kb.pay.applePay': 'Apple Pay',
    'sb.kb.pay.googlePay': 'Google Pay',
    'sb.kb.pay.payPal': 'PayPal',
    'sb.kb.pay.mobileMoney': 'Mobile Money',
    'sb.kb.pay.interac': 'Interac e-Transfer',
    'sb.kb.pay.other': 'Other',
    // ---- KnowledgeBuilder.jsx: validate ----
    'sb.kb.revalidate': 'Re-validate',
    'sb.kb.runValidationHint': 'Run validation from the Intake tab to see readiness.',
    'sb.kb.readiness': 'Readiness',
    'sb.kb.readinessHint': 'Generation is never blocked. Missing business-specific facts become a Suggested FAQ or the answer "Please confirm this policy with the business." — never invented.',
    'sb.kb.val.missingFields': 'Missing fields',
    'sb.kb.val.missingFieldsEmpty': 'Nothing required is missing.',
    'sb.kb.val.contradictions': 'Contradictions',
    'sb.kb.val.contradictionsEmpty': 'No contradictions found.',
    'sb.kb.val.vague': 'Vague answers',
    'sb.kb.val.vagueEmpty': 'No vague answers.',
    'sb.kb.val.followups': 'Follow-up questions',
    'sb.kb.val.followupsEmpty': 'No follow-ups.',
    'sb.kb.completeMore': '← Complete more fields',
    'sb.kb.continueGenerate': 'Continue to Generate →',
    // ---- KnowledgeBuilder.jsx: generate & review ----
    'sb.kb.generate50': 'Generate 50 Draft FAQs',
    'sb.kb.generate50Sub': 'Runs validation + AI generation via the real backend. Drafts are internal only until approved.',
    'sb.kb.generating': 'Generating…',
    'sb.kb.internalVsLive': 'Draft & suggested = internal only. Approved = live for AI Chat/Voice.',
    'sb.kb.showArchived': 'Show archived',
    'sb.kb.noGeneratedYet': 'No generated FAQs yet. Click Generate 50 Draft FAQs above.',
    // ---- KnowledgeBuilder.jsx: status badges ----
    'sb.kb.status.draft': 'Draft · internal only',
    'sb.kb.status.suggested': 'Suggested · internal only',
    'sb.kb.status.pending': 'Pending approval · internal',
    'sb.kb.status.approved': 'Approved · live for AI',
    'sb.kb.status.archived': 'Archived',
    'sb.kb.status.rejected': 'Rejected',
    // ---- KnowledgeBuilder.jsx: source badges ----
    'sb.kb.ownerSupplied': 'Owner-supplied',
    'sb.kb.aiSuggested': 'AI-suggested',
    // ---- KnowledgeBuilder.jsx: coverage ----
    'sb.kb.totalGenerated': 'Total generated',
    'sb.kb.customerFacing': 'Customer-facing (approved)',
    'sb.kb.internalDraft': 'Internal (draft/suggested)',
    'sb.kb.categoryCoverage': 'Category coverage',
    'sb.kb.missing': 'missing',
    'sb.kb.missingCategories': 'Missing categories: {cats}. Generate (or add more intake) to cover them.',
    // ---- KnowledgeBuilder.jsx: gaps ----
    'sb.kb.gapsIntro': 'Unanswered customer questions recorded for this business.',
    'sb.kb.noGaps': 'No knowledge gaps recorded.',
    'sb.kb.createDraftFaq': 'Create draft FAQ',
    'sb.kb.gapsLoadError': 'Knowledge gaps couldn’t be loaded: {error}',
    'sb.kb.seenTimes': 'Seen {count}× · {status}',
    // ---- KnowledgeBuilder.jsx: dialogs ----
    'sb.kb.editDraftFaq': 'Edit draft FAQ',
    'sb.kb.createDraftFromGap': 'Create draft FAQ from gap',
    'sb.kb.question': 'Question',
    'sb.kb.answer': 'Answer',
    'sb.kb.keywords': 'Keywords (comma-sep)',
    'sb.kb.answerPlaceholder': 'Write the answer. Leave tenant-specifics out if unknown.',
    'sb.kb.editInternalHint': 'Editing keeps this internal. Approve separately to make it live.',
    'sb.kb.createdDraftHint': 'Created as a draft (internal). Approve it to go live.',
    'sb.kb.saveDraft': 'Save draft',
    'sb.kb.createDraft': 'Create draft',
    // ---- KnowledgeBuilder.jsx: guards ----
    'sb.kb.noBusinessContext': 'No active business context',
    'sb.kb.selectBusiness': 'Select or link a business to use the Knowledge Builder.',
    // ---- KnowledgeBuilder.jsx: toasts ----
    'sb.kb.toast.noBusiness': 'No business selected',
    'sb.kb.toast.noBusinessDesc': 'Cannot start — no active business context.',
    'sb.kb.toast.started': 'Knowledge Builder started',
    'sb.kb.toast.startedDesc': 'Fill in what you know — you can skip anything unknown.',
    'sb.kb.toast.couldNotStart': 'Could not start session',
    'sb.kb.toast.progressSaved': 'Progress saved',
    'sb.kb.toast.savedFields': '{count} field(s) saved.',
    'sb.kb.toast.nothingToSave': 'Nothing new to save.',
    'sb.kb.toast.saveFailed': 'Save failed',
    'sb.kb.toast.validationFailed': 'Validation failed',
    'sb.kb.toast.draftsGenerated': 'Draft FAQs generated',
    'sb.kb.toast.draftsGeneratedDesc': '{count} draft/suggested items created (internal only).',
    'sb.kb.toast.generationFailed': 'Generation failed',
    'sb.kb.toast.providerUnavailable': 'The AI generation provider is unavailable right now. No drafts were created. Please try again later.',
    'sb.kb.toast.loadGenFailed': 'Could not load generated FAQs',
    'sb.kb.toast.approved': 'Approved',
    'sb.kb.toast.approvedDesc': 'This FAQ is now live for AI Chat & Voice immediately.',
    'sb.kb.toast.approvalFailed': 'Approval failed',
    'sb.kb.toast.rejected': 'Rejected',
    'sb.kb.toast.rejectedDesc': 'This draft will not be used by AI.',
    'sb.kb.toast.rejectFailed': 'Reject failed',
    'sb.kb.toast.archived': 'Archived',
    'sb.kb.toast.archivedDesc': 'Item retired (excluded from AI retrieval).',
    'sb.kb.toast.archiveFailed': 'Archive failed',
    'sb.kb.toast.draftUpdated': 'Draft updated',
    'sb.kb.toast.draftUpdatedDesc': 'Your edit was saved. Still internal until approved.',
    'sb.kb.toast.updateFailed': 'Update failed',
    'sb.kb.toast.draftCreated': 'Draft FAQ created',
    'sb.kb.toast.draftCreatedDesc': 'Created as a draft (internal). Approve it to go live.',
    'sb.kb.toast.couldNotCreate': 'Could not create draft',
    // ---- LiveAiKnowledgePanel.jsx ----
    'sb.live.approvalsImmediate': 'Approvals take effect immediately — no retraining delay',
    'sb.live.approvalsBlurb': 'Approved solutions are available to AI Chat and AI Voice immediately. No retraining delay is required because Servisfy reads approved SolutionBank knowledge live on each AI interaction.',
    'sb.live.aiChatLive': 'AI Chat: live',
    'sb.live.aiVoiceLive': 'AI Voice: live',
    'sb.live.business': 'Business: {name}',
    'sb.live.liveInAi': 'Live in AI (approved)',
    'sb.live.usedByAiNow': 'Used by AI Chat & Voice now',
    'sb.live.pendingApproval': 'Pending approval',
    'sb.live.notYetUsed': 'Not yet used by AI',
    'sb.live.draftsSuggested': 'Drafts / suggested',
    'sb.live.internalOnly': 'Internal only (Knowledge Builder)',
    'sb.live.inactive': 'Inactive',
    'sb.live.rejectedRevoked': 'Rejected / revoked / archived',
    'sb.live.totalUsage': 'Total AI usage (approved)',
    'sb.live.realUsageSum': 'Real usage_count sum',
    'sb.live.avgSuccess': 'Avg success rate',
    'sb.live.noUsageYet': 'No usage recorded yet',
    'sb.live.acrossUsed': 'Across used approved solutions',
    'sb.live.lastApproved': 'Last approved',
    'sb.live.effectiveImmediately': 'Effective immediately',
    'sb.live.noApprovalsYet': 'No approvals yet',
    'sb.live.topUsed': 'Top-used approved solutions',
    'sb.live.topUsedEmpty': 'No approved solutions have been used by AI yet.',
    'sb.live.recentlyApproved': 'Recently approved',
    'sb.live.recentlyApprovedEmpty': 'No approved solutions yet. Approve from Review to activate.',
    'sb.live.lowSuccess': 'Approved but low success — may need review',
    'sb.live.lowSuccessEmpty': 'No low-success approved solutions. 👍',
    'sb.live.lowSuccessHint': 'Approved solutions used by AI with success rate under {threshold}%.',
    'sb.live.loading': 'Loading live AI knowledge…',
    'sb.live.loadError': 'Couldn’t load AI knowledge',
    'sb.live.tryAgain': 'Try again',
    'sb.live.loadFailedToast': 'Failed to load AI knowledge',
    'sb.live.untitled': 'Untitled',
    'sb.live.uncategorized': 'Uncategorized',
    // ---- SolutionBankReview.jsx ----
    'sb.review.title': 'Solution Bank Review',
    'sb.review.subtitle': 'Review and approve AI-suggested solutions for the Solution Bank',
    'sb.review.pendingReview': 'Pending Review',
    'sb.review.approvedTab': 'Approved',
    'sb.review.rejectedTab': 'Rejected',
    'sb.review.noPending': 'No pending solutions to review',
    'sb.review.noPendingSub': 'AI-suggested solutions will appear here for approval',
    'sb.review.noApproved': 'No approved solutions',
    'sb.review.noRejected': 'No rejected solutions',
    'sb.review.description': 'Description:',
    'sb.review.resolutionSteps': 'Resolution Steps:',
    'sb.review.keywords': 'Keywords:',
    'sb.review.submittedBy': 'Submitted by {name}',
    'sb.review.staff': 'staff',
    'sb.review.aiVoice': 'AI Voice',
    'sb.review.aiChat': 'AI Chat',
    'sb.review.ch.humanChat': 'Human chat',
    'sb.review.ch.voiceCall': 'Voice call',
    'sb.review.ch.humanCall': 'Human call',
    'sb.review.ch.solutionBank': 'Solution Bank',
    'sb.review.ch.unknown': 'Unknown',
    'sb.review.aiGenerated': 'AI Generated',
    'sb.review.openaiFallback': 'OpenAI fallback',
    'sb.review.sourceConversation': 'Source: Conversation {id}',
    'sb.review.confHigh': 'High ({pct}%)',
    'sb.review.confMedium': 'Medium ({pct}%)',
    'sb.review.confLow': 'Low ({pct}%)',
    'sb.review.activeInAi': 'Active in AI',
    'sb.review.approvedOn': 'Approved {date}',
    'sb.review.rejectedOn': 'Rejected {date}',
    'sb.review.revoke': 'Revoke',
    'sb.review.resubmit': 'Resubmit for Review',
    'sb.review.unknownDate': 'Unknown',
    'sb.review.loadFailedDesc': 'Check your connection and try again.',
    'sb.review.toast.approved': 'Solution Approved',
    'sb.review.toast.approvedDesc': 'Solution is now live in the AI knowledge base.',
    'sb.review.toast.approvalFailed': 'Approval Failed',
    'sb.review.toast.rejected': 'Solution Rejected',
    'sb.review.toast.rejectedDesc': 'Solution will not be used by AI.',
    'sb.review.toast.rejectionFailed': 'Rejection Failed',
    'sb.review.toast.revoked': 'Solution Revoked',
    'sb.review.toast.revokedDesc': 'AI will no longer use this solution.',
    'sb.review.toast.revokeFailed': 'Revoke Failed',
    'sb.review.toast.resubmitted': 'Solution Resubmitted',
    'sb.review.toast.resubmittedDesc': 'Solution is pending review again.',
    'sb.review.toast.resubmitFailed': 'Resubmit Failed',
    'sb.review.toast.deleted': 'Solution Deleted',
    'sb.review.toast.deletedDesc': 'Solution permanently removed.',
    'sb.review.toast.deleteFailed': 'Delete Failed',
  },
  es: {
    'sb.tab.solutions': 'Soluciones',
    'sb.tab.knowledgeBuilder': 'Constructor de conocimiento IA',
    'sb.tab.review': 'Revisión',
    'sb.tab.analytics': 'Analíticas',
    'sb.tab.liveAi': 'Conocimiento IA en vivo',
    'sb.tab.addSolution': 'Añadir solución',
    'sb.tab.manageSolutions': 'Gestionar soluciones',
    'sb.exporting': 'Exportando...',
    'sb.importing': 'Importando...',
    'sb.searchPlaceholder': 'Buscar soluciones...',
    'sb.filterSolutions': 'Filtrar soluciones',
    'sb.filterSolutionsDesc': 'Acote las soluciones por criterios',
    'sb.difficulty': 'Dificultad',
    'sb.allDifficulties': 'Todas las dificultades',
    'sb.easy': 'Fácil',
    'sb.medium': 'Media',
    'sb.hard': 'Difícil',
    'sb.reset': 'Restablecer',
    'sb.apply': 'Aplicar',
    'sb.allCategories': 'Todas las categorías',
    'sb.revoke.action': 'Revocar',
    'sb.revoke.confirm': '¿Revocar esta solución aprobada? Dejará de ser utilizada por la IA hasta que se corrija y se apruebe de nuevo.',
    'sb.revoke.done': 'Solución revocada',
    'sb.revoke.doneDesc': 'Ya no la utiliza la IA. Edítela y vuelva a enviarla para revisión.',
    'sb.revoke.failed': 'No se pudo revocar',
    'sb.cat.choose': 'Elija una categoría',
    'sb.cat.noneConfigured': 'No hay categorías configuradas',
    'sb.cat.noneConfiguredHint': 'Agregue categorías en Gestión de categorías antes de crear soluciones.',
    'sb.cat.technicalSupport': 'Soporte técnico',
    'sb.cat.billing': 'Facturación',
    'sb.cat.productIssues': 'Problemas de producto',
    'sb.cat.accountManagement': 'Gestión de cuentas',
    'sb.totalSolutions': 'Total de soluciones',
    'sb.avgSuccessRate': 'Tasa de éxito promedio',
    'sb.totalUsage': 'Uso total',
    'sb.categories': 'Categorías',
    'sb.topPerforming': 'Soluciones con mejor rendimiento',
    'sb.mostUsed': 'Soluciones más usadas',
    'sb.loadingSolutions': 'Cargando soluciones...',
    'sb.loadingSolutionsSub': 'Espere mientras obtenemos su banco de soluciones',
    'sb.noSolutionsFound': 'No se encontraron soluciones',
    'sb.noSolutionsHint': 'Intente ajustar sus criterios de búsqueda o use la pestaña Gestionar para añadir soluciones',
    'sb.mgmtFunctionsHint': 'Las funciones de gestión de soluciones están disponibles en la pestaña Gestionar',
    'sb.successRateBadge': '{rate}% de tasa de éxito',
    'sb.usesBadge': '{count} usos',
    'sb.usesLower': '{count} usos',
    'sb.updated': 'Actualizado: {date}',
    'sb.quickTips': 'Consejos rápidos',
    'sb.tip1': 'Sea específico y claro en las descripciones de las soluciones',
    'sb.tip2': 'Incluya instrucciones paso a paso',
    'sb.tip3': 'Etiquete las soluciones con palabras clave relevantes',
    'sb.tip4': 'Haga referencia a soluciones relacionadas para problemas complejos',
    'sb.recentActivity': 'Actividad reciente',
    'sb.noRecentActivity': 'Aún no hay actividad reciente.',
    'sb.solutionsAddedToday': 'Soluciones añadidas hoy:',
    'sb.pendingReviewLabel': 'Pendientes de revisión:',
    'sb.successRateLabel': 'Tasa de éxito:',
    'sb.bulkOperations': 'Operaciones masivas',
    'sb.importFromCsv': 'Importar soluciones desde CSV',
    'sb.exportAll': 'Exportar todas las soluciones',
    'sb.generateReport': 'Generar informe de rendimiento',
    'sb.importCsvDesc': 'Suba un archivo CSV para importar varias soluciones. El CSV debe tener las columnas: title, description, category, tags, resolution, difficulty, success_rate, usage_count.',
    'sb.selectCsv': 'Seleccione un archivo CSV para subir',
    'sb.selectedFile': 'Seleccionado: {name}',
    'sb.importSolutionsBtn': 'Importar soluciones',
    'sb.exportAllDesc': 'Elija su formato de exportación preferido para todas las soluciones',
    'sb.csvFile': 'Archivo CSV',
    'sb.excelFile': 'Archivo Excel',
    'sb.jsonData': 'Datos JSON',
    'sb.pdfReport': 'Informe PDF',
    'sb.performanceReport': 'Informe de rendimiento del banco de soluciones',
    'sb.performanceReportDesc': 'Perspectivas y análisis de rendimiento generados por IA',
    'sb.aiAnalysisReport': 'Informe de análisis de IA',
    'sb.downloadReport': 'Descargar informe',
    'sb.editSolution': 'Editar solución',
    'sb.editSolutionDesc': 'Actualice la información de la solución',
    'sb.addNewSolutionTitle': 'Añadir nueva solución',
    'sb.addNewSolutionDesc': 'Cree una nueva solución para su banco de soluciones',
    'sb.solutionTitle': 'Título de la solución *',
    'sb.solutionTitlePlaceholder': 'Introduzca el título de la solución',
    'sb.descriptionLabel': 'Descripción *',
    'sb.descriptionPlaceholder': 'Describa el problema que aborda esta solución',
    'sb.category': 'Categoría',
    'sb.tagsLabel': 'Etiquetas (separadas por comas)',
    'sb.tagsPlaceholder': 'contraseña, restablecer, inicio de sesión',
    'sb.resolutionSteps': 'Pasos de resolución',
    'sb.resolutionPlaceholder': 'Introduzca instrucciones de resolución paso a paso',
    'sb.updateSolution': 'Actualizar solución',
    'sb.addSolutionBtn': 'Añadir solución',
    'sb.deleteSolution': 'Eliminar solución',
    'sb.deleteSolutionDesc': '¿Está seguro de que desea eliminar esta solución? Esta acción no se puede deshacer.',
    'sb.toast.loadFailed': 'No se pudieron cargar las soluciones',
    'sb.toast.missingInfo': 'Falta información',
    'sb.toast.missingInfoDesc': 'Complete el título y la descripción',
    'sb.toast.submitted': 'Solución enviada',
    'sb.toast.submittedDesc': 'La solución se ha enviado al gerente para su aprobación',
    'sb.toast.addFailed': 'No se pudo añadir la solución',
    'sb.toast.exportStarted': 'Exportación iniciada',
    'sb.toast.exportStartedDesc': 'Exportando soluciones como {format}',
    'sb.toast.exportComplete': 'Exportación completada',
    'sb.toast.exportCompleteDesc': 'Soluciones exportadas correctamente como {format}',
    'sb.toast.exportFailed': 'Error de exportación',
    'sb.toast.exportFailedDesc': 'No se pudieron exportar las soluciones',
    'sb.toast.noFile': 'Ningún archivo seleccionado',
    'sb.toast.noFileDesc': 'Seleccione un archivo CSV para importar',
    'sb.toast.importStarted': 'Importación iniciada',
    'sb.toast.importStartedDesc': 'Procesando archivo CSV...',
    'sb.toast.importComplete': 'Importación completada',
    'sb.toast.importCompleteDesc': 'Soluciones importadas correctamente',
    'sb.toast.importFailed': 'Error de importación',
    'sb.toast.importFailedDesc': 'No se pudieron importar las soluciones desde CSV',
    'sb.toast.invalidFile': 'Archivo no válido',
    'sb.toast.invalidFileDesc': 'Seleccione un archivo CSV válido',
    'sb.toast.reportGenerated': 'Informe generado',
    'sb.toast.reportGeneratedDesc': 'El informe de rendimiento se ha generado correctamente',
    'sb.toast.reportFailed': 'Error al generar el informe',
    'sb.toast.reportFailedDesc': 'No se pudo generar el informe de rendimiento',
    'sb.toast.updated': 'Solución actualizada',
    'sb.toast.updatedDesc': 'La solución se ha actualizado correctamente',
    'sb.toast.updateFailed': 'No se pudo actualizar la solución',
    'sb.toast.deleted': 'Solución eliminada',
    'sb.toast.deletedDesc': 'La solución se ha eliminado correctamente',
    'sb.toast.deleteFailed': 'No se pudo eliminar la solución',
    'sb.kb.title': 'Constructor de conocimiento IA',
    'sb.kb.subtitle': 'Ingrese la información de su negocio → genere 50 FAQ en borrador → revise y apruebe. Los borradores permanecen internos hasta su aprobación.',
    'sb.kb.newSession': 'Nueva sesión',
    'sb.kb.buildTitle': 'Construya su base de conocimiento de IA',
    'sb.kb.buildBlurb': 'Responda lo que sepa sobre su negocio. Generaremos 50 FAQ en borrador/sugeridas. Nada se publica para los clientes hasta que un gerente o administrador lo apruebe.',
    'sb.kb.start': 'Iniciar Constructor de conocimiento',
    'sb.kb.tab.intake': 'Recopilación',
    'sb.kb.tab.validate': 'Validar',
    'sb.kb.tab.review': 'Generar y revisar',
    'sb.kb.tab.coverage': 'Cobertura',
    'sb.kb.tab.gaps': 'Vacíos',
    'sb.kb.skipHint': 'Omita lo que no sepa: los campos desconocidos nunca se inventan.',
    'sb.kb.saveProgress': 'Guardar progreso',
    'sb.kb.blankUnknown': 'Deje en blanco si lo desconoce',
    'sb.kb.blankUnknownTextarea': 'Deje en blanco si lo desconoce: no se inventará',
    'sb.kb.unknownSkip': 'Desconocido / omitir',
    'sb.kb.sec.identity': 'Identidad del negocio',
    'sb.kb.sec.profile': 'Perfil del negocio',
    'sb.kb.sec.operations': 'Operaciones',
    'sb.kb.sec.payments': 'Pagos',
    'sb.kb.sec.delivery': 'Envío / Entrega',
    'sb.kb.sec.booking': 'Reservas',
    'sb.kb.sec.returns': 'Devoluciones / Reembolsos',
    'sb.kb.sec.support': 'Soporte',
    'sb.kb.sec.policies': 'Políticas',
    'sb.kb.sec.extras': 'Preguntas y notas de clientes',
    'sb.kb.f.business_name': 'Nombre del negocio',
    'sb.kb.f.address': 'Dirección',
    'sb.kb.f.city': 'Ciudad',
    'sb.kb.f.province': 'Provincia / Estado',
    'sb.kb.f.country': 'País',
    'sb.kb.f.phone': 'Teléfono',
    'sb.kb.f.email': 'Correo electrónico',
    'sb.kb.f.website': 'Sitio web',
    'sb.kb.f.social_media': 'Redes sociales',
    'sb.kb.f.business_description': 'Descripción del negocio',
    'sb.kb.f.products': 'Productos vendidos',
    'sb.kb.f.services': 'Servicios ofrecidos',
    'sb.kb.f.target_customers': 'Clientes objetivo',
    'sb.kb.f.industries_served': 'Sectores atendidos',
    'sb.kb.f.differentiator': 'Diferenciador',
    'sb.kb.f.mission_vision': 'Misión / visión',
    'sb.kb.f.opening_days': 'Días de apertura',
    'sb.kb.f.operating_hours': 'Horario de apertura',
    'sb.kb.f.holiday_hours': 'Horario en festivos',
    'sb.kb.f.appointment_required': 'Cita requerida',
    'sb.kb.f.walk_ins_accepted': 'Se aceptan visitas sin cita',
    'sb.kb.f.delivery_available': 'Entrega disponible',
    'sb.kb.f.pickup_available': 'Recogida disponible',
    'sb.kb.f.online_orders': 'Pedidos en línea',
    'sb.kb.f.service_area': 'Área de servicio',
    'sb.kb.f.payment_methods': 'Métodos de pago aceptados',
    'sb.kb.f.delivery_locations': 'Lugares de entrega',
    'sb.kb.f.delivery_fees': 'Tarifas de entrega',
    'sb.kb.f.delivery_timelines': 'Plazos de entrega',
    'sb.kb.f.same_day_delivery': 'Entrega el mismo día',
    'sb.kb.f.international_shipping': 'Envío internacional',
    'sb.kb.f.booking_method': 'Cómo se reservan las citas',
    'sb.kb.f.cancellation_policy': 'Política de cancelación',
    'sb.kb.f.rescheduling_policy': 'Política de reprogramación',
    'sb.kb.f.late_arrival_policy': 'Política de llegada tardía',
    'sb.kb.f.ordering_method': 'Cómo se realizan los pedidos en línea',
    'sb.kb.f.return_policy': 'Política de devoluciones',
    'sb.kb.f.refund_policy': 'Política de reembolsos',
    'sb.kb.f.exchange_policy': 'Política de cambios',
    'sb.kb.f.contact_methods': 'Principales métodos de contacto',
    'sb.kb.f.support_phone': 'Teléfono de soporte',
    'sb.kb.f.support_whatsapp': 'WhatsApp',
    'sb.kb.f.support_email': 'Correo de soporte',
    'sb.kb.f.support_website': 'Sitio web de soporte',
    'sb.kb.f.support_in_person': 'Soporte en persona',
    'sb.kb.f.privacy_policy': 'Política de privacidad',
    'sb.kb.f.warranty_policy': 'Política de garantía',
    'sb.kb.f.terms_of_service': 'Términos del servicio',
    'sb.kb.f.service_guarantees': 'Garantías de servicio',
    'sb.kb.f.policies': 'Resumen de políticas generales',
    'sb.kb.f.common_questions': 'Preguntas frecuentes de los clientes',
    'sb.kb.f.special_instructions': 'Lo que los clientes siempre deben saber',
    'sb.kb.pay.cash': 'Efectivo',
    'sb.kb.pay.debit': 'Débito',
    'sb.kb.pay.credit': 'Crédito',
    'sb.kb.pay.bankTransfer': 'Transferencia bancaria',
    'sb.kb.pay.applePay': 'Apple Pay',
    'sb.kb.pay.googlePay': 'Google Pay',
    'sb.kb.pay.payPal': 'PayPal',
    'sb.kb.pay.mobileMoney': 'Dinero móvil',
    'sb.kb.pay.interac': 'Interac e-Transfer',
    'sb.kb.pay.other': 'Otro',
    'sb.kb.revalidate': 'Volver a validar',
    'sb.kb.runValidationHint': 'Ejecute la validación desde la pestaña Recopilación para ver la preparación.',
    'sb.kb.readiness': 'Preparación',
    'sb.kb.readinessHint': 'La generación nunca se bloquea. Los datos específicos del negocio que falten se convierten en una FAQ sugerida o en la respuesta "Confirme esta política con el negocio." — nunca se inventan.',
    'sb.kb.val.missingFields': 'Campos faltantes',
    'sb.kb.val.missingFieldsEmpty': 'No falta nada obligatorio.',
    'sb.kb.val.contradictions': 'Contradicciones',
    'sb.kb.val.contradictionsEmpty': 'No se encontraron contradicciones.',
    'sb.kb.val.vague': 'Respuestas vagas',
    'sb.kb.val.vagueEmpty': 'No hay respuestas vagas.',
    'sb.kb.val.followups': 'Preguntas de seguimiento',
    'sb.kb.val.followupsEmpty': 'No hay seguimientos.',
    'sb.kb.completeMore': '← Completar más campos',
    'sb.kb.continueGenerate': 'Continuar para generar →',
    'sb.kb.generate50': 'Generar 50 FAQ en borrador',
    'sb.kb.generate50Sub': 'Ejecuta la validación + generación de IA a través del backend real. Los borradores son solo internos hasta su aprobación.',
    'sb.kb.generating': 'Generando…',
    'sb.kb.internalVsLive': 'Borrador y sugerido = solo interno. Aprobado = activo para Chat/Voz IA.',
    'sb.kb.showArchived': 'Mostrar archivados',
    'sb.kb.noGeneratedYet': 'Aún no hay FAQ generadas. Haga clic en Generar 50 FAQ en borrador arriba.',
    'sb.kb.status.draft': 'Borrador · solo interno',
    'sb.kb.status.suggested': 'Sugerido · solo interno',
    'sb.kb.status.pending': 'Pendiente de aprobación · interno',
    'sb.kb.status.approved': 'Aprobado · activo para IA',
    'sb.kb.status.archived': 'Archivado',
    'sb.kb.status.rejected': 'Rechazado',
    'sb.kb.ownerSupplied': 'Proporcionado por el propietario',
    'sb.kb.aiSuggested': 'Sugerido por IA',
    'sb.kb.totalGenerated': 'Total generado',
    'sb.kb.customerFacing': 'De cara al cliente (aprobado)',
    'sb.kb.internalDraft': 'Interno (borrador/sugerido)',
    'sb.kb.categoryCoverage': 'Cobertura por categoría',
    'sb.kb.missing': 'faltante',
    'sb.kb.missingCategories': 'Categorías faltantes: {cats}. Genere (o añada más datos) para cubrirlas.',
    'sb.kb.gapsIntro': 'Preguntas de clientes sin responder registradas para este negocio.',
    'sb.kb.noGaps': 'No hay vacíos de conocimiento registrados.',
    'sb.kb.createDraftFaq': 'Crear FAQ en borrador',
    'sb.kb.gapsLoadError': 'No se pudieron cargar los vacíos de conocimiento: {error}',
    'sb.kb.seenTimes': 'Visto {count}× · {status}',
    'sb.kb.editDraftFaq': 'Editar FAQ en borrador',
    'sb.kb.createDraftFromGap': 'Crear FAQ en borrador a partir del vacío',
    'sb.kb.question': 'Pregunta',
    'sb.kb.answer': 'Respuesta',
    'sb.kb.keywords': 'Palabras clave (separadas por comas)',
    'sb.kb.answerPlaceholder': 'Escriba la respuesta. Omita los detalles específicos del negocio si los desconoce.',
    'sb.kb.editInternalHint': 'La edición lo mantiene interno. Apruebe por separado para publicarlo.',
    'sb.kb.createdDraftHint': 'Creado como borrador (interno). Apruébelo para publicarlo.',
    'sb.kb.saveDraft': 'Guardar borrador',
    'sb.kb.createDraft': 'Crear borrador',
    'sb.kb.noBusinessContext': 'Sin contexto de negocio activo',
    'sb.kb.selectBusiness': 'Seleccione o vincule un negocio para usar el Constructor de conocimiento.',
    'sb.kb.toast.noBusiness': 'Ningún negocio seleccionado',
    'sb.kb.toast.noBusinessDesc': 'No se puede iniciar: no hay contexto de negocio activo.',
    'sb.kb.toast.started': 'Constructor de conocimiento iniciado',
    'sb.kb.toast.startedDesc': 'Complete lo que sepa: puede omitir lo que desconozca.',
    'sb.kb.toast.couldNotStart': 'No se pudo iniciar la sesión',
    'sb.kb.toast.progressSaved': 'Progreso guardado',
    'sb.kb.toast.savedFields': '{count} campo(s) guardado(s).',
    'sb.kb.toast.nothingToSave': 'No hay nada nuevo que guardar.',
    'sb.kb.toast.saveFailed': 'Error al guardar',
    'sb.kb.toast.validationFailed': 'Error de validación',
    'sb.kb.toast.draftsGenerated': 'FAQ en borrador generadas',
    'sb.kb.toast.draftsGeneratedDesc': '{count} elementos en borrador/sugeridos creados (solo internos).',
    'sb.kb.toast.generationFailed': 'Error de generación',
    'sb.kb.toast.providerUnavailable': 'El proveedor de generación de IA no está disponible en este momento. No se crearon borradores. Inténtelo de nuevo más tarde.',
    'sb.kb.toast.loadGenFailed': 'No se pudieron cargar las FAQ generadas',
    'sb.kb.toast.approved': 'Aprobado',
    'sb.kb.toast.approvedDesc': 'Esta FAQ ya está activa para Chat y Voz IA de inmediato.',
    'sb.kb.toast.approvalFailed': 'Error de aprobación',
    'sb.kb.toast.rejected': 'Rechazado',
    'sb.kb.toast.rejectedDesc': 'Este borrador no será utilizado por la IA.',
    'sb.kb.toast.rejectFailed': 'Error al rechazar',
    'sb.kb.toast.archived': 'Archivado',
    'sb.kb.toast.archivedDesc': 'Elemento retirado (excluido de la recuperación por IA).',
    'sb.kb.toast.archiveFailed': 'Error al archivar',
    'sb.kb.toast.draftUpdated': 'Borrador actualizado',
    'sb.kb.toast.draftUpdatedDesc': 'Su edición se guardó. Sigue siendo interno hasta su aprobación.',
    'sb.kb.toast.updateFailed': 'Error al actualizar',
    'sb.kb.toast.draftCreated': 'FAQ en borrador creada',
    'sb.kb.toast.draftCreatedDesc': 'Creado como borrador (interno). Apruébelo para publicarlo.',
    'sb.kb.toast.couldNotCreate': 'No se pudo crear el borrador',
    'sb.live.approvalsImmediate': 'Las aprobaciones surten efecto de inmediato: sin retraso de reentrenamiento',
    'sb.live.approvalsBlurb': 'Las soluciones aprobadas están disponibles para Chat de IA y Voz de IA de inmediato. No se requiere retraso de reentrenamiento porque Servisfy lee el conocimiento aprobado del banco de soluciones en vivo en cada interacción de IA.',
    'sb.live.aiChatLive': 'Chat IA: activo',
    'sb.live.aiVoiceLive': 'Voz IA: activo',
    'sb.live.business': 'Negocio: {name}',
    'sb.live.liveInAi': 'Activo en IA (aprobado)',
    'sb.live.usedByAiNow': 'Usado por Chat y Voz IA ahora',
    'sb.live.pendingApproval': 'Pendiente de aprobación',
    'sb.live.notYetUsed': 'Aún no usado por la IA',
    'sb.live.draftsSuggested': 'Borradores / sugeridos',
    'sb.live.internalOnly': 'Solo interno (Constructor de conocimiento)',
    'sb.live.inactive': 'Inactivo',
    'sb.live.rejectedRevoked': 'Rechazado / revocado / archivado',
    'sb.live.totalUsage': 'Uso total de IA (aprobado)',
    'sb.live.realUsageSum': 'Suma real de usage_count',
    'sb.live.avgSuccess': 'Tasa de éxito promedio',
    'sb.live.noUsageYet': 'Aún no se registra uso',
    'sb.live.acrossUsed': 'En soluciones aprobadas utilizadas',
    'sb.live.lastApproved': 'Última aprobación',
    'sb.live.effectiveImmediately': 'Efectivo de inmediato',
    'sb.live.noApprovalsYet': 'Aún no hay aprobaciones',
    'sb.live.topUsed': 'Soluciones aprobadas más usadas',
    'sb.live.topUsedEmpty': 'La IA aún no ha usado ninguna solución aprobada.',
    'sb.live.recentlyApproved': 'Aprobadas recientemente',
    'sb.live.recentlyApprovedEmpty': 'Aún no hay soluciones aprobadas. Apruebe desde Revisión para activar.',
    'sb.live.lowSuccess': 'Aprobadas pero con bajo éxito: pueden necesitar revisión',
    'sb.live.lowSuccessEmpty': 'No hay soluciones aprobadas con bajo éxito. 👍',
    'sb.live.lowSuccessHint': 'Soluciones aprobadas usadas por la IA con una tasa de éxito inferior al {threshold}%.',
    'sb.live.loading': 'Cargando conocimiento IA en vivo…',
    'sb.live.loadError': 'No se pudo cargar el conocimiento de IA',
    'sb.live.tryAgain': 'Inténtelo de nuevo',
    'sb.live.loadFailedToast': 'No se pudo cargar el conocimiento de IA',
    'sb.live.untitled': 'Sin título',
    'sb.live.uncategorized': 'Sin categoría',
    'sb.review.title': 'Revisión del banco de soluciones',
    'sb.review.subtitle': 'Revise y apruebe las soluciones sugeridas por IA para el banco de soluciones',
    'sb.review.pendingReview': 'Pendientes de revisión',
    'sb.review.approvedTab': 'Aprobadas',
    'sb.review.rejectedTab': 'Rechazadas',
    'sb.review.noPending': 'No hay soluciones pendientes de revisar',
    'sb.review.noPendingSub': 'Las soluciones sugeridas por IA aparecerán aquí para su aprobación',
    'sb.review.noApproved': 'No hay soluciones aprobadas',
    'sb.review.noRejected': 'No hay soluciones rechazadas',
    'sb.review.description': 'Descripción:',
    'sb.review.resolutionSteps': 'Pasos de resolución:',
    'sb.review.keywords': 'Palabras clave:',
    'sb.review.submittedBy': 'Enviado por {name}',
    'sb.review.staff': 'personal',
    'sb.review.aiVoice': 'Voz IA',
    'sb.review.aiChat': 'Chat IA',
    'sb.review.ch.humanChat': 'Chat humano',
    'sb.review.ch.voiceCall': 'Llamada de voz',
    'sb.review.ch.humanCall': 'Llamada con agente',
    'sb.review.ch.solutionBank': 'Banco de soluciones',
    'sb.review.ch.unknown': 'Desconocido',
    'sb.review.aiGenerated': 'Generado por IA',
    'sb.review.openaiFallback': 'Respaldo de OpenAI',
    'sb.review.sourceConversation': 'Origen: Conversación {id}',
    'sb.review.confHigh': 'Alta ({pct}%)',
    'sb.review.confMedium': 'Media ({pct}%)',
    'sb.review.confLow': 'Baja ({pct}%)',
    'sb.review.activeInAi': 'Activo en IA',
    'sb.review.approvedOn': 'Aprobado {date}',
    'sb.review.rejectedOn': 'Rechazado {date}',
    'sb.review.revoke': 'Revocar',
    'sb.review.resubmit': 'Reenviar para revisión',
    'sb.review.unknownDate': 'Desconocido',
    'sb.review.loadFailedDesc': 'Verifique su conexión e inténtelo de nuevo.',
    'sb.review.toast.approved': 'Solución aprobada',
    'sb.review.toast.approvedDesc': 'La solución ya está activa en la base de conocimiento de IA.',
    'sb.review.toast.approvalFailed': 'Error de aprobación',
    'sb.review.toast.rejected': 'Solución rechazada',
    'sb.review.toast.rejectedDesc': 'La solución no será utilizada por la IA.',
    'sb.review.toast.rejectionFailed': 'Error de rechazo',
    'sb.review.toast.revoked': 'Solución revocada',
    'sb.review.toast.revokedDesc': 'La IA ya no usará esta solución.',
    'sb.review.toast.revokeFailed': 'Error al revocar',
    'sb.review.toast.resubmitted': 'Solución reenviada',
    'sb.review.toast.resubmittedDesc': 'La solución está pendiente de revisión nuevamente.',
    'sb.review.toast.resubmitFailed': 'Error al reenviar',
    'sb.review.toast.deleted': 'Solución eliminada',
    'sb.review.toast.deletedDesc': 'Solución eliminada permanentemente.',
    'sb.review.toast.deleteFailed': 'Error al eliminar',
  },
  fr: {
    'sb.tab.solutions': 'Solutions',
    'sb.tab.knowledgeBuilder': 'Générateur de connaissances IA',
    'sb.tab.review': 'Révision',
    'sb.tab.analytics': 'Analytique',
    'sb.tab.liveAi': 'Connaissances IA en direct',
    'sb.tab.addSolution': 'Ajouter une solution',
    'sb.tab.manageSolutions': 'Gérer les solutions',
    'sb.exporting': 'Exportation...',
    'sb.importing': 'Importation...',
    'sb.searchPlaceholder': 'Rechercher des solutions...',
    'sb.filterSolutions': 'Filtrer les solutions',
    'sb.filterSolutionsDesc': 'Affinez les solutions selon des critères',
    'sb.difficulty': 'Difficulté',
    'sb.allDifficulties': 'Toutes les difficultés',
    'sb.easy': 'Facile',
    'sb.medium': 'Moyenne',
    'sb.hard': 'Difficile',
    'sb.reset': 'Réinitialiser',
    'sb.apply': 'Appliquer',
    'sb.allCategories': 'Toutes les catégories',
    'sb.revoke.action': 'Révoquer',
    'sb.revoke.confirm': 'Révoquer cette solution approuvée ? Elle ne sera plus utilisée par l’IA jusqu’à correction et nouvelle approbation.',
    'sb.revoke.done': 'Solution révoquée',
    'sb.revoke.doneDesc': 'Elle n’est plus utilisée par l’IA. Modifiez-la, puis renvoyez-la pour révision.',
    'sb.revoke.failed': 'Révocation impossible',
    'sb.cat.choose': 'Choisissez une catégorie',
    'sb.cat.noneConfigured': 'Aucune catégorie configurée',
    'sb.cat.noneConfiguredHint': 'Ajoutez des catégories dans Gestion des catégories avant de créer des solutions.',
    'sb.cat.technicalSupport': 'Support technique',
    'sb.cat.billing': 'Facturation',
    'sb.cat.productIssues': 'Problèmes de produit',
    'sb.cat.accountManagement': 'Gestion des comptes',
    'sb.totalSolutions': 'Total des solutions',
    'sb.avgSuccessRate': 'Taux de réussite moyen',
    'sb.totalUsage': 'Utilisation totale',
    'sb.categories': 'Catégories',
    'sb.topPerforming': 'Solutions les plus performantes',
    'sb.mostUsed': 'Solutions les plus utilisées',
    'sb.loadingSolutions': 'Chargement des solutions...',
    'sb.loadingSolutionsSub': 'Veuillez patienter pendant la récupération de votre banque de solutions',
    'sb.noSolutionsFound': 'Aucune solution trouvée',
    'sb.noSolutionsHint': 'Essayez d’ajuster vos critères de recherche ou utilisez l’onglet Gestion pour ajouter des solutions',
    'sb.mgmtFunctionsHint': 'Les fonctions de gestion des solutions sont disponibles dans l’onglet Gestion',
    'sb.successRateBadge': '{rate} % de réussite',
    'sb.usesBadge': '{count} utilisations',
    'sb.usesLower': '{count} utilisations',
    'sb.updated': 'Mis à jour : {date}',
    'sb.quickTips': 'Conseils rapides',
    'sb.tip1': 'Soyez précis et clair dans les descriptions des solutions',
    'sb.tip2': 'Incluez des instructions étape par étape',
    'sb.tip3': 'Étiquetez les solutions avec des mots-clés pertinents',
    'sb.tip4': 'Référencez les solutions connexes pour les problèmes complexes',
    'sb.recentActivity': 'Activité récente',
    'sb.noRecentActivity': 'Aucune activité récente pour le moment.',
    'sb.solutionsAddedToday': 'Solutions ajoutées aujourd’hui :',
    'sb.pendingReviewLabel': 'En attente de révision :',
    'sb.successRateLabel': 'Taux de réussite :',
    'sb.bulkOperations': 'Opérations groupées',
    'sb.importFromCsv': 'Importer des solutions depuis un CSV',
    'sb.exportAll': 'Exporter toutes les solutions',
    'sb.generateReport': 'Générer un rapport de performance',
    'sb.importCsvDesc': 'Téléversez un fichier CSV pour importer plusieurs solutions. Le CSV doit comporter les colonnes : title, description, category, tags, resolution, difficulty, success_rate, usage_count.',
    'sb.selectCsv': 'Sélectionnez un fichier CSV à téléverser',
    'sb.selectedFile': 'Sélectionné : {name}',
    'sb.importSolutionsBtn': 'Importer des solutions',
    'sb.exportAllDesc': 'Choisissez votre format d’exportation préféré pour toutes les solutions',
    'sb.csvFile': 'Fichier CSV',
    'sb.excelFile': 'Fichier Excel',
    'sb.jsonData': 'Données JSON',
    'sb.pdfReport': 'Rapport PDF',
    'sb.performanceReport': 'Rapport de performance de la banque de solutions',
    'sb.performanceReportDesc': 'Analyses et perspectives de performance générées par l’IA',
    'sb.aiAnalysisReport': 'Rapport d’analyse IA',
    'sb.downloadReport': 'Télécharger le rapport',
    'sb.editSolution': 'Modifier la solution',
    'sb.editSolutionDesc': 'Mettez à jour les informations de la solution',
    'sb.addNewSolutionTitle': 'Ajouter une nouvelle solution',
    'sb.addNewSolutionDesc': 'Créez une nouvelle solution pour votre banque de solutions',
    'sb.solutionTitle': 'Titre de la solution *',
    'sb.solutionTitlePlaceholder': 'Saisissez le titre de la solution',
    'sb.descriptionLabel': 'Description *',
    'sb.descriptionPlaceholder': 'Décrivez le problème que cette solution résout',
    'sb.category': 'Catégorie',
    'sb.tagsLabel': 'Étiquettes (séparées par des virgules)',
    'sb.tagsPlaceholder': 'mot de passe, réinitialiser, connexion',
    'sb.resolutionSteps': 'Étapes de résolution',
    'sb.resolutionPlaceholder': 'Saisissez les instructions de résolution étape par étape',
    'sb.updateSolution': 'Mettre à jour la solution',
    'sb.addSolutionBtn': 'Ajouter une solution',
    'sb.deleteSolution': 'Supprimer la solution',
    'sb.deleteSolutionDesc': 'Êtes-vous sûr de vouloir supprimer cette solution ? Cette action est irréversible.',
    'sb.toast.loadFailed': 'Échec du chargement des solutions',
    'sb.toast.missingInfo': 'Informations manquantes',
    'sb.toast.missingInfoDesc': 'Veuillez remplir le titre et la description',
    'sb.toast.submitted': 'Solution soumise',
    'sb.toast.submittedDesc': 'La solution a été transmise au gestionnaire pour approbation',
    'sb.toast.addFailed': 'Échec de l’ajout de la solution',
    'sb.toast.exportStarted': 'Exportation démarrée',
    'sb.toast.exportStartedDesc': 'Exportation des solutions au format {format}',
    'sb.toast.exportComplete': 'Exportation terminée',
    'sb.toast.exportCompleteDesc': 'Solutions exportées avec succès au format {format}',
    'sb.toast.exportFailed': 'Échec de l’exportation',
    'sb.toast.exportFailedDesc': 'Échec de l’exportation des solutions',
    'sb.toast.noFile': 'Aucun fichier sélectionné',
    'sb.toast.noFileDesc': 'Veuillez sélectionner un fichier CSV à importer',
    'sb.toast.importStarted': 'Importation démarrée',
    'sb.toast.importStartedDesc': 'Traitement du fichier CSV...',
    'sb.toast.importComplete': 'Importation terminée',
    'sb.toast.importCompleteDesc': 'Solutions importées avec succès',
    'sb.toast.importFailed': 'Échec de l’importation',
    'sb.toast.importFailedDesc': 'Échec de l’importation des solutions depuis le CSV',
    'sb.toast.invalidFile': 'Fichier non valide',
    'sb.toast.invalidFileDesc': 'Veuillez sélectionner un fichier CSV valide',
    'sb.toast.reportGenerated': 'Rapport généré',
    'sb.toast.reportGeneratedDesc': 'Le rapport de performance a été généré avec succès',
    'sb.toast.reportFailed': 'Échec de la génération du rapport',
    'sb.toast.reportFailedDesc': 'Échec de la génération du rapport de performance',
    'sb.toast.updated': 'Solution mise à jour',
    'sb.toast.updatedDesc': 'La solution a été mise à jour avec succès',
    'sb.toast.updateFailed': 'Échec de la mise à jour de la solution',
    'sb.toast.deleted': 'Solution supprimée',
    'sb.toast.deletedDesc': 'La solution a été supprimée avec succès',
    'sb.toast.deleteFailed': 'Échec de la suppression de la solution',
    'sb.kb.title': 'Générateur de connaissances IA',
    'sb.kb.subtitle': 'Saisissez les informations de votre entreprise → générez 50 FAQ provisoires → révisez et approuvez. Les brouillons restent internes jusqu’à approbation.',
    'sb.kb.newSession': 'Nouvelle session',
    'sb.kb.buildTitle': 'Créez votre base de connaissances IA',
    'sb.kb.buildBlurb': 'Répondez à ce que vous savez sur votre entreprise. Nous générerons 50 FAQ provisoires/suggérées. Rien n’est publié aux clients tant qu’un gestionnaire ou un administrateur ne l’a pas approuvé.',
    'sb.kb.start': 'Démarrer le générateur de connaissances',
    'sb.kb.tab.intake': 'Saisie',
    'sb.kb.tab.validate': 'Valider',
    'sb.kb.tab.review': 'Générer et réviser',
    'sb.kb.tab.coverage': 'Couverture',
    'sb.kb.tab.gaps': 'Lacunes',
    'sb.kb.skipHint': 'Ignorez tout ce que vous ignorez — les champs inconnus ne sont jamais inventés.',
    'sb.kb.saveProgress': 'Enregistrer la progression',
    'sb.kb.blankUnknown': 'Laissez vide si inconnu',
    'sb.kb.blankUnknownTextarea': 'Laissez vide si inconnu — cela ne sera pas inventé',
    'sb.kb.unknownSkip': 'Inconnu / ignorer',
    'sb.kb.sec.identity': 'Identité de l’entreprise',
    'sb.kb.sec.profile': 'Profil de l’entreprise',
    'sb.kb.sec.operations': 'Opérations',
    'sb.kb.sec.payments': 'Paiements',
    'sb.kb.sec.delivery': 'Expédition / Livraison',
    'sb.kb.sec.booking': 'Réservation',
    'sb.kb.sec.returns': 'Retours / Remboursements',
    'sb.kb.sec.support': 'Support',
    'sb.kb.sec.policies': 'Politiques',
    'sb.kb.sec.extras': 'Questions et notes des clients',
    'sb.kb.f.business_name': 'Nom de l’entreprise',
    'sb.kb.f.address': 'Adresse',
    'sb.kb.f.city': 'Ville',
    'sb.kb.f.province': 'Province / État',
    'sb.kb.f.country': 'Pays',
    'sb.kb.f.phone': 'Téléphone',
    'sb.kb.f.email': 'E-mail',
    'sb.kb.f.website': 'Site web',
    'sb.kb.f.social_media': 'Réseaux sociaux',
    'sb.kb.f.business_description': 'Description de l’entreprise',
    'sb.kb.f.products': 'Produits vendus',
    'sb.kb.f.services': 'Services fournis',
    'sb.kb.f.target_customers': 'Clientèle cible',
    'sb.kb.f.industries_served': 'Secteurs desservis',
    'sb.kb.f.differentiator': 'Élément différenciateur',
    'sb.kb.f.mission_vision': 'Mission / vision',
    'sb.kb.f.opening_days': 'Jours d’ouverture',
    'sb.kb.f.operating_hours': 'Heures d’ouverture',
    'sb.kb.f.holiday_hours': 'Horaires des jours fériés',
    'sb.kb.f.appointment_required': 'Rendez-vous requis',
    'sb.kb.f.walk_ins_accepted': 'Visites sans rendez-vous acceptées',
    'sb.kb.f.delivery_available': 'Livraison disponible',
    'sb.kb.f.pickup_available': 'Retrait disponible',
    'sb.kb.f.online_orders': 'Commandes en ligne',
    'sb.kb.f.service_area': 'Zone de service',
    'sb.kb.f.payment_methods': 'Moyens de paiement acceptés',
    'sb.kb.f.delivery_locations': 'Lieux de livraison',
    'sb.kb.f.delivery_fees': 'Frais de livraison',
    'sb.kb.f.delivery_timelines': 'Délais de livraison',
    'sb.kb.f.same_day_delivery': 'Livraison le jour même',
    'sb.kb.f.international_shipping': 'Expédition internationale',
    'sb.kb.f.booking_method': 'Comment les rendez-vous sont pris',
    'sb.kb.f.cancellation_policy': 'Politique d’annulation',
    'sb.kb.f.rescheduling_policy': 'Politique de report',
    'sb.kb.f.late_arrival_policy': 'Politique de retard',
    'sb.kb.f.ordering_method': 'Comment les commandes en ligne sont passées',
    'sb.kb.f.return_policy': 'Politique de retour',
    'sb.kb.f.refund_policy': 'Politique de remboursement',
    'sb.kb.f.exchange_policy': 'Politique d’échange',
    'sb.kb.f.contact_methods': 'Principaux moyens de contact',
    'sb.kb.f.support_phone': 'Téléphone du support',
    'sb.kb.f.support_whatsapp': 'WhatsApp',
    'sb.kb.f.support_email': 'E-mail du support',
    'sb.kb.f.support_website': 'Site web du support',
    'sb.kb.f.support_in_person': 'Support en personne',
    'sb.kb.f.privacy_policy': 'Politique de confidentialité',
    'sb.kb.f.warranty_policy': 'Politique de garantie',
    'sb.kb.f.terms_of_service': 'Conditions d’utilisation',
    'sb.kb.f.service_guarantees': 'Garanties de service',
    'sb.kb.f.policies': 'Résumé des politiques générales',
    'sb.kb.f.common_questions': 'Questions fréquentes des clients',
    'sb.kb.f.special_instructions': 'Ce que les clients doivent toujours savoir',
    'sb.kb.pay.cash': 'Espèces',
    'sb.kb.pay.debit': 'Débit',
    'sb.kb.pay.credit': 'Crédit',
    'sb.kb.pay.bankTransfer': 'Virement bancaire',
    'sb.kb.pay.applePay': 'Apple Pay',
    'sb.kb.pay.googlePay': 'Google Pay',
    'sb.kb.pay.payPal': 'PayPal',
    'sb.kb.pay.mobileMoney': 'Argent mobile',
    'sb.kb.pay.interac': 'Interac e-Transfer',
    'sb.kb.pay.other': 'Autre',
    'sb.kb.revalidate': 'Revalider',
    'sb.kb.runValidationHint': 'Lancez la validation depuis l’onglet Saisie pour voir l’état de préparation.',
    'sb.kb.readiness': 'Préparation',
    'sb.kb.readinessHint': 'La génération n’est jamais bloquée. Les informations propres à l’entreprise manquantes deviennent une FAQ suggérée ou la réponse "Veuillez confirmer cette politique auprès de l’entreprise." — jamais inventées.',
    'sb.kb.val.missingFields': 'Champs manquants',
    'sb.kb.val.missingFieldsEmpty': 'Rien d’obligatoire ne manque.',
    'sb.kb.val.contradictions': 'Contradictions',
    'sb.kb.val.contradictionsEmpty': 'Aucune contradiction trouvée.',
    'sb.kb.val.vague': 'Réponses vagues',
    'sb.kb.val.vagueEmpty': 'Aucune réponse vague.',
    'sb.kb.val.followups': 'Questions de suivi',
    'sb.kb.val.followupsEmpty': 'Aucun suivi.',
    'sb.kb.completeMore': '← Compléter plus de champs',
    'sb.kb.continueGenerate': 'Continuer vers la génération →',
    'sb.kb.generate50': 'Générer 50 FAQ provisoires',
    'sb.kb.generate50Sub': 'Exécute la validation + la génération par IA via le backend réel. Les brouillons sont internes uniquement jusqu’à approbation.',
    'sb.kb.generating': 'Génération…',
    'sb.kb.internalVsLive': 'Brouillon et suggéré = interne uniquement. Approuvé = actif pour le Chat/la Voix IA.',
    'sb.kb.showArchived': 'Afficher les archivés',
    'sb.kb.noGeneratedYet': 'Aucune FAQ générée pour le moment. Cliquez sur Générer 50 FAQ provisoires ci-dessus.',
    'sb.kb.status.draft': 'Brouillon · interne uniquement',
    'sb.kb.status.suggested': 'Suggéré · interne uniquement',
    'sb.kb.status.pending': 'En attente d’approbation · interne',
    'sb.kb.status.approved': 'Approuvé · actif pour l’IA',
    'sb.kb.status.archived': 'Archivé',
    'sb.kb.status.rejected': 'Rejeté',
    'sb.kb.ownerSupplied': 'Fourni par le propriétaire',
    'sb.kb.aiSuggested': 'Suggéré par l’IA',
    'sb.kb.totalGenerated': 'Total généré',
    'sb.kb.customerFacing': 'Destiné aux clients (approuvé)',
    'sb.kb.internalDraft': 'Interne (brouillon/suggéré)',
    'sb.kb.categoryCoverage': 'Couverture par catégorie',
    'sb.kb.missing': 'manquant',
    'sb.kb.missingCategories': 'Catégories manquantes : {cats}. Générez (ou ajoutez plus d’informations) pour les couvrir.',
    'sb.kb.gapsIntro': 'Questions de clients sans réponse enregistrées pour cette entreprise.',
    'sb.kb.noGaps': 'Aucune lacune de connaissances enregistrée.',
    'sb.kb.createDraftFaq': 'Créer une FAQ provisoire',
    'sb.kb.gapsLoadError': 'Impossible de charger les lacunes de connaissances : {error}',
    'sb.kb.seenTimes': 'Vu {count}× · {status}',
    'sb.kb.editDraftFaq': 'Modifier la FAQ provisoire',
    'sb.kb.createDraftFromGap': 'Créer une FAQ provisoire à partir de la lacune',
    'sb.kb.question': 'Question',
    'sb.kb.answer': 'Réponse',
    'sb.kb.keywords': 'Mots-clés (séparés par des virgules)',
    'sb.kb.answerPlaceholder': 'Rédigez la réponse. Laissez de côté les détails propres à l’entreprise si inconnus.',
    'sb.kb.editInternalHint': 'La modification reste interne. Approuvez séparément pour la publier.',
    'sb.kb.createdDraftHint': 'Créé comme brouillon (interne). Approuvez-le pour le publier.',
    'sb.kb.saveDraft': 'Enregistrer le brouillon',
    'sb.kb.createDraft': 'Créer un brouillon',
    'sb.kb.noBusinessContext': 'Aucun contexte d’entreprise actif',
    'sb.kb.selectBusiness': 'Sélectionnez ou associez une entreprise pour utiliser le générateur de connaissances.',
    'sb.kb.toast.noBusiness': 'Aucune entreprise sélectionnée',
    'sb.kb.toast.noBusinessDesc': 'Impossible de démarrer — aucun contexte d’entreprise actif.',
    'sb.kb.toast.started': 'Générateur de connaissances démarré',
    'sb.kb.toast.startedDesc': 'Renseignez ce que vous savez — vous pouvez ignorer ce que vous ignorez.',
    'sb.kb.toast.couldNotStart': 'Impossible de démarrer la session',
    'sb.kb.toast.progressSaved': 'Progression enregistrée',
    'sb.kb.toast.savedFields': '{count} champ(s) enregistré(s).',
    'sb.kb.toast.nothingToSave': 'Rien de nouveau à enregistrer.',
    'sb.kb.toast.saveFailed': 'Échec de l’enregistrement',
    'sb.kb.toast.validationFailed': 'Échec de la validation',
    'sb.kb.toast.draftsGenerated': 'FAQ provisoires générées',
    'sb.kb.toast.draftsGeneratedDesc': '{count} éléments provisoires/suggérés créés (internes uniquement).',
    'sb.kb.toast.generationFailed': 'Échec de la génération',
    'sb.kb.toast.providerUnavailable': 'Le fournisseur de génération IA est actuellement indisponible. Aucun brouillon n’a été créé. Veuillez réessayer plus tard.',
    'sb.kb.toast.loadGenFailed': 'Impossible de charger les FAQ générées',
    'sb.kb.toast.approved': 'Approuvé',
    'sb.kb.toast.approvedDesc': 'Cette FAQ est désormais active pour le Chat et la Voix IA immédiatement.',
    'sb.kb.toast.approvalFailed': 'Échec de l’approbation',
    'sb.kb.toast.rejected': 'Rejeté',
    'sb.kb.toast.rejectedDesc': 'Ce brouillon ne sera pas utilisé par l’IA.',
    'sb.kb.toast.rejectFailed': 'Échec du rejet',
    'sb.kb.toast.archived': 'Archivé',
    'sb.kb.toast.archivedDesc': 'Élément retiré (exclu de la récupération par l’IA).',
    'sb.kb.toast.archiveFailed': 'Échec de l’archivage',
    'sb.kb.toast.draftUpdated': 'Brouillon mis à jour',
    'sb.kb.toast.draftUpdatedDesc': 'Votre modification a été enregistrée. Toujours interne jusqu’à approbation.',
    'sb.kb.toast.updateFailed': 'Échec de la mise à jour',
    'sb.kb.toast.draftCreated': 'FAQ provisoire créée',
    'sb.kb.toast.draftCreatedDesc': 'Créé comme brouillon (interne). Approuvez-le pour le publier.',
    'sb.kb.toast.couldNotCreate': 'Impossible de créer le brouillon',
    'sb.live.approvalsImmediate': 'Les approbations prennent effet immédiatement — sans délai de réentraînement',
    'sb.live.approvalsBlurb': 'Les solutions approuvées sont disponibles pour le Chat IA et la Voix IA immédiatement. Aucun délai de réentraînement n’est requis car Servisfy lit en direct les connaissances approuvées de la banque de solutions à chaque interaction IA.',
    'sb.live.aiChatLive': 'Chat IA : en direct',
    'sb.live.aiVoiceLive': 'Voix IA : en direct',
    'sb.live.business': 'Entreprise : {name}',
    'sb.live.liveInAi': 'En direct dans l’IA (approuvé)',
    'sb.live.usedByAiNow': 'Utilisé par le Chat et la Voix IA maintenant',
    'sb.live.pendingApproval': 'En attente d’approbation',
    'sb.live.notYetUsed': 'Pas encore utilisé par l’IA',
    'sb.live.draftsSuggested': 'Brouillons / suggérés',
    'sb.live.internalOnly': 'Interne uniquement (Générateur de connaissances)',
    'sb.live.inactive': 'Inactif',
    'sb.live.rejectedRevoked': 'Rejeté / révoqué / archivé',
    'sb.live.totalUsage': 'Utilisation totale de l’IA (approuvé)',
    'sb.live.realUsageSum': 'Somme réelle de usage_count',
    'sb.live.avgSuccess': 'Taux de réussite moyen',
    'sb.live.noUsageYet': 'Aucune utilisation enregistrée',
    'sb.live.acrossUsed': 'Sur les solutions approuvées utilisées',
    'sb.live.lastApproved': 'Dernière approbation',
    'sb.live.effectiveImmediately': 'Effectif immédiatement',
    'sb.live.noApprovalsYet': 'Aucune approbation pour le moment',
    'sb.live.topUsed': 'Solutions approuvées les plus utilisées',
    'sb.live.topUsedEmpty': 'Aucune solution approuvée n’a encore été utilisée par l’IA.',
    'sb.live.recentlyApproved': 'Approuvées récemment',
    'sb.live.recentlyApprovedEmpty': 'Aucune solution approuvée pour le moment. Approuvez depuis Révision pour activer.',
    'sb.live.lowSuccess': 'Approuvées mais peu efficaces — peuvent nécessiter une révision',
    'sb.live.lowSuccessEmpty': 'Aucune solution approuvée à faible réussite. 👍',
    'sb.live.lowSuccessHint': 'Solutions approuvées utilisées par l’IA avec un taux de réussite inférieur à {threshold} %.',
    'sb.live.loading': 'Chargement des connaissances IA en direct…',
    'sb.live.loadError': 'Impossible de charger les connaissances IA',
    'sb.live.tryAgain': 'Réessayer',
    'sb.live.loadFailedToast': 'Échec du chargement des connaissances IA',
    'sb.live.untitled': 'Sans titre',
    'sb.live.uncategorized': 'Non catégorisé',
    'sb.review.title': 'Révision de la banque de solutions',
    'sb.review.subtitle': 'Révisez et approuvez les solutions suggérées par l’IA pour la banque de solutions',
    'sb.review.pendingReview': 'En attente de révision',
    'sb.review.approvedTab': 'Approuvées',
    'sb.review.rejectedTab': 'Rejetées',
    'sb.review.noPending': 'Aucune solution en attente de révision',
    'sb.review.noPendingSub': 'Les solutions suggérées par l’IA apparaîtront ici pour approbation',
    'sb.review.noApproved': 'Aucune solution approuvée',
    'sb.review.noRejected': 'Aucune solution rejetée',
    'sb.review.description': 'Description :',
    'sb.review.resolutionSteps': 'Étapes de résolution :',
    'sb.review.keywords': 'Mots-clés :',
    'sb.review.submittedBy': 'Soumis par {name}',
    'sb.review.staff': 'personnel',
    'sb.review.aiVoice': 'Voix IA',
    'sb.review.aiChat': 'Chat IA',
    'sb.review.ch.humanChat': 'Chat humain',
    'sb.review.ch.voiceCall': 'Appel vocal',
    'sb.review.ch.humanCall': 'Appel avec un agent',
    'sb.review.ch.solutionBank': 'Banque de solutions',
    'sb.review.ch.unknown': 'Inconnu',
    'sb.review.aiGenerated': 'Généré par l’IA',
    'sb.review.openaiFallback': 'Repli OpenAI',
    'sb.review.sourceConversation': 'Source : Conversation {id}',
    'sb.review.confHigh': 'Élevée ({pct} %)',
    'sb.review.confMedium': 'Moyenne ({pct} %)',
    'sb.review.confLow': 'Faible ({pct} %)',
    'sb.review.activeInAi': 'Actif dans l’IA',
    'sb.review.approvedOn': 'Approuvé {date}',
    'sb.review.rejectedOn': 'Rejeté {date}',
    'sb.review.revoke': 'Révoquer',
    'sb.review.resubmit': 'Resoumettre pour révision',
    'sb.review.unknownDate': 'Inconnu',
    'sb.review.loadFailedDesc': 'Vérifiez votre connexion et réessayez.',
    'sb.review.toast.approved': 'Solution approuvée',
    'sb.review.toast.approvedDesc': 'La solution est désormais active dans la base de connaissances IA.',
    'sb.review.toast.approvalFailed': 'Échec de l’approbation',
    'sb.review.toast.rejected': 'Solution rejetée',
    'sb.review.toast.rejectedDesc': 'La solution ne sera pas utilisée par l’IA.',
    'sb.review.toast.rejectionFailed': 'Échec du rejet',
    'sb.review.toast.revoked': 'Solution révoquée',
    'sb.review.toast.revokedDesc': 'L’IA n’utilisera plus cette solution.',
    'sb.review.toast.revokeFailed': 'Échec de la révocation',
    'sb.review.toast.resubmitted': 'Solution resoumise',
    'sb.review.toast.resubmittedDesc': 'La solution est de nouveau en attente de révision.',
    'sb.review.toast.resubmitFailed': 'Échec de la resoumission',
    'sb.review.toast.deleted': 'Solution supprimée',
    'sb.review.toast.deletedDesc': 'Solution supprimée définitivement.',
    'sb.review.toast.deleteFailed': 'Échec de la suppression',
  },
  pt: {
    'sb.tab.solutions': 'Soluções',
    'sb.tab.knowledgeBuilder': 'Construtor de Conhecimento IA',
    'sb.tab.review': 'Revisão',
    'sb.tab.analytics': 'Análises',
    'sb.tab.liveAi': 'Conhecimento de IA ao Vivo',
    'sb.tab.addSolution': 'Adicionar Solução',
    'sb.tab.manageSolutions': 'Gerenciar Soluções',
    'sb.exporting': 'Exportando...',
    'sb.importing': 'Importando...',
    'sb.searchPlaceholder': 'Pesquisar soluções...',
    'sb.filterSolutions': 'Filtrar Soluções',
    'sb.filterSolutionsDesc': 'Refine as soluções por critérios',
    'sb.difficulty': 'Dificuldade',
    'sb.allDifficulties': 'Todas as Dificuldades',
    'sb.easy': 'Fácil',
    'sb.medium': 'Média',
    'sb.hard': 'Difícil',
    'sb.reset': 'Redefinir',
    'sb.apply': 'Aplicar',
    'sb.allCategories': 'Todas as Categorias',
    'sb.revoke.action': 'Revogar',
    'sb.revoke.confirm': 'Revogar esta solução aprovada? Ela deixará de ser usada pela IA até ser corrigida e aprovada novamente.',
    'sb.revoke.done': 'Solução revogada',
    'sb.revoke.doneDesc': 'Não é mais usada pela IA. Edite-a e reenvie para revisão.',
    'sb.revoke.failed': 'Não foi possível revogar',
    'sb.cat.choose': 'Escolha uma categoria',
    'sb.cat.noneConfigured': 'Nenhuma categoria configurada',
    'sb.cat.noneConfiguredHint': 'Adicione categorias em Gerenciamento de categorias antes de criar soluções.',
    'sb.cat.technicalSupport': 'Suporte Técnico',
    'sb.cat.billing': 'Faturamento',
    'sb.cat.productIssues': 'Problemas de Produto',
    'sb.cat.accountManagement': 'Gerenciamento de Conta',
    'sb.totalSolutions': 'Total de Soluções',
    'sb.avgSuccessRate': 'Taxa Média de Sucesso',
    'sb.totalUsage': 'Uso Total',
    'sb.categories': 'Categorias',
    'sb.topPerforming': 'Soluções de Melhor Desempenho',
    'sb.mostUsed': 'Soluções Mais Usadas',
    'sb.loadingSolutions': 'Carregando soluções...',
    'sb.loadingSolutionsSub': 'Aguarde enquanto buscamos seu banco de soluções',
    'sb.noSolutionsFound': 'Nenhuma solução encontrada',
    'sb.noSolutionsHint': 'Tente ajustar seus critérios de pesquisa ou use a aba Gerenciamento para adicionar soluções',
    'sb.mgmtFunctionsHint': 'As funções de gerenciamento de soluções estão disponíveis na aba Gerenciamento',
    'sb.successRateBadge': '{rate}% de Taxa de Sucesso',
    'sb.usesBadge': '{count} Usos',
    'sb.usesLower': '{count} usos',
    'sb.updated': 'Atualizado: {date}',
    'sb.quickTips': 'Dicas Rápidas',
    'sb.tip1': 'Seja específico e claro nas descrições das soluções',
    'sb.tip2': 'Inclua instruções passo a passo',
    'sb.tip3': 'Marque as soluções com palavras-chave relevantes',
    'sb.tip4': 'Faça referência a soluções relacionadas para problemas complexos',
    'sb.recentActivity': 'Atividade Recente',
    'sb.noRecentActivity': 'Ainda não há atividade recente.',
    'sb.solutionsAddedToday': 'Soluções adicionadas hoje:',
    'sb.pendingReviewLabel': 'Pendentes de revisão:',
    'sb.successRateLabel': 'Taxa de sucesso:',
    'sb.bulkOperations': 'Operações em Massa',
    'sb.importFromCsv': 'Importar Soluções de CSV',
    'sb.exportAll': 'Exportar Todas as Soluções',
    'sb.generateReport': 'Gerar Relatório de Desempenho',
    'sb.importCsvDesc': 'Envie um arquivo CSV para importar várias soluções. O CSV deve ter as colunas: title, description, category, tags, resolution, difficulty, success_rate, usage_count.',
    'sb.selectCsv': 'Selecione um arquivo CSV para enviar',
    'sb.selectedFile': 'Selecionado: {name}',
    'sb.importSolutionsBtn': 'Importar Soluções',
    'sb.exportAllDesc': 'Escolha o formato de exportação de sua preferência para todas as soluções',
    'sb.csvFile': 'Arquivo CSV',
    'sb.excelFile': 'Arquivo Excel',
    'sb.jsonData': 'Dados JSON',
    'sb.pdfReport': 'Relatório PDF',
    'sb.performanceReport': 'Relatório de Desempenho do Banco de Soluções',
    'sb.performanceReportDesc': 'Insights e análise de desempenho gerados por IA',
    'sb.aiAnalysisReport': 'Relatório de Análise de IA',
    'sb.downloadReport': 'Baixar Relatório',
    'sb.editSolution': 'Editar Solução',
    'sb.editSolutionDesc': 'Atualize as informações da solução',
    'sb.addNewSolutionTitle': 'Adicionar Nova Solução',
    'sb.addNewSolutionDesc': 'Crie uma nova solução para o seu banco de soluções',
    'sb.solutionTitle': 'Título da Solução *',
    'sb.solutionTitlePlaceholder': 'Digite o título da solução',
    'sb.descriptionLabel': 'Descrição *',
    'sb.descriptionPlaceholder': 'Descreva o problema que esta solução resolve',
    'sb.category': 'Categoria',
    'sb.tagsLabel': 'Tags (separadas por vírgula)',
    'sb.tagsPlaceholder': 'senha, redefinir, login',
    'sb.resolutionSteps': 'Etapas de Resolução',
    'sb.resolutionPlaceholder': 'Digite instruções de resolução passo a passo',
    'sb.updateSolution': 'Atualizar Solução',
    'sb.addSolutionBtn': 'Adicionar Solução',
    'sb.deleteSolution': 'Excluir Solução',
    'sb.deleteSolutionDesc': 'Tem certeza de que deseja excluir esta solução? Esta ação não pode ser desfeita.',
    'sb.toast.loadFailed': 'Falha ao carregar as soluções',
    'sb.toast.missingInfo': 'Informações Faltando',
    'sb.toast.missingInfoDesc': 'Preencha o título e a descrição',
    'sb.toast.submitted': 'Solução Enviada',
    'sb.toast.submittedDesc': 'A solução foi encaminhada ao Gerente para aprovação',
    'sb.toast.addFailed': 'Falha ao adicionar a solução',
    'sb.toast.exportStarted': 'Exportação Iniciada',
    'sb.toast.exportStartedDesc': 'Exportando soluções como {format}',
    'sb.toast.exportComplete': 'Exportação Concluída',
    'sb.toast.exportCompleteDesc': 'Soluções exportadas com sucesso como {format}',
    'sb.toast.exportFailed': 'Falha na Exportação',
    'sb.toast.exportFailedDesc': 'Falha ao exportar as soluções',
    'sb.toast.noFile': 'Nenhum Arquivo Selecionado',
    'sb.toast.noFileDesc': 'Selecione um arquivo CSV para importar',
    'sb.toast.importStarted': 'Importação Iniciada',
    'sb.toast.importStartedDesc': 'Processando o arquivo CSV...',
    'sb.toast.importComplete': 'Importação Concluída',
    'sb.toast.importCompleteDesc': 'Soluções importadas com sucesso',
    'sb.toast.importFailed': 'Falha na Importação',
    'sb.toast.importFailedDesc': 'Falha ao importar as soluções do CSV',
    'sb.toast.invalidFile': 'Arquivo Inválido',
    'sb.toast.invalidFileDesc': 'Selecione um arquivo CSV válido',
    'sb.toast.reportGenerated': 'Relatório Gerado',
    'sb.toast.reportGeneratedDesc': 'O relatório de desempenho foi gerado com sucesso',
    'sb.toast.reportFailed': 'Falha na Geração do Relatório',
    'sb.toast.reportFailedDesc': 'Falha ao gerar o relatório de desempenho',
    'sb.toast.updated': 'Solução Atualizada',
    'sb.toast.updatedDesc': 'A solução foi atualizada com sucesso',
    'sb.toast.updateFailed': 'Falha ao atualizar a solução',
    'sb.toast.deleted': 'Solução Excluída',
    'sb.toast.deletedDesc': 'A solução foi excluída com sucesso',
    'sb.toast.deleteFailed': 'Falha ao excluir a solução',
    'sb.kb.title': 'Construtor de Conhecimento IA',
    'sb.kb.subtitle': 'Insira as informações do seu negócio → gere 50 rascunhos de FAQs → revise e aprove. Os rascunhos permanecem internos até serem aprovados.',
    'sb.kb.newSession': 'Nova sessão',
    'sb.kb.buildTitle': 'Construa sua base de conhecimento de IA',
    'sb.kb.buildBlurb': 'Responda o que você sabe sobre o seu negócio. Vamos gerar 50 rascunhos/sugestões de FAQs. Nada é publicado para os clientes até que um gerente ou administrador aprove.',
    'sb.kb.start': 'Iniciar Construtor de Conhecimento',
    'sb.kb.tab.intake': 'Coleta',
    'sb.kb.tab.validate': 'Validar',
    'sb.kb.tab.review': 'Gerar e Revisar',
    'sb.kb.tab.coverage': 'Cobertura',
    'sb.kb.tab.gaps': 'Lacunas',
    'sb.kb.skipHint': 'Pule qualquer item desconhecido — campos desconhecidos nunca são inventados.',
    'sb.kb.saveProgress': 'Salvar progresso',
    'sb.kb.blankUnknown': 'Deixe em branco se desconhecido',
    'sb.kb.blankUnknownTextarea': 'Deixe em branco se desconhecido — não será inventado',
    'sb.kb.unknownSkip': 'Desconhecido / pular',
    'sb.kb.sec.identity': 'Identidade do Negócio',
    'sb.kb.sec.profile': 'Perfil do Negócio',
    'sb.kb.sec.operations': 'Operações',
    'sb.kb.sec.payments': 'Pagamentos',
    'sb.kb.sec.delivery': 'Envio / Entrega',
    'sb.kb.sec.booking': 'Agendamento',
    'sb.kb.sec.returns': 'Devoluções / Reembolsos',
    'sb.kb.sec.support': 'Suporte',
    'sb.kb.sec.policies': 'Políticas',
    'sb.kb.sec.extras': 'Perguntas e Observações dos Clientes',
    'sb.kb.f.business_name': 'Nome do Negócio',
    'sb.kb.f.address': 'Endereço',
    'sb.kb.f.city': 'Cidade',
    'sb.kb.f.province': 'Província / Estado',
    'sb.kb.f.country': 'País',
    'sb.kb.f.phone': 'Telefone',
    'sb.kb.f.email': 'E-mail',
    'sb.kb.f.website': 'Site',
    'sb.kb.f.social_media': 'Redes Sociais',
    'sb.kb.f.business_description': 'Descrição do negócio',
    'sb.kb.f.products': 'Produtos vendidos',
    'sb.kb.f.services': 'Serviços prestados',
    'sb.kb.f.target_customers': 'Clientes-alvo',
    'sb.kb.f.industries_served': 'Setores atendidos',
    'sb.kb.f.differentiator': 'Diferencial',
    'sb.kb.f.mission_vision': 'Missão / visão',
    'sb.kb.f.opening_days': 'Dias de funcionamento',
    'sb.kb.f.operating_hours': 'Horário de funcionamento',
    'sb.kb.f.holiday_hours': 'Horário em feriados',
    'sb.kb.f.appointment_required': 'Agendamento obrigatório',
    'sb.kb.f.walk_ins_accepted': 'Aceita atendimento sem agendamento',
    'sb.kb.f.delivery_available': 'Entrega disponível',
    'sb.kb.f.pickup_available': 'Retirada disponível',
    'sb.kb.f.online_orders': 'Pedidos online',
    'sb.kb.f.service_area': 'Área de atendimento',
    'sb.kb.f.payment_methods': 'Formas de pagamento aceitas',
    'sb.kb.f.delivery_locations': 'Locais de entrega',
    'sb.kb.f.delivery_fees': 'Taxas de entrega',
    'sb.kb.f.delivery_timelines': 'Prazos de entrega',
    'sb.kb.f.same_day_delivery': 'Entrega no mesmo dia',
    'sb.kb.f.international_shipping': 'Envio internacional',
    'sb.kb.f.booking_method': 'Como os agendamentos são feitos',
    'sb.kb.f.cancellation_policy': 'Política de cancelamento',
    'sb.kb.f.rescheduling_policy': 'Política de reagendamento',
    'sb.kb.f.late_arrival_policy': 'Política de atraso',
    'sb.kb.f.ordering_method': 'Como os pedidos online são feitos',
    'sb.kb.f.return_policy': 'Política de devolução',
    'sb.kb.f.refund_policy': 'Política de reembolso',
    'sb.kb.f.exchange_policy': 'Política de troca',
    'sb.kb.f.contact_methods': 'Principais formas de contato',
    'sb.kb.f.support_phone': 'Telefone de suporte',
    'sb.kb.f.support_whatsapp': 'WhatsApp',
    'sb.kb.f.support_email': 'E-mail de suporte',
    'sb.kb.f.support_website': 'Site de suporte',
    'sb.kb.f.support_in_person': 'Suporte presencial',
    'sb.kb.f.privacy_policy': 'Política de privacidade',
    'sb.kb.f.warranty_policy': 'Política de garantia',
    'sb.kb.f.terms_of_service': 'Termos de serviço',
    'sb.kb.f.service_guarantees': 'Garantias de serviço',
    'sb.kb.f.policies': 'Resumo das políticas gerais',
    'sb.kb.f.common_questions': 'Perguntas comuns dos clientes',
    'sb.kb.f.special_instructions': 'O que os clientes devem sempre saber',
    'sb.kb.pay.cash': 'Dinheiro',
    'sb.kb.pay.debit': 'Débito',
    'sb.kb.pay.credit': 'Crédito',
    'sb.kb.pay.bankTransfer': 'Transferência bancária',
    'sb.kb.pay.applePay': 'Apple Pay',
    'sb.kb.pay.googlePay': 'Google Pay',
    'sb.kb.pay.payPal': 'PayPal',
    'sb.kb.pay.mobileMoney': 'Mobile Money',
    'sb.kb.pay.interac': 'Interac e-Transfer',
    'sb.kb.pay.other': 'Outro',
    'sb.kb.revalidate': 'Revalidar',
    'sb.kb.runValidationHint': 'Execute a validação na aba Coleta para ver a prontidão.',
    'sb.kb.readiness': 'Prontidão',
    'sb.kb.readinessHint': 'A geração nunca é bloqueada. Fatos específicos do negócio que estejam faltando se tornam um FAQ Sugerido ou a resposta "Confirme esta política com o negócio." — nunca inventados.',
    'sb.kb.val.missingFields': 'Campos faltando',
    'sb.kb.val.missingFieldsEmpty': 'Nada obrigatório está faltando.',
    'sb.kb.val.contradictions': 'Contradições',
    'sb.kb.val.contradictionsEmpty': 'Nenhuma contradição encontrada.',
    'sb.kb.val.vague': 'Respostas vagas',
    'sb.kb.val.vagueEmpty': 'Nenhuma resposta vaga.',
    'sb.kb.val.followups': 'Perguntas de acompanhamento',
    'sb.kb.val.followupsEmpty': 'Nenhum acompanhamento.',
    'sb.kb.completeMore': '← Preencher mais campos',
    'sb.kb.continueGenerate': 'Continuar para Gerar →',
    'sb.kb.generate50': 'Gerar 50 Rascunhos de FAQs',
    'sb.kb.generate50Sub': 'Executa validação + geração por IA por meio do backend real. Os rascunhos são apenas internos até serem aprovados.',
    'sb.kb.generating': 'Gerando…',
    'sb.kb.internalVsLive': 'Rascunho e sugerido = apenas internos. Aprovado = ao vivo para o AI Chat/Voice.',
    'sb.kb.showArchived': 'Mostrar arquivados',
    'sb.kb.noGeneratedYet': 'Ainda não há FAQs geradas. Clique em Gerar 50 Rascunhos de FAQs acima.',
    'sb.kb.status.draft': 'Rascunho · apenas interno',
    'sb.kb.status.suggested': 'Sugerido · apenas interno',
    'sb.kb.status.pending': 'Pendente de aprovação · interno',
    'sb.kb.status.approved': 'Aprovado · ao vivo para IA',
    'sb.kb.status.archived': 'Arquivado',
    'sb.kb.status.rejected': 'Rejeitado',
    'sb.kb.ownerSupplied': 'Fornecido pelo proprietário',
    'sb.kb.aiSuggested': 'Sugerido por IA',
    'sb.kb.totalGenerated': 'Total gerado',
    'sb.kb.customerFacing': 'Voltado ao cliente (aprovado)',
    'sb.kb.internalDraft': 'Interno (rascunho/sugerido)',
    'sb.kb.categoryCoverage': 'Cobertura por categoria',
    'sb.kb.missing': 'faltando',
    'sb.kb.missingCategories': 'Categorias faltando: {cats}. Gere (ou adicione mais coleta) para cobri-las.',
    'sb.kb.gapsIntro': 'Perguntas de clientes sem resposta registradas para este negócio.',
    'sb.kb.noGaps': 'Nenhuma lacuna de conhecimento registrada.',
    'sb.kb.createDraftFaq': 'Criar rascunho de FAQ',
    'sb.kb.gapsLoadError': 'Não foi possível carregar as lacunas de conhecimento: {error}',
    'sb.kb.seenTimes': 'Vista {count}× · {status}',
    'sb.kb.editDraftFaq': 'Editar rascunho de FAQ',
    'sb.kb.createDraftFromGap': 'Criar rascunho de FAQ a partir da lacuna',
    'sb.kb.question': 'Pergunta',
    'sb.kb.answer': 'Resposta',
    'sb.kb.keywords': 'Palavras-chave (separadas por vírgula)',
    'sb.kb.answerPlaceholder': 'Escreva a resposta. Deixe de fora detalhes específicos do negócio se forem desconhecidos.',
    'sb.kb.editInternalHint': 'A edição mantém isto interno. Aprove separadamente para publicá-lo.',
    'sb.kb.createdDraftHint': 'Criado como rascunho (interno). Aprove-o para publicá-lo.',
    'sb.kb.saveDraft': 'Salvar rascunho',
    'sb.kb.createDraft': 'Criar rascunho',
    'sb.kb.noBusinessContext': 'Nenhum contexto de negócio ativo',
    'sb.kb.selectBusiness': 'Selecione ou vincule um negócio para usar o Construtor de Conhecimento.',
    'sb.kb.toast.noBusiness': 'Nenhum negócio selecionado',
    'sb.kb.toast.noBusinessDesc': 'Não é possível iniciar — nenhum contexto de negócio ativo.',
    'sb.kb.toast.started': 'Construtor de Conhecimento iniciado',
    'sb.kb.toast.startedDesc': 'Preencha o que você sabe — você pode pular qualquer item desconhecido.',
    'sb.kb.toast.couldNotStart': 'Não foi possível iniciar a sessão',
    'sb.kb.toast.progressSaved': 'Progresso salvo',
    'sb.kb.toast.savedFields': '{count} campo(s) salvo(s).',
    'sb.kb.toast.nothingToSave': 'Nada novo para salvar.',
    'sb.kb.toast.saveFailed': 'Falha ao salvar',
    'sb.kb.toast.validationFailed': 'Falha na validação',
    'sb.kb.toast.draftsGenerated': 'Rascunhos de FAQs gerados',
    'sb.kb.toast.draftsGeneratedDesc': '{count} itens de rascunho/sugestão criados (apenas internos).',
    'sb.kb.toast.generationFailed': 'Falha na geração',
    'sb.kb.toast.providerUnavailable': 'O provedor de geração de IA está indisponível no momento. Nenhum rascunho foi criado. Tente novamente mais tarde.',
    'sb.kb.toast.loadGenFailed': 'Não foi possível carregar as FAQs geradas',
    'sb.kb.toast.approved': 'Aprovado',
    'sb.kb.toast.approvedDesc': 'Esta FAQ agora está ao vivo para o AI Chat e Voice imediatamente.',
    'sb.kb.toast.approvalFailed': 'Falha na aprovação',
    'sb.kb.toast.rejected': 'Rejeitado',
    'sb.kb.toast.rejectedDesc': 'Este rascunho não será usado pela IA.',
    'sb.kb.toast.rejectFailed': 'Falha na rejeição',
    'sb.kb.toast.archived': 'Arquivado',
    'sb.kb.toast.archivedDesc': 'Item retirado (excluído da recuperação por IA).',
    'sb.kb.toast.archiveFailed': 'Falha ao arquivar',
    'sb.kb.toast.draftUpdated': 'Rascunho atualizado',
    'sb.kb.toast.draftUpdatedDesc': 'Sua edição foi salva. Ainda interno até ser aprovado.',
    'sb.kb.toast.updateFailed': 'Falha na atualização',
    'sb.kb.toast.draftCreated': 'Rascunho de FAQ criado',
    'sb.kb.toast.draftCreatedDesc': 'Criado como rascunho (interno). Aprove-o para publicá-lo.',
    'sb.kb.toast.couldNotCreate': 'Não foi possível criar o rascunho',
    'sb.live.approvalsImmediate': 'As aprovações têm efeito imediato — sem atraso de retreinamento',
    'sb.live.approvalsBlurb': 'As soluções aprovadas ficam disponíveis para o AI Chat e o AI Voice imediatamente. Nenhum atraso de retreinamento é necessário porque a Servisfy lê o conhecimento aprovado do SolutionBank ao vivo a cada interação de IA.',
    'sb.live.aiChatLive': 'AI Chat: ao vivo',
    'sb.live.aiVoiceLive': 'AI Voice: ao vivo',
    'sb.live.business': 'Negócio: {name}',
    'sb.live.liveInAi': 'Ao vivo na IA (aprovado)',
    'sb.live.usedByAiNow': 'Usado agora pelo AI Chat e Voice',
    'sb.live.pendingApproval': 'Pendente de aprovação',
    'sb.live.notYetUsed': 'Ainda não usado pela IA',
    'sb.live.draftsSuggested': 'Rascunhos / sugeridos',
    'sb.live.internalOnly': 'Apenas interno (Construtor de Conhecimento)',
    'sb.live.inactive': 'Inativo',
    'sb.live.rejectedRevoked': 'Rejeitado / revogado / arquivado',
    'sb.live.totalUsage': 'Uso total de IA (aprovado)',
    'sb.live.realUsageSum': 'Soma real de usage_count',
    'sb.live.avgSuccess': 'Taxa média de sucesso',
    'sb.live.noUsageYet': 'Nenhum uso registrado ainda',
    'sb.live.acrossUsed': 'Entre as soluções aprovadas utilizadas',
    'sb.live.lastApproved': 'Última aprovação',
    'sb.live.effectiveImmediately': 'Em vigor imediatamente',
    'sb.live.noApprovalsYet': 'Nenhuma aprovação ainda',
    'sb.live.topUsed': 'Soluções aprovadas mais usadas',
    'sb.live.topUsedEmpty': 'Nenhuma solução aprovada foi usada pela IA ainda.',
    'sb.live.recentlyApproved': 'Aprovadas recentemente',
    'sb.live.recentlyApprovedEmpty': 'Nenhuma solução aprovada ainda. Aprove na Revisão para ativar.',
    'sb.live.lowSuccess': 'Aprovada, mas com baixo sucesso — pode precisar de revisão',
    'sb.live.lowSuccessEmpty': 'Nenhuma solução aprovada com baixo sucesso. 👍',
    'sb.live.lowSuccessHint': 'Soluções aprovadas usadas pela IA com taxa de sucesso abaixo de {threshold}%.',
    'sb.live.loading': 'Carregando conhecimento de IA ao vivo…',
    'sb.live.loadError': 'Não foi possível carregar o conhecimento de IA',
    'sb.live.tryAgain': 'Tentar novamente',
    'sb.live.loadFailedToast': 'Falha ao carregar o conhecimento de IA',
    'sb.live.untitled': 'Sem título',
    'sb.live.uncategorized': 'Sem categoria',
    'sb.review.title': 'Revisão do Banco de Soluções',
    'sb.review.subtitle': 'Revise e aprove as soluções sugeridas por IA para o Banco de Soluções',
    'sb.review.pendingReview': 'Pendente de Revisão',
    'sb.review.approvedTab': 'Aprovadas',
    'sb.review.rejectedTab': 'Rejeitadas',
    'sb.review.noPending': 'Nenhuma solução pendente para revisar',
    'sb.review.noPendingSub': 'As soluções sugeridas por IA aparecerão aqui para aprovação',
    'sb.review.noApproved': 'Nenhuma solução aprovada',
    'sb.review.noRejected': 'Nenhuma solução rejeitada',
    'sb.review.description': 'Descrição:',
    'sb.review.resolutionSteps': 'Etapas de Resolução:',
    'sb.review.keywords': 'Palavras-chave:',
    'sb.review.submittedBy': 'Enviado por {name}',
    'sb.review.staff': 'equipe',
    'sb.review.aiVoice': 'AI Voice',
    'sb.review.aiChat': 'AI Chat',
    'sb.review.ch.humanChat': 'Chat humano',
    'sb.review.ch.voiceCall': 'Chamada de voz',
    'sb.review.ch.humanCall': 'Chamada com atendente',
    'sb.review.ch.solutionBank': 'Banco de soluções',
    'sb.review.ch.unknown': 'Desconhecido',
    'sb.review.aiGenerated': 'Gerado por IA',
    'sb.review.openaiFallback': 'Fallback do OpenAI',
    'sb.review.sourceConversation': 'Origem: Conversa {id}',
    'sb.review.confHigh': 'Alta ({pct}%)',
    'sb.review.confMedium': 'Média ({pct}%)',
    'sb.review.confLow': 'Baixa ({pct}%)',
    'sb.review.activeInAi': 'Ativa na IA',
    'sb.review.approvedOn': 'Aprovada em {date}',
    'sb.review.rejectedOn': 'Rejeitada em {date}',
    'sb.review.revoke': 'Revogar',
    'sb.review.resubmit': 'Reenviar para Revisão',
    'sb.review.unknownDate': 'Desconhecida',
    'sb.review.loadFailedDesc': 'Verifique sua conexão e tente novamente.',
    'sb.review.toast.approved': 'Solução Aprovada',
    'sb.review.toast.approvedDesc': 'A solução agora está ao vivo na base de conhecimento de IA.',
    'sb.review.toast.approvalFailed': 'Falha na Aprovação',
    'sb.review.toast.rejected': 'Solução Rejeitada',
    'sb.review.toast.rejectedDesc': 'A solução não será usada pela IA.',
    'sb.review.toast.rejectionFailed': 'Falha na Rejeição',
    'sb.review.toast.revoked': 'Solução Revogada',
    'sb.review.toast.revokedDesc': 'A IA não usará mais esta solução.',
    'sb.review.toast.revokeFailed': 'Falha na Revogação',
    'sb.review.toast.resubmitted': 'Solução Reenviada',
    'sb.review.toast.resubmittedDesc': 'A solução está pendente de revisão novamente.',
    'sb.review.toast.resubmitFailed': 'Falha no Reenvio',
    'sb.review.toast.deleted': 'Solução Excluída',
    'sb.review.toast.deletedDesc': 'Solução removida permanentemente.',
    'sb.review.toast.deleteFailed': 'Falha na Exclusão',
  },
  de: {
    'sb.tab.solutions': 'Lösungen',
    'sb.tab.knowledgeBuilder': 'KI-Wissensassistent',
    'sb.tab.review': 'Prüfung',
    'sb.tab.analytics': 'Analysen',
    'sb.tab.liveAi': 'Live-KI-Wissen',
    'sb.tab.addSolution': 'Lösung hinzufügen',
    'sb.tab.manageSolutions': 'Lösungen verwalten',
    'sb.exporting': 'Wird exportiert...',
    'sb.importing': 'Wird importiert...',
    'sb.searchPlaceholder': 'Lösungen suchen...',
    'sb.filterSolutions': 'Lösungen filtern',
    'sb.filterSolutionsDesc': 'Lösungen nach Kriterien eingrenzen',
    'sb.difficulty': 'Schwierigkeitsgrad',
    'sb.allDifficulties': 'Alle Schwierigkeitsgrade',
    'sb.easy': 'Einfach',
    'sb.medium': 'Mittel',
    'sb.hard': 'Schwer',
    'sb.reset': 'Zurücksetzen',
    'sb.apply': 'Anwenden',
    'sb.allCategories': 'Alle Kategorien',
    'sb.revoke.action': 'Widerrufen',
    'sb.revoke.confirm': 'Diese genehmigte Lösung widerrufen? Sie wird von der KI nicht mehr verwendet, bis sie korrigiert und erneut genehmigt wurde.',
    'sb.revoke.done': 'Lösung widerrufen',
    'sb.revoke.doneDesc': 'Sie wird nicht mehr von der KI verwendet. Bearbeiten und erneut zur Prüfung einreichen.',
    'sb.revoke.failed': 'Widerruf fehlgeschlagen',
    'sb.cat.choose': 'Kategorie auswählen',
    'sb.cat.noneConfigured': 'Keine Kategorien konfiguriert',
    'sb.cat.noneConfiguredHint': 'Fügen Sie Kategorien in der Kategorieverwaltung hinzu, bevor Sie Lösungen erstellen.',
    'sb.cat.technicalSupport': 'Technischer Support',
    'sb.cat.billing': 'Abrechnung',
    'sb.cat.productIssues': 'Produktprobleme',
    'sb.cat.accountManagement': 'Kontoverwaltung',
    'sb.totalSolutions': 'Lösungen gesamt',
    'sb.avgSuccessRate': 'Durchschnittliche Erfolgsquote',
    'sb.totalUsage': 'Nutzung gesamt',
    'sb.categories': 'Kategorien',
    'sb.topPerforming': 'Leistungsstärkste Lösungen',
    'sb.mostUsed': 'Meistgenutzte Lösungen',
    'sb.loadingSolutions': 'Lösungen werden geladen...',
    'sb.loadingSolutionsSub': 'Bitte warten Sie, während wir Ihre Lösungsdatenbank abrufen',
    'sb.noSolutionsFound': 'Keine Lösungen gefunden',
    'sb.noSolutionsHint': 'Passen Sie Ihre Suchkriterien an oder verwenden Sie den Tab „Verwaltung“, um Lösungen hinzuzufügen',
    'sb.mgmtFunctionsHint': 'Die Funktionen zur Lösungsverwaltung finden Sie im Tab „Verwaltung“',
    'sb.successRateBadge': '{rate}% Erfolgsquote',
    'sb.usesBadge': '{count} Verwendungen',
    'sb.usesLower': '{count} Verwendungen',
    'sb.updated': 'Aktualisiert: {date}',
    'sb.quickTips': 'Kurztipps',
    'sb.tip1': 'Formulieren Sie Lösungsbeschreibungen präzise und verständlich',
    'sb.tip2': 'Fügen Sie Schritt-für-Schritt-Anleitungen hinzu',
    'sb.tip3': 'Versehen Sie Lösungen mit relevanten Schlüsselwörtern',
    'sb.tip4': 'Verweisen Sie bei komplexen Problemen auf verwandte Lösungen',
    'sb.recentActivity': 'Letzte Aktivität',
    'sb.noRecentActivity': 'Noch keine aktuelle Aktivität.',
    'sb.solutionsAddedToday': 'Heute hinzugefügte Lösungen:',
    'sb.pendingReviewLabel': 'Ausstehende Prüfung:',
    'sb.successRateLabel': 'Erfolgsquote:',
    'sb.bulkOperations': 'Massenvorgänge',
    'sb.importFromCsv': 'Lösungen aus CSV importieren',
    'sb.exportAll': 'Alle Lösungen exportieren',
    'sb.generateReport': 'Leistungsbericht erstellen',
    'sb.importCsvDesc': 'Laden Sie eine CSV-Datei hoch, um mehrere Lösungen zu importieren. Die CSV sollte folgende Spalten enthalten: title, description, category, tags, resolution, difficulty, success_rate, usage_count.',
    'sb.selectCsv': 'Wählen Sie eine CSV-Datei zum Hochladen aus',
    'sb.selectedFile': 'Ausgewählt: {name}',
    'sb.importSolutionsBtn': 'Lösungen importieren',
    'sb.exportAllDesc': 'Wählen Sie das bevorzugte Exportformat für alle Lösungen',
    'sb.csvFile': 'CSV-Datei',
    'sb.excelFile': 'Excel-Datei',
    'sb.jsonData': 'JSON-Daten',
    'sb.pdfReport': 'PDF-Bericht',
    'sb.performanceReport': 'Leistungsbericht der Lösungsdatenbank',
    'sb.performanceReportDesc': 'KI-generierte Erkenntnisse und Leistungsanalyse',
    'sb.aiAnalysisReport': 'KI-Analysebericht',
    'sb.downloadReport': 'Bericht herunterladen',
    'sb.editSolution': 'Lösung bearbeiten',
    'sb.editSolutionDesc': 'Aktualisieren Sie die Lösungsinformationen',
    'sb.addNewSolutionTitle': 'Neue Lösung hinzufügen',
    'sb.addNewSolutionDesc': 'Erstellen Sie eine neue Lösung für Ihre Lösungsdatenbank',
    'sb.solutionTitle': 'Lösungstitel *',
    'sb.solutionTitlePlaceholder': 'Lösungstitel eingeben',
    'sb.descriptionLabel': 'Beschreibung *',
    'sb.descriptionPlaceholder': 'Beschreiben Sie das Problem, das diese Lösung behebt',
    'sb.category': 'Kategorie',
    'sb.tagsLabel': 'Schlagwörter (durch Komma getrennt)',
    'sb.tagsPlaceholder': 'passwort, zurücksetzen, anmeldung',
    'sb.resolutionSteps': 'Lösungsschritte',
    'sb.resolutionPlaceholder': 'Geben Sie eine Schritt-für-Schritt-Lösungsanleitung ein',
    'sb.updateSolution': 'Lösung aktualisieren',
    'sb.addSolutionBtn': 'Lösung hinzufügen',
    'sb.deleteSolution': 'Lösung löschen',
    'sb.deleteSolutionDesc': 'Möchten Sie diese Lösung wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    'sb.toast.loadFailed': 'Lösungen konnten nicht geladen werden',
    'sb.toast.missingInfo': 'Fehlende Angaben',
    'sb.toast.missingInfoDesc': 'Bitte füllen Sie Titel und Beschreibung aus',
    'sb.toast.submitted': 'Lösung eingereicht',
    'sb.toast.submittedDesc': 'Die Lösung wurde zur Genehmigung an die Führungskraft weitergeleitet',
    'sb.toast.addFailed': 'Lösung konnte nicht hinzugefügt werden',
    'sb.toast.exportStarted': 'Export gestartet',
    'sb.toast.exportStartedDesc': 'Lösungen werden als {format} exportiert',
    'sb.toast.exportComplete': 'Export abgeschlossen',
    'sb.toast.exportCompleteDesc': 'Lösungen erfolgreich als {format} exportiert',
    'sb.toast.exportFailed': 'Export fehlgeschlagen',
    'sb.toast.exportFailedDesc': 'Lösungen konnten nicht exportiert werden',
    'sb.toast.noFile': 'Keine Datei ausgewählt',
    'sb.toast.noFileDesc': 'Bitte wählen Sie eine CSV-Datei zum Importieren aus',
    'sb.toast.importStarted': 'Import gestartet',
    'sb.toast.importStartedDesc': 'CSV-Datei wird verarbeitet...',
    'sb.toast.importComplete': 'Import abgeschlossen',
    'sb.toast.importCompleteDesc': 'Lösungen erfolgreich importiert',
    'sb.toast.importFailed': 'Import fehlgeschlagen',
    'sb.toast.importFailedDesc': 'Lösungen konnten nicht aus der CSV importiert werden',
    'sb.toast.invalidFile': 'Ungültige Datei',
    'sb.toast.invalidFileDesc': 'Bitte wählen Sie eine gültige CSV-Datei aus',
    'sb.toast.reportGenerated': 'Bericht erstellt',
    'sb.toast.reportGeneratedDesc': 'Der Leistungsbericht wurde erfolgreich erstellt',
    'sb.toast.reportFailed': 'Berichterstellung fehlgeschlagen',
    'sb.toast.reportFailedDesc': 'Der Leistungsbericht konnte nicht erstellt werden',
    'sb.toast.updated': 'Lösung aktualisiert',
    'sb.toast.updatedDesc': 'Die Lösung wurde erfolgreich aktualisiert',
    'sb.toast.updateFailed': 'Lösung konnte nicht aktualisiert werden',
    'sb.toast.deleted': 'Lösung gelöscht',
    'sb.toast.deletedDesc': 'Die Lösung wurde erfolgreich gelöscht',
    'sb.toast.deleteFailed': 'Lösung konnte nicht gelöscht werden',
    'sb.kb.title': 'KI-Wissensassistent',
    'sb.kb.subtitle': 'Erfassen Sie Ihre Geschäftsdaten → generieren Sie 50 FAQ-Entwürfe → prüfen & genehmigen. Entwürfe bleiben intern, bis sie genehmigt sind.',
    'sb.kb.newSession': 'Neue Sitzung',
    'sb.kb.buildTitle': 'Erstellen Sie Ihre KI-Wissensdatenbank',
    'sb.kb.buildBlurb': 'Beantworten Sie, was Sie über Ihr Unternehmen wissen. Wir generieren 50 FAQ-Entwürfe/-Vorschläge. Nichts wird für Kunden freigeschaltet, bevor eine Führungskraft oder ein Administrator es genehmigt.',
    'sb.kb.start': 'Wissensassistenten starten',
    'sb.kb.tab.intake': 'Erfassung',
    'sb.kb.tab.validate': 'Validierung',
    'sb.kb.tab.review': 'Generieren & Prüfen',
    'sb.kb.tab.coverage': 'Abdeckung',
    'sb.kb.tab.gaps': 'Lücken',
    'sb.kb.skipHint': 'Überspringen Sie alles Unbekannte — unbekannte Felder werden niemals erfunden.',
    'sb.kb.saveProgress': 'Fortschritt speichern',
    'sb.kb.blankUnknown': 'Leer lassen, falls unbekannt',
    'sb.kb.blankUnknownTextarea': 'Leer lassen, falls unbekannt — es wird nicht erfunden',
    'sb.kb.unknownSkip': 'Unbekannt / überspringen',
    'sb.kb.sec.identity': 'Unternehmensidentität',
    'sb.kb.sec.profile': 'Unternehmensprofil',
    'sb.kb.sec.operations': 'Betrieb',
    'sb.kb.sec.payments': 'Zahlungen',
    'sb.kb.sec.delivery': 'Versand / Lieferung',
    'sb.kb.sec.booking': 'Terminbuchung',
    'sb.kb.sec.returns': 'Rückgaben / Rückerstattungen',
    'sb.kb.sec.support': 'Support',
    'sb.kb.sec.policies': 'Richtlinien',
    'sb.kb.sec.extras': 'Kundenfragen & Hinweise',
    'sb.kb.f.business_name': 'Unternehmensname',
    'sb.kb.f.address': 'Adresse',
    'sb.kb.f.city': 'Stadt',
    'sb.kb.f.province': 'Provinz / Bundesland',
    'sb.kb.f.country': 'Land',
    'sb.kb.f.phone': 'Telefon',
    'sb.kb.f.email': 'E-Mail',
    'sb.kb.f.website': 'Website',
    'sb.kb.f.social_media': 'Soziale Medien',
    'sb.kb.f.business_description': 'Unternehmensbeschreibung',
    'sb.kb.f.products': 'Verkaufte Produkte',
    'sb.kb.f.services': 'Angebotene Dienstleistungen',
    'sb.kb.f.target_customers': 'Zielkunden',
    'sb.kb.f.industries_served': 'Bediente Branchen',
    'sb.kb.f.differentiator': 'Alleinstellungsmerkmal',
    'sb.kb.f.mission_vision': 'Mission / Vision',
    'sb.kb.f.opening_days': 'Öffnungstage',
    'sb.kb.f.operating_hours': 'Öffnungszeiten',
    'sb.kb.f.holiday_hours': 'Feiertagsöffnungszeiten',
    'sb.kb.f.appointment_required': 'Termin erforderlich',
    'sb.kb.f.walk_ins_accepted': 'Laufkundschaft willkommen',
    'sb.kb.f.delivery_available': 'Lieferung verfügbar',
    'sb.kb.f.pickup_available': 'Abholung verfügbar',
    'sb.kb.f.online_orders': 'Online-Bestellungen',
    'sb.kb.f.service_area': 'Einzugsgebiet',
    'sb.kb.f.payment_methods': 'Akzeptierte Zahlungsmethoden',
    'sb.kb.f.delivery_locations': 'Lieferorte',
    'sb.kb.f.delivery_fees': 'Liefergebühren',
    'sb.kb.f.delivery_timelines': 'Lieferzeiten',
    'sb.kb.f.same_day_delivery': 'Lieferung am selben Tag',
    'sb.kb.f.international_shipping': 'Internationaler Versand',
    'sb.kb.f.booking_method': 'Wie Termine gebucht werden',
    'sb.kb.f.cancellation_policy': 'Stornierungsrichtlinie',
    'sb.kb.f.rescheduling_policy': 'Umbuchungsrichtlinie',
    'sb.kb.f.late_arrival_policy': 'Richtlinie bei verspäteter Ankunft',
    'sb.kb.f.ordering_method': 'Wie Online-Bestellungen aufgegeben werden',
    'sb.kb.f.return_policy': 'Rückgaberichtlinie',
    'sb.kb.f.refund_policy': 'Rückerstattungsrichtlinie',
    'sb.kb.f.exchange_policy': 'Umtauschrichtlinie',
    'sb.kb.f.contact_methods': 'Primäre Kontaktmöglichkeiten',
    'sb.kb.f.support_phone': 'Support-Telefon',
    'sb.kb.f.support_whatsapp': 'WhatsApp',
    'sb.kb.f.support_email': 'Support-E-Mail',
    'sb.kb.f.support_website': 'Support-Website',
    'sb.kb.f.support_in_person': 'Support vor Ort',
    'sb.kb.f.privacy_policy': 'Datenschutzrichtlinie',
    'sb.kb.f.warranty_policy': 'Garantierichtlinie',
    'sb.kb.f.terms_of_service': 'Nutzungsbedingungen',
    'sb.kb.f.service_guarantees': 'Servicegarantien',
    'sb.kb.f.policies': 'Zusammenfassung der allgemeinen Richtlinien',
    'sb.kb.f.common_questions': 'Häufige Fragen der Kunden',
    'sb.kb.f.special_instructions': 'Was Kunden immer wissen sollten',
    'sb.kb.pay.cash': 'Bargeld',
    'sb.kb.pay.debit': 'Debitkarte',
    'sb.kb.pay.credit': 'Kreditkarte',
    'sb.kb.pay.bankTransfer': 'Banküberweisung',
    'sb.kb.pay.applePay': 'Apple Pay',
    'sb.kb.pay.googlePay': 'Google Pay',
    'sb.kb.pay.payPal': 'PayPal',
    'sb.kb.pay.mobileMoney': 'Mobiles Bezahlen',
    'sb.kb.pay.interac': 'Interac e-Transfer',
    'sb.kb.pay.other': 'Sonstige',
    'sb.kb.revalidate': 'Erneut validieren',
    'sb.kb.runValidationHint': 'Führen Sie die Validierung im Tab „Erfassung“ aus, um die Bereitschaft zu sehen.',
    'sb.kb.readiness': 'Bereitschaft',
    'sb.kb.readinessHint': 'Die Generierung wird niemals blockiert. Fehlende unternehmensspezifische Fakten werden zu einer vorgeschlagenen FAQ oder zur Antwort "Bitte bestätigen Sie diese Richtlinie mit dem Unternehmen." — niemals erfunden.',
    'sb.kb.val.missingFields': 'Fehlende Felder',
    'sb.kb.val.missingFieldsEmpty': 'Es fehlt nichts Erforderliches.',
    'sb.kb.val.contradictions': 'Widersprüche',
    'sb.kb.val.contradictionsEmpty': 'Keine Widersprüche gefunden.',
    'sb.kb.val.vague': 'Vage Antworten',
    'sb.kb.val.vagueEmpty': 'Keine vagen Antworten.',
    'sb.kb.val.followups': 'Rückfragen',
    'sb.kb.val.followupsEmpty': 'Keine Rückfragen.',
    'sb.kb.completeMore': '← Weitere Felder ausfüllen',
    'sb.kb.continueGenerate': 'Weiter zur Generierung →',
    'sb.kb.generate50': '50 FAQ-Entwürfe generieren',
    'sb.kb.generate50Sub': 'Führt Validierung + KI-Generierung über das reale Backend aus. Entwürfe sind nur intern, bis sie genehmigt sind.',
    'sb.kb.generating': 'Wird generiert…',
    'sb.kb.internalVsLive': 'Entwurf & Vorschlag = nur intern. Genehmigt = live für KI-Chat/-Voice.',
    'sb.kb.showArchived': 'Archivierte anzeigen',
    'sb.kb.noGeneratedYet': 'Noch keine generierten FAQs. Klicken Sie oben auf „50 FAQ-Entwürfe generieren“.',
    'sb.kb.status.draft': 'Entwurf · nur intern',
    'sb.kb.status.suggested': 'Vorgeschlagen · nur intern',
    'sb.kb.status.pending': 'Genehmigung ausstehend · intern',
    'sb.kb.status.approved': 'Genehmigt · live für KI',
    'sb.kb.status.archived': 'Archiviert',
    'sb.kb.status.rejected': 'Abgelehnt',
    'sb.kb.ownerSupplied': 'Vom Inhaber bereitgestellt',
    'sb.kb.aiSuggested': 'KI-vorgeschlagen',
    'sb.kb.totalGenerated': 'Insgesamt generiert',
    'sb.kb.customerFacing': 'Kundenseitig (genehmigt)',
    'sb.kb.internalDraft': 'Intern (Entwurf/Vorschlag)',
    'sb.kb.categoryCoverage': 'Kategorieabdeckung',
    'sb.kb.missing': 'fehlend',
    'sb.kb.missingCategories': 'Fehlende Kategorien: {cats}. Generieren Sie (oder erfassen Sie mehr), um sie abzudecken.',
    'sb.kb.gapsIntro': 'Für dieses Unternehmen erfasste unbeantwortete Kundenfragen.',
    'sb.kb.noGaps': 'Keine Wissenslücken erfasst.',
    'sb.kb.createDraftFaq': 'FAQ-Entwurf erstellen',
    'sb.kb.gapsLoadError': 'Wissenslücken konnten nicht geladen werden: {error}',
    'sb.kb.seenTimes': '{count}× gesehen · {status}',
    'sb.kb.editDraftFaq': 'FAQ-Entwurf bearbeiten',
    'sb.kb.createDraftFromGap': 'FAQ-Entwurf aus Lücke erstellen',
    'sb.kb.question': 'Frage',
    'sb.kb.answer': 'Antwort',
    'sb.kb.keywords': 'Schlüsselwörter (durch Komma getrennt)',
    'sb.kb.answerPlaceholder': 'Verfassen Sie die Antwort. Lassen Sie unternehmensspezifische Angaben weg, wenn sie unbekannt sind.',
    'sb.kb.editInternalHint': 'Durch die Bearbeitung bleibt dies intern. Genehmigen Sie es separat, um es live zu schalten.',
    'sb.kb.createdDraftHint': 'Als Entwurf erstellt (intern). Genehmigen Sie ihn, um ihn live zu schalten.',
    'sb.kb.saveDraft': 'Entwurf speichern',
    'sb.kb.createDraft': 'Entwurf erstellen',
    'sb.kb.noBusinessContext': 'Kein aktiver Unternehmenskontext',
    'sb.kb.selectBusiness': 'Wählen Sie ein Unternehmen aus oder verknüpfen Sie eines, um den Wissensassistenten zu verwenden.',
    'sb.kb.toast.noBusiness': 'Kein Unternehmen ausgewählt',
    'sb.kb.toast.noBusinessDesc': 'Start nicht möglich — kein aktiver Unternehmenskontext.',
    'sb.kb.toast.started': 'Wissensassistent gestartet',
    'sb.kb.toast.startedDesc': 'Tragen Sie ein, was Sie wissen — Unbekanntes können Sie überspringen.',
    'sb.kb.toast.couldNotStart': 'Sitzung konnte nicht gestartet werden',
    'sb.kb.toast.progressSaved': 'Fortschritt gespeichert',
    'sb.kb.toast.savedFields': '{count} Feld(er) gespeichert.',
    'sb.kb.toast.nothingToSave': 'Nichts Neues zu speichern.',
    'sb.kb.toast.saveFailed': 'Speichern fehlgeschlagen',
    'sb.kb.toast.validationFailed': 'Validierung fehlgeschlagen',
    'sb.kb.toast.draftsGenerated': 'FAQ-Entwürfe generiert',
    'sb.kb.toast.draftsGeneratedDesc': '{count} Entwurfs-/Vorschlagselemente erstellt (nur intern).',
    'sb.kb.toast.generationFailed': 'Generierung fehlgeschlagen',
    'sb.kb.toast.providerUnavailable': 'Der KI-Generierungsanbieter ist derzeit nicht verfügbar. Es wurden keine Entwürfe erstellt. Bitte versuchen Sie es später erneut.',
    'sb.kb.toast.loadGenFailed': 'Generierte FAQs konnten nicht geladen werden',
    'sb.kb.toast.approved': 'Genehmigt',
    'sb.kb.toast.approvedDesc': 'Diese FAQ ist ab sofort live für KI-Chat & Voice.',
    'sb.kb.toast.approvalFailed': 'Genehmigung fehlgeschlagen',
    'sb.kb.toast.rejected': 'Abgelehnt',
    'sb.kb.toast.rejectedDesc': 'Dieser Entwurf wird von der KI nicht verwendet.',
    'sb.kb.toast.rejectFailed': 'Ablehnung fehlgeschlagen',
    'sb.kb.toast.archived': 'Archiviert',
    'sb.kb.toast.archivedDesc': 'Element stillgelegt (von der KI-Abfrage ausgeschlossen).',
    'sb.kb.toast.archiveFailed': 'Archivierung fehlgeschlagen',
    'sb.kb.toast.draftUpdated': 'Entwurf aktualisiert',
    'sb.kb.toast.draftUpdatedDesc': 'Ihre Änderung wurde gespeichert. Bleibt intern, bis sie genehmigt ist.',
    'sb.kb.toast.updateFailed': 'Aktualisierung fehlgeschlagen',
    'sb.kb.toast.draftCreated': 'FAQ-Entwurf erstellt',
    'sb.kb.toast.draftCreatedDesc': 'Als Entwurf erstellt (intern). Genehmigen Sie ihn, um ihn live zu schalten.',
    'sb.kb.toast.couldNotCreate': 'Entwurf konnte nicht erstellt werden',
    'sb.live.approvalsImmediate': 'Genehmigungen werden sofort wirksam — keine Verzögerung durch erneutes Training',
    'sb.live.approvalsBlurb': 'Genehmigte Lösungen stehen KI-Chat und KI-Voice sofort zur Verfügung. Es ist keine Verzögerung durch erneutes Training erforderlich, da Servisfy das genehmigte SolutionBank-Wissen bei jeder KI-Interaktion live liest.',
    'sb.live.aiChatLive': 'KI-Chat: live',
    'sb.live.aiVoiceLive': 'KI-Voice: live',
    'sb.live.business': 'Unternehmen: {name}',
    'sb.live.liveInAi': 'Live in der KI (genehmigt)',
    'sb.live.usedByAiNow': 'Wird jetzt von KI-Chat & Voice verwendet',
    'sb.live.pendingApproval': 'Genehmigung ausstehend',
    'sb.live.notYetUsed': 'Noch nicht von der KI verwendet',
    'sb.live.draftsSuggested': 'Entwürfe / vorgeschlagen',
    'sb.live.internalOnly': 'Nur intern (Wissensassistent)',
    'sb.live.inactive': 'Inaktiv',
    'sb.live.rejectedRevoked': 'Abgelehnt / widerrufen / archiviert',
    'sb.live.totalUsage': 'Gesamte KI-Nutzung (genehmigt)',
    'sb.live.realUsageSum': 'Tatsächliche usage_count-Summe',
    'sb.live.avgSuccess': 'Durchschnittliche Erfolgsquote',
    'sb.live.noUsageYet': 'Noch keine Nutzung erfasst',
    'sb.live.acrossUsed': 'Über die verwendeten genehmigten Lösungen hinweg',
    'sb.live.lastApproved': 'Zuletzt genehmigt',
    'sb.live.effectiveImmediately': 'Sofort wirksam',
    'sb.live.noApprovalsYet': 'Noch keine Genehmigungen',
    'sb.live.topUsed': 'Meistgenutzte genehmigte Lösungen',
    'sb.live.topUsedEmpty': 'Es wurden noch keine genehmigten Lösungen von der KI verwendet.',
    'sb.live.recentlyApproved': 'Kürzlich genehmigt',
    'sb.live.recentlyApprovedEmpty': 'Noch keine genehmigten Lösungen. Genehmigen Sie über die Prüfung, um sie zu aktivieren.',
    'sb.live.lowSuccess': 'Genehmigt, aber geringe Erfolgsquote — eventuell Prüfung erforderlich',
    'sb.live.lowSuccessEmpty': 'Keine genehmigten Lösungen mit geringer Erfolgsquote. 👍',
    'sb.live.lowSuccessHint': 'Genehmigte Lösungen, die von der KI mit einer Erfolgsquote unter {threshold}% verwendet werden.',
    'sb.live.loading': 'Live-KI-Wissen wird geladen…',
    'sb.live.loadError': 'KI-Wissen konnte nicht geladen werden',
    'sb.live.tryAgain': 'Erneut versuchen',
    'sb.live.loadFailedToast': 'KI-Wissen konnte nicht geladen werden',
    'sb.live.untitled': 'Ohne Titel',
    'sb.live.uncategorized': 'Nicht kategorisiert',
    'sb.review.title': 'Prüfung der Lösungsdatenbank',
    'sb.review.subtitle': 'Prüfen und genehmigen Sie KI-vorgeschlagene Lösungen für die Lösungsdatenbank',
    'sb.review.pendingReview': 'Ausstehende Prüfung',
    'sb.review.approvedTab': 'Genehmigt',
    'sb.review.rejectedTab': 'Abgelehnt',
    'sb.review.noPending': 'Keine ausstehenden Lösungen zur Prüfung',
    'sb.review.noPendingSub': 'KI-vorgeschlagene Lösungen erscheinen hier zur Genehmigung',
    'sb.review.noApproved': 'Keine genehmigten Lösungen',
    'sb.review.noRejected': 'Keine abgelehnten Lösungen',
    'sb.review.description': 'Beschreibung:',
    'sb.review.resolutionSteps': 'Lösungsschritte:',
    'sb.review.keywords': 'Schlüsselwörter:',
    'sb.review.submittedBy': 'Eingereicht von {name}',
    'sb.review.staff': 'Mitarbeiter',
    'sb.review.aiVoice': 'KI-Voice',
    'sb.review.aiChat': 'KI-Chat',
    'sb.review.ch.humanChat': 'Menschlicher Chat',
    'sb.review.ch.voiceCall': 'Sprachanruf',
    'sb.review.ch.humanCall': 'Anruf mit Mitarbeiter',
    'sb.review.ch.solutionBank': 'Lösungsdatenbank',
    'sb.review.ch.unknown': 'Unbekannt',
    'sb.review.aiGenerated': 'KI-generiert',
    'sb.review.openaiFallback': 'OpenAI-Fallback',
    'sb.review.sourceConversation': 'Quelle: Konversation {id}',
    'sb.review.confHigh': 'Hoch ({pct}%)',
    'sb.review.confMedium': 'Mittel ({pct}%)',
    'sb.review.confLow': 'Niedrig ({pct}%)',
    'sb.review.activeInAi': 'Aktiv in der KI',
    'sb.review.approvedOn': 'Genehmigt {date}',
    'sb.review.rejectedOn': 'Abgelehnt {date}',
    'sb.review.revoke': 'Widerrufen',
    'sb.review.resubmit': 'Zur erneuten Prüfung einreichen',
    'sb.review.unknownDate': 'Unbekannt',
    'sb.review.loadFailedDesc': 'Überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    'sb.review.toast.approved': 'Lösung genehmigt',
    'sb.review.toast.approvedDesc': 'Die Lösung ist jetzt live in der KI-Wissensdatenbank.',
    'sb.review.toast.approvalFailed': 'Genehmigung fehlgeschlagen',
    'sb.review.toast.rejected': 'Lösung abgelehnt',
    'sb.review.toast.rejectedDesc': 'Die Lösung wird von der KI nicht verwendet.',
    'sb.review.toast.rejectionFailed': 'Ablehnung fehlgeschlagen',
    'sb.review.toast.revoked': 'Lösung widerrufen',
    'sb.review.toast.revokedDesc': 'Die KI wird diese Lösung nicht mehr verwenden.',
    'sb.review.toast.revokeFailed': 'Widerruf fehlgeschlagen',
    'sb.review.toast.resubmitted': 'Lösung erneut eingereicht',
    'sb.review.toast.resubmittedDesc': 'Die Lösung wird erneut geprüft.',
    'sb.review.toast.resubmitFailed': 'Erneute Einreichung fehlgeschlagen',
    'sb.review.toast.deleted': 'Lösung gelöscht',
    'sb.review.toast.deletedDesc': 'Lösung dauerhaft entfernt.',
    'sb.review.toast.deleteFailed': 'Löschen fehlgeschlagen',
  },
  it: {
    'sb.tab.solutions': 'Soluzioni',
    'sb.tab.knowledgeBuilder': 'Generatore di conoscenza AI',
    'sb.tab.review': 'Revisione',
    'sb.tab.analytics': 'Analisi',
    'sb.tab.liveAi': 'Conoscenza AI in tempo reale',
    'sb.tab.addSolution': 'Aggiungi soluzione',
    'sb.tab.manageSolutions': 'Gestisci soluzioni',
    'sb.exporting': 'Esportazione in corso...',
    'sb.importing': 'Importazione in corso...',
    'sb.searchPlaceholder': 'Cerca soluzioni...',
    'sb.filterSolutions': 'Filtra soluzioni',
    'sb.filterSolutionsDesc': 'Restringi le soluzioni in base ai criteri',
    'sb.difficulty': 'Difficoltà',
    'sb.allDifficulties': 'Tutte le difficoltà',
    'sb.easy': 'Facile',
    'sb.medium': 'Media',
    'sb.hard': 'Difficile',
    'sb.reset': 'Reimposta',
    'sb.apply': 'Applica',
    'sb.allCategories': 'Tutte le categorie',
    'sb.revoke.action': 'Revoca',
    'sb.revoke.confirm': 'Revocare questa soluzione approvata? Non sarà più usata dall’IA finché non verrà corretta e approvata di nuovo.',
    'sb.revoke.done': 'Soluzione revocata',
    'sb.revoke.doneDesc': 'Non è più usata dall’IA. Modificala e reinviala per la revisione.',
    'sb.revoke.failed': 'Impossibile revocare',
    'sb.cat.choose': 'Scegli una categoria',
    'sb.cat.noneConfigured': 'Nessuna categoria configurata',
    'sb.cat.noneConfiguredHint': 'Aggiungi categorie in Gestione categorie prima di creare soluzioni.',
    'sb.cat.technicalSupport': 'Supporto tecnico',
    'sb.cat.billing': 'Fatturazione',
    'sb.cat.productIssues': 'Problemi di prodotto',
    'sb.cat.accountManagement': 'Gestione account',
    'sb.totalSolutions': 'Soluzioni totali',
    'sb.avgSuccessRate': 'Tasso di successo medio',
    'sb.totalUsage': 'Utilizzo totale',
    'sb.categories': 'Categorie',
    'sb.topPerforming': 'Soluzioni con le migliori prestazioni',
    'sb.mostUsed': 'Soluzioni più utilizzate',
    'sb.loadingSolutions': 'Caricamento delle soluzioni...',
    'sb.loadingSolutionsSub': 'Attendi mentre recuperiamo il tuo archivio di soluzioni',
    'sb.noSolutionsFound': 'Nessuna soluzione trovata',
    'sb.noSolutionsHint': 'Prova a modificare i criteri di ricerca oppure usa la scheda Gestione per aggiungere soluzioni',
    'sb.mgmtFunctionsHint': 'Le funzioni di gestione delle soluzioni sono disponibili nella scheda Gestione',
    'sb.successRateBadge': '{rate}% di tasso di successo',
    'sb.usesBadge': '{count} utilizzi',
    'sb.usesLower': '{count} utilizzi',
    'sb.updated': 'Aggiornato: {date}',
    'sb.quickTips': 'Suggerimenti rapidi',
    'sb.tip1': 'Sii specifico e chiaro nelle descrizioni delle soluzioni',
    'sb.tip2': 'Includi istruzioni passo dopo passo',
    'sb.tip3': 'Etichetta le soluzioni con parole chiave pertinenti',
    'sb.tip4': 'Fai riferimento a soluzioni correlate per problemi complessi',
    'sb.recentActivity': 'Attività recente',
    'sb.noRecentActivity': 'Nessuna attività recente al momento.',
    'sb.solutionsAddedToday': 'Soluzioni aggiunte oggi:',
    'sb.pendingReviewLabel': 'In attesa di revisione:',
    'sb.successRateLabel': 'Tasso di successo:',
    'sb.bulkOperations': 'Operazioni in blocco',
    'sb.importFromCsv': 'Importa soluzioni da CSV',
    'sb.exportAll': 'Esporta tutte le soluzioni',
    'sb.generateReport': 'Genera report sulle prestazioni',
    'sb.importCsvDesc': 'Carica un file CSV per importare più soluzioni. Il CSV dovrebbe contenere le colonne: title, description, category, tags, resolution, difficulty, success_rate, usage_count.',
    'sb.selectCsv': 'Seleziona un file CSV da caricare',
    'sb.selectedFile': 'Selezionato: {name}',
    'sb.importSolutionsBtn': 'Importa soluzioni',
    'sb.exportAllDesc': 'Scegli il formato di esportazione preferito per tutte le soluzioni',
    'sb.csvFile': 'File CSV',
    'sb.excelFile': 'File Excel',
    'sb.jsonData': 'Dati JSON',
    'sb.pdfReport': 'Report PDF',
    'sb.performanceReport': 'Report sulle prestazioni dell’archivio soluzioni',
    'sb.performanceReportDesc': 'Approfondimenti e analisi delle prestazioni generati dall’AI',
    'sb.aiAnalysisReport': 'Report di analisi AI',
    'sb.downloadReport': 'Scarica report',
    'sb.editSolution': 'Modifica soluzione',
    'sb.editSolutionDesc': 'Aggiorna le informazioni della soluzione',
    'sb.addNewSolutionTitle': 'Aggiungi nuova soluzione',
    'sb.addNewSolutionDesc': 'Crea una nuova soluzione per il tuo archivio soluzioni',
    'sb.solutionTitle': 'Titolo della soluzione *',
    'sb.solutionTitlePlaceholder': 'Inserisci il titolo della soluzione',
    'sb.descriptionLabel': 'Descrizione *',
    'sb.descriptionPlaceholder': 'Descrivi il problema che questa soluzione affronta',
    'sb.category': 'Categoria',
    'sb.tagsLabel': 'Etichette (separate da virgola)',
    'sb.tagsPlaceholder': 'password, reimpostazione, accesso',
    'sb.resolutionSteps': 'Passaggi di risoluzione',
    'sb.resolutionPlaceholder': 'Inserisci le istruzioni di risoluzione passo dopo passo',
    'sb.updateSolution': 'Aggiorna soluzione',
    'sb.addSolutionBtn': 'Aggiungi soluzione',
    'sb.deleteSolution': 'Elimina soluzione',
    'sb.deleteSolutionDesc': 'Sei sicuro di voler eliminare questa soluzione? Questa azione non può essere annullata.',
    'sb.toast.loadFailed': 'Impossibile caricare le soluzioni',
    'sb.toast.missingInfo': 'Informazioni mancanti',
    'sb.toast.missingInfoDesc': 'Compila il titolo e la descrizione',
    'sb.toast.submitted': 'Soluzione inviata',
    'sb.toast.submittedDesc': 'La soluzione è stata inoltrata al Responsabile per l’approvazione',
    'sb.toast.addFailed': 'Impossibile aggiungere la soluzione',
    'sb.toast.exportStarted': 'Esportazione avviata',
    'sb.toast.exportStartedDesc': 'Esportazione delle soluzioni come {format}',
    'sb.toast.exportComplete': 'Esportazione completata',
    'sb.toast.exportCompleteDesc': 'Soluzioni esportate correttamente come {format}',
    'sb.toast.exportFailed': 'Esportazione non riuscita',
    'sb.toast.exportFailedDesc': 'Impossibile esportare le soluzioni',
    'sb.toast.noFile': 'Nessun file selezionato',
    'sb.toast.noFileDesc': 'Seleziona un file CSV da importare',
    'sb.toast.importStarted': 'Importazione avviata',
    'sb.toast.importStartedDesc': 'Elaborazione del file CSV...',
    'sb.toast.importComplete': 'Importazione completata',
    'sb.toast.importCompleteDesc': 'Soluzioni importate correttamente',
    'sb.toast.importFailed': 'Importazione non riuscita',
    'sb.toast.importFailedDesc': 'Impossibile importare le soluzioni dal CSV',
    'sb.toast.invalidFile': 'File non valido',
    'sb.toast.invalidFileDesc': 'Seleziona un file CSV valido',
    'sb.toast.reportGenerated': 'Report generato',
    'sb.toast.reportGeneratedDesc': 'Il report sulle prestazioni è stato generato correttamente',
    'sb.toast.reportFailed': 'Generazione del report non riuscita',
    'sb.toast.reportFailedDesc': 'Impossibile generare il report sulle prestazioni',
    'sb.toast.updated': 'Soluzione aggiornata',
    'sb.toast.updatedDesc': 'La soluzione è stata aggiornata correttamente',
    'sb.toast.updateFailed': 'Impossibile aggiornare la soluzione',
    'sb.toast.deleted': 'Soluzione eliminata',
    'sb.toast.deletedDesc': 'La soluzione è stata eliminata correttamente',
    'sb.toast.deleteFailed': 'Impossibile eliminare la soluzione',
    'sb.kb.title': 'Generatore di conoscenza AI',
    'sb.kb.subtitle': 'Inserisci le informazioni sulla tua attività → genera 50 bozze di FAQ → rivedi e approva. Le bozze restano interne fino all’approvazione.',
    'sb.kb.newSession': 'Nuova sessione',
    'sb.kb.buildTitle': 'Costruisci la tua base di conoscenza AI',
    'sb.kb.buildBlurb': 'Rispondi in base a ciò che sai della tua attività. Genereremo 50 bozze/FAQ suggerite. Nulla viene reso disponibile ai clienti finché un responsabile o un amministratore non lo approva.',
    'sb.kb.start': 'Avvia il Generatore di conoscenza',
    'sb.kb.tab.intake': 'Raccolta dati',
    'sb.kb.tab.validate': 'Convalida',
    'sb.kb.tab.review': 'Genera e rivedi',
    'sb.kb.tab.coverage': 'Copertura',
    'sb.kb.tab.gaps': 'Lacune',
    'sb.kb.skipHint': 'Salta tutto ciò che non conosci — i campi sconosciuti non vengono mai inventati.',
    'sb.kb.saveProgress': 'Salva i progressi',
    'sb.kb.blankUnknown': 'Lascia vuoto se sconosciuto',
    'sb.kb.blankUnknownTextarea': 'Lascia vuoto se sconosciuto — non verrà inventato',
    'sb.kb.unknownSkip': 'Sconosciuto / salta',
    'sb.kb.sec.identity': 'Identità dell’attività',
    'sb.kb.sec.profile': 'Profilo dell’attività',
    'sb.kb.sec.operations': 'Operatività',
    'sb.kb.sec.payments': 'Pagamenti',
    'sb.kb.sec.delivery': 'Spedizione / Consegna',
    'sb.kb.sec.booking': 'Prenotazione',
    'sb.kb.sec.returns': 'Resi / Rimborsi',
    'sb.kb.sec.support': 'Assistenza',
    'sb.kb.sec.policies': 'Politiche',
    'sb.kb.sec.extras': 'Domande e note dei clienti',
    'sb.kb.f.business_name': 'Nome dell’attività',
    'sb.kb.f.address': 'Indirizzo',
    'sb.kb.f.city': 'Città',
    'sb.kb.f.province': 'Provincia / Regione',
    'sb.kb.f.country': 'Paese',
    'sb.kb.f.phone': 'Telefono',
    'sb.kb.f.email': 'Email',
    'sb.kb.f.website': 'Sito web',
    'sb.kb.f.social_media': 'Social media',
    'sb.kb.f.business_description': 'Descrizione dell’attività',
    'sb.kb.f.products': 'Prodotti venduti',
    'sb.kb.f.services': 'Servizi offerti',
    'sb.kb.f.target_customers': 'Clienti target',
    'sb.kb.f.industries_served': 'Settori serviti',
    'sb.kb.f.differentiator': 'Elemento distintivo',
    'sb.kb.f.mission_vision': 'Missione / visione',
    'sb.kb.f.opening_days': 'Giorni di apertura',
    'sb.kb.f.operating_hours': 'Orari di apertura',
    'sb.kb.f.holiday_hours': 'Orari festivi',
    'sb.kb.f.appointment_required': 'Appuntamento obbligatorio',
    'sb.kb.f.walk_ins_accepted': 'Accesso senza appuntamento',
    'sb.kb.f.delivery_available': 'Consegna disponibile',
    'sb.kb.f.pickup_available': 'Ritiro disponibile',
    'sb.kb.f.online_orders': 'Ordini online',
    'sb.kb.f.service_area': 'Area di servizio',
    'sb.kb.f.payment_methods': 'Metodi di pagamento accettati',
    'sb.kb.f.delivery_locations': 'Località di consegna',
    'sb.kb.f.delivery_fees': 'Costi di consegna',
    'sb.kb.f.delivery_timelines': 'Tempi di consegna',
    'sb.kb.f.same_day_delivery': 'Consegna in giornata',
    'sb.kb.f.international_shipping': 'Spedizione internazionale',
    'sb.kb.f.booking_method': 'Come vengono prenotati gli appuntamenti',
    'sb.kb.f.cancellation_policy': 'Politica di cancellazione',
    'sb.kb.f.rescheduling_policy': 'Politica di riprogrammazione',
    'sb.kb.f.late_arrival_policy': 'Politica per i ritardi',
    'sb.kb.f.ordering_method': 'Come vengono effettuati gli ordini online',
    'sb.kb.f.return_policy': 'Politica di reso',
    'sb.kb.f.refund_policy': 'Politica di rimborso',
    'sb.kb.f.exchange_policy': 'Politica di cambio',
    'sb.kb.f.contact_methods': 'Metodi di contatto principali',
    'sb.kb.f.support_phone': 'Telefono di assistenza',
    'sb.kb.f.support_whatsapp': 'WhatsApp',
    'sb.kb.f.support_email': 'Email di assistenza',
    'sb.kb.f.support_website': 'Sito web di assistenza',
    'sb.kb.f.support_in_person': 'Assistenza di persona',
    'sb.kb.f.privacy_policy': 'Informativa sulla privacy',
    'sb.kb.f.warranty_policy': 'Politica di garanzia',
    'sb.kb.f.terms_of_service': 'Termini di servizio',
    'sb.kb.f.service_guarantees': 'Garanzie di servizio',
    'sb.kb.f.policies': 'Riepilogo delle politiche generali',
    'sb.kb.f.common_questions': 'Domande frequenti dei clienti',
    'sb.kb.f.special_instructions': 'Ciò che i clienti dovrebbero sempre sapere',
    'sb.kb.pay.cash': 'Contanti',
    'sb.kb.pay.debit': 'Carta di debito',
    'sb.kb.pay.credit': 'Carta di credito',
    'sb.kb.pay.bankTransfer': 'Bonifico bancario',
    'sb.kb.pay.applePay': 'Apple Pay',
    'sb.kb.pay.googlePay': 'Google Pay',
    'sb.kb.pay.payPal': 'PayPal',
    'sb.kb.pay.mobileMoney': 'Mobile Money',
    'sb.kb.pay.interac': 'Interac e-Transfer',
    'sb.kb.pay.other': 'Altro',
    'sb.kb.revalidate': 'Riconvalida',
    'sb.kb.runValidationHint': 'Esegui la convalida dalla scheda Raccolta dati per vedere il livello di prontezza.',
    'sb.kb.readiness': 'Prontezza',
    'sb.kb.readinessHint': 'La generazione non viene mai bloccata. I fatti specifici dell’attività mancanti diventano una FAQ suggerita oppure la risposta "Conferma questa politica con l’attività." — mai inventati.',
    'sb.kb.val.missingFields': 'Campi mancanti',
    'sb.kb.val.missingFieldsEmpty': 'Non manca nulla di obbligatorio.',
    'sb.kb.val.contradictions': 'Contraddizioni',
    'sb.kb.val.contradictionsEmpty': 'Nessuna contraddizione rilevata.',
    'sb.kb.val.vague': 'Risposte vaghe',
    'sb.kb.val.vagueEmpty': 'Nessuna risposta vaga.',
    'sb.kb.val.followups': 'Domande di approfondimento',
    'sb.kb.val.followupsEmpty': 'Nessun approfondimento.',
    'sb.kb.completeMore': '← Completa altri campi',
    'sb.kb.continueGenerate': 'Continua verso la generazione →',
    'sb.kb.generate50': 'Genera 50 bozze di FAQ',
    'sb.kb.generate50Sub': 'Esegue la convalida + la generazione AI tramite il backend reale. Le bozze restano solo interne fino all’approvazione.',
    'sb.kb.generating': 'Generazione in corso…',
    'sb.kb.internalVsLive': 'Bozza e suggerite = solo interne. Approvata = attiva per AI Chat/Voce.',
    'sb.kb.showArchived': 'Mostra archiviate',
    'sb.kb.noGeneratedYet': 'Nessuna FAQ generata ancora. Fai clic su Genera 50 bozze di FAQ qui sopra.',
    'sb.kb.status.draft': 'Bozza · solo interna',
    'sb.kb.status.suggested': 'Suggerita · solo interna',
    'sb.kb.status.pending': 'In attesa di approvazione · interna',
    'sb.kb.status.approved': 'Approvata · attiva per l’AI',
    'sb.kb.status.archived': 'Archiviata',
    'sb.kb.status.rejected': 'Rifiutata',
    'sb.kb.ownerSupplied': 'Fornita dal titolare',
    'sb.kb.aiSuggested': 'Suggerita dall’AI',
    'sb.kb.totalGenerated': 'Totale generate',
    'sb.kb.customerFacing': 'Rivolte al cliente (approvate)',
    'sb.kb.internalDraft': 'Interne (bozza/suggerite)',
    'sb.kb.categoryCoverage': 'Copertura per categoria',
    'sb.kb.missing': 'mancante',
    'sb.kb.missingCategories': 'Categorie mancanti: {cats}. Genera (o aggiungi altri dati di raccolta) per coprirle.',
    'sb.kb.gapsIntro': 'Domande dei clienti senza risposta registrate per questa attività.',
    'sb.kb.noGaps': 'Nessuna lacuna di conoscenza registrata.',
    'sb.kb.createDraftFaq': 'Crea bozza di FAQ',
    'sb.kb.gapsLoadError': 'Impossibile caricare le lacune di conoscenza: {error}',
    'sb.kb.seenTimes': 'Vista {count}× · {status}',
    'sb.kb.editDraftFaq': 'Modifica bozza di FAQ',
    'sb.kb.createDraftFromGap': 'Crea bozza di FAQ dalla lacuna',
    'sb.kb.question': 'Domanda',
    'sb.kb.answer': 'Risposta',
    'sb.kb.keywords': 'Parole chiave (separate da virgola)',
    'sb.kb.answerPlaceholder': 'Scrivi la risposta. Ometti i dettagli specifici dell’attività se sconosciuti.',
    'sb.kb.editInternalHint': 'La modifica mantiene questo elemento interno. Approva separatamente per renderlo attivo.',
    'sb.kb.createdDraftHint': 'Creata come bozza (interna). Approvala per renderla attiva.',
    'sb.kb.saveDraft': 'Salva bozza',
    'sb.kb.createDraft': 'Crea bozza',
    'sb.kb.noBusinessContext': 'Nessun contesto attività attivo',
    'sb.kb.selectBusiness': 'Seleziona o collega un’attività per usare il Generatore di conoscenza.',
    'sb.kb.toast.noBusiness': 'Nessuna attività selezionata',
    'sb.kb.toast.noBusinessDesc': 'Impossibile avviare — nessun contesto attività attivo.',
    'sb.kb.toast.started': 'Generatore di conoscenza avviato',
    'sb.kb.toast.startedDesc': 'Inserisci ciò che sai — puoi saltare tutto ciò che non conosci.',
    'sb.kb.toast.couldNotStart': 'Impossibile avviare la sessione',
    'sb.kb.toast.progressSaved': 'Progressi salvati',
    'sb.kb.toast.savedFields': '{count} campo/i salvato/i.',
    'sb.kb.toast.nothingToSave': 'Niente di nuovo da salvare.',
    'sb.kb.toast.saveFailed': 'Salvataggio non riuscito',
    'sb.kb.toast.validationFailed': 'Convalida non riuscita',
    'sb.kb.toast.draftsGenerated': 'Bozze di FAQ generate',
    'sb.kb.toast.draftsGeneratedDesc': '{count} elementi bozza/suggeriti creati (solo interni).',
    'sb.kb.toast.generationFailed': 'Generazione non riuscita',
    'sb.kb.toast.providerUnavailable': 'Il provider di generazione AI non è al momento disponibile. Nessuna bozza è stata creata. Riprova più tardi.',
    'sb.kb.toast.loadGenFailed': 'Impossibile caricare le FAQ generate',
    'sb.kb.toast.approved': 'Approvata',
    'sb.kb.toast.approvedDesc': 'Questa FAQ è ora attiva immediatamente per AI Chat e Voce.',
    'sb.kb.toast.approvalFailed': 'Approvazione non riuscita',
    'sb.kb.toast.rejected': 'Rifiutata',
    'sb.kb.toast.rejectedDesc': 'Questa bozza non verrà utilizzata dall’AI.',
    'sb.kb.toast.rejectFailed': 'Rifiuto non riuscito',
    'sb.kb.toast.archived': 'Archiviata',
    'sb.kb.toast.archivedDesc': 'Elemento ritirato (escluso dal recupero dell’AI).',
    'sb.kb.toast.archiveFailed': 'Archiviazione non riuscita',
    'sb.kb.toast.draftUpdated': 'Bozza aggiornata',
    'sb.kb.toast.draftUpdatedDesc': 'La tua modifica è stata salvata. Resta interna fino all’approvazione.',
    'sb.kb.toast.updateFailed': 'Aggiornamento non riuscito',
    'sb.kb.toast.draftCreated': 'Bozza di FAQ creata',
    'sb.kb.toast.draftCreatedDesc': 'Creata come bozza (interna). Approvala per renderla attiva.',
    'sb.kb.toast.couldNotCreate': 'Impossibile creare la bozza',
    'sb.live.approvalsImmediate': 'Le approvazioni hanno effetto immediato — nessun ritardo di riaddestramento',
    'sb.live.approvalsBlurb': 'Le soluzioni approvate sono immediatamente disponibili per AI Chat e AI Voce. Non è richiesto alcun ritardo di riaddestramento perché Servisfy legge in tempo reale la conoscenza approvata dell’archivio soluzioni a ogni interazione AI.',
    'sb.live.aiChatLive': 'AI Chat: attiva',
    'sb.live.aiVoiceLive': 'AI Voce: attiva',
    'sb.live.business': 'Attività: {name}',
    'sb.live.liveInAi': 'Attiva nell’AI (approvate)',
    'sb.live.usedByAiNow': 'Utilizzate ora da AI Chat e Voce',
    'sb.live.pendingApproval': 'In attesa di approvazione',
    'sb.live.notYetUsed': 'Non ancora utilizzate dall’AI',
    'sb.live.draftsSuggested': 'Bozze / suggerite',
    'sb.live.internalOnly': 'Solo interne (Generatore di conoscenza)',
    'sb.live.inactive': 'Inattive',
    'sb.live.rejectedRevoked': 'Rifiutate / revocate / archiviate',
    'sb.live.totalUsage': 'Utilizzo totale dell’AI (approvate)',
    'sb.live.realUsageSum': 'Somma reale di usage_count',
    'sb.live.avgSuccess': 'Tasso di successo medio',
    'sb.live.noUsageYet': 'Nessun utilizzo registrato ancora',
    'sb.live.acrossUsed': 'Tra le soluzioni approvate utilizzate',
    'sb.live.lastApproved': 'Ultima approvata',
    'sb.live.effectiveImmediately': 'Effettiva immediatamente',
    'sb.live.noApprovalsYet': 'Nessuna approvazione ancora',
    'sb.live.topUsed': 'Soluzioni approvate più utilizzate',
    'sb.live.topUsedEmpty': 'Nessuna soluzione approvata è stata ancora utilizzata dall’AI.',
    'sb.live.recentlyApproved': 'Approvate di recente',
    'sb.live.recentlyApprovedEmpty': 'Nessuna soluzione approvata ancora. Approva dalla Revisione per attivarla.',
    'sb.live.lowSuccess': 'Approvate ma con basso successo — potrebbero necessitare di revisione',
    'sb.live.lowSuccessEmpty': 'Nessuna soluzione approvata con basso successo. 👍',
    'sb.live.lowSuccessHint': 'Soluzioni approvate utilizzate dall’AI con tasso di successo inferiore al {threshold}%.',
    'sb.live.loading': 'Caricamento della conoscenza AI in tempo reale…',
    'sb.live.loadError': 'Impossibile caricare la conoscenza AI',
    'sb.live.tryAgain': 'Riprova',
    'sb.live.loadFailedToast': 'Impossibile caricare la conoscenza AI',
    'sb.live.untitled': 'Senza titolo',
    'sb.live.uncategorized': 'Senza categoria',
    'sb.review.title': 'Revisione dell’archivio soluzioni',
    'sb.review.subtitle': 'Rivedi e approva le soluzioni suggerite dall’AI per l’archivio soluzioni',
    'sb.review.pendingReview': 'In attesa di revisione',
    'sb.review.approvedTab': 'Approvate',
    'sb.review.rejectedTab': 'Rifiutate',
    'sb.review.noPending': 'Nessuna soluzione in attesa di revisione',
    'sb.review.noPendingSub': 'Le soluzioni suggerite dall’AI appariranno qui per l’approvazione',
    'sb.review.noApproved': 'Nessuna soluzione approvata',
    'sb.review.noRejected': 'Nessuna soluzione rifiutata',
    'sb.review.description': 'Descrizione:',
    'sb.review.resolutionSteps': 'Passaggi di risoluzione:',
    'sb.review.keywords': 'Parole chiave:',
    'sb.review.submittedBy': 'Inviata da {name}',
    'sb.review.staff': 'personale',
    'sb.review.aiVoice': 'AI Voce',
    'sb.review.aiChat': 'AI Chat',
    'sb.review.ch.humanChat': 'Chat umana',
    'sb.review.ch.voiceCall': 'Chiamata vocale',
    'sb.review.ch.humanCall': 'Chiamata con operatore',
    'sb.review.ch.solutionBank': 'Banca delle soluzioni',
    'sb.review.ch.unknown': 'Sconosciuto',
    'sb.review.aiGenerated': 'Generata dall’AI',
    'sb.review.openaiFallback': 'Fallback OpenAI',
    'sb.review.sourceConversation': 'Origine: Conversazione {id}',
    'sb.review.confHigh': 'Alta ({pct}%)',
    'sb.review.confMedium': 'Media ({pct}%)',
    'sb.review.confLow': 'Bassa ({pct}%)',
    'sb.review.activeInAi': 'Attiva nell’AI',
    'sb.review.approvedOn': 'Approvata il {date}',
    'sb.review.rejectedOn': 'Rifiutata il {date}',
    'sb.review.revoke': 'Revoca',
    'sb.review.resubmit': 'Reinvia per la revisione',
    'sb.review.unknownDate': 'Sconosciuta',
    'sb.review.loadFailedDesc': 'Controlla la connessione e riprova.',
    'sb.review.toast.approved': 'Soluzione approvata',
    'sb.review.toast.approvedDesc': 'La soluzione è ora attiva nella base di conoscenza AI.',
    'sb.review.toast.approvalFailed': 'Approvazione non riuscita',
    'sb.review.toast.rejected': 'Soluzione rifiutata',
    'sb.review.toast.rejectedDesc': 'La soluzione non verrà utilizzata dall’AI.',
    'sb.review.toast.rejectionFailed': 'Rifiuto non riuscito',
    'sb.review.toast.revoked': 'Soluzione revocata',
    'sb.review.toast.revokedDesc': 'L’AI non utilizzerà più questa soluzione.',
    'sb.review.toast.revokeFailed': 'Revoca non riuscita',
    'sb.review.toast.resubmitted': 'Soluzione reinviata',
    'sb.review.toast.resubmittedDesc': 'La soluzione è di nuovo in attesa di revisione.',
    'sb.review.toast.resubmitFailed': 'Reinvio non riuscito',
    'sb.review.toast.deleted': 'Soluzione eliminata',
    'sb.review.toast.deletedDesc': 'Soluzione rimossa definitivamente.',
    'sb.review.toast.deleteFailed': 'Eliminazione non riuscita',
  },
  ja: {
    'sb.tab.solutions': 'ソリューション',
    'sb.tab.knowledgeBuilder': 'AIナレッジビルダー',
    'sb.tab.review': 'レビュー',
    'sb.tab.analytics': '分析',
    'sb.tab.liveAi': 'ライブAIナレッジ',
    'sb.tab.addSolution': 'ソリューションを追加',
    'sb.tab.manageSolutions': 'ソリューションを管理',
    'sb.exporting': 'エクスポート中...',
    'sb.importing': 'インポート中...',
    'sb.searchPlaceholder': 'ソリューションを検索...',
    'sb.filterSolutions': 'ソリューションを絞り込む',
    'sb.filterSolutionsDesc': '条件でソリューションを絞り込みます',
    'sb.difficulty': '難易度',
    'sb.allDifficulties': 'すべての難易度',
    'sb.easy': '易しい',
    'sb.medium': '普通',
    'sb.hard': '難しい',
    'sb.reset': 'リセット',
    'sb.apply': '適用',
    'sb.allCategories': 'すべてのカテゴリ',
    'sb.revoke.action': '取り消す',
    'sb.revoke.confirm': 'この承認済みソリューションを取り消しますか？修正して再承認されるまでAIは使用しません。',
    'sb.revoke.done': 'ソリューションを取り消しました',
    'sb.revoke.doneDesc': 'AIはこれを使用しません。編集してレビューに再提出してください。',
    'sb.revoke.failed': '取り消せませんでした',
    'sb.cat.choose': 'カテゴリを選択してください',
    'sb.cat.noneConfigured': 'カテゴリが設定されていません',
    'sb.cat.noneConfiguredHint': 'ソリューションを作成する前に、カテゴリ管理でカテゴリを追加してください。',
    'sb.cat.technicalSupport': 'テクニカルサポート',
    'sb.cat.billing': '請求',
    'sb.cat.productIssues': '製品の問題',
    'sb.cat.accountManagement': 'アカウント管理',
    'sb.totalSolutions': 'ソリューション総数',
    'sb.avgSuccessRate': '平均成功率',
    'sb.totalUsage': '総利用回数',
    'sb.categories': 'カテゴリ',
    'sb.topPerforming': '成績上位のソリューション',
    'sb.mostUsed': '最も利用されているソリューション',
    'sb.loadingSolutions': 'ソリューションを読み込んでいます...',
    'sb.loadingSolutionsSub': 'ソリューションバンクを取得しています。しばらくお待ちください',
    'sb.noSolutionsFound': 'ソリューションが見つかりません',
    'sb.noSolutionsHint': '検索条件を調整するか、管理タブを使用してソリューションを追加してください',
    'sb.mgmtFunctionsHint': 'ソリューション管理機能は管理タブでご利用いただけます',
    'sb.successRateBadge': '成功率 {rate}%',
    'sb.usesBadge': '{count} 回利用',
    'sb.usesLower': '{count} 回利用',
    'sb.updated': '更新日: {date}',
    'sb.quickTips': 'クイックヒント',
    'sb.tip1': 'ソリューションの説明は具体的かつ明確に記載してください',
    'sb.tip2': '手順を段階的に記載してください',
    'sb.tip3': '関連するキーワードでソリューションにタグを付けてください',
    'sb.tip4': '複雑な問題については関連するソリューションを参照してください',
    'sb.recentActivity': '最近のアクティビティ',
    'sb.noRecentActivity': '最近のアクティビティはまだありません。',
    'sb.solutionsAddedToday': '本日追加されたソリューション:',
    'sb.pendingReviewLabel': 'レビュー待ち:',
    'sb.successRateLabel': '成功率:',
    'sb.bulkOperations': '一括操作',
    'sb.importFromCsv': 'CSVからソリューションをインポート',
    'sb.exportAll': 'すべてのソリューションをエクスポート',
    'sb.generateReport': 'パフォーマンスレポートを生成',
    'sb.importCsvDesc': '複数のソリューションをインポートするにはCSVファイルをアップロードしてください。CSVには次の列が必要です: title, description, category, tags, resolution, difficulty, success_rate, usage_count。',
    'sb.selectCsv': 'アップロードするCSVファイルを選択してください',
    'sb.selectedFile': '選択済み: {name}',
    'sb.importSolutionsBtn': 'ソリューションをインポート',
    'sb.exportAllDesc': 'すべてのソリューションについて、ご希望のエクスポート形式を選択してください',
    'sb.csvFile': 'CSVファイル',
    'sb.excelFile': 'Excelファイル',
    'sb.jsonData': 'JSONデータ',
    'sb.pdfReport': 'PDFレポート',
    'sb.performanceReport': 'ソリューションバンク パフォーマンスレポート',
    'sb.performanceReportDesc': 'AIが生成したインサイトとパフォーマンス分析',
    'sb.aiAnalysisReport': 'AI分析レポート',
    'sb.downloadReport': 'レポートをダウンロード',
    'sb.editSolution': 'ソリューションを編集',
    'sb.editSolutionDesc': 'ソリューション情報を更新します',
    'sb.addNewSolutionTitle': '新しいソリューションを追加',
    'sb.addNewSolutionDesc': 'ソリューションバンクに新しいソリューションを作成します',
    'sb.solutionTitle': 'ソリューションのタイトル *',
    'sb.solutionTitlePlaceholder': 'ソリューションのタイトルを入力してください',
    'sb.descriptionLabel': '説明 *',
    'sb.descriptionPlaceholder': 'このソリューションが対応する問題を説明してください',
    'sb.category': 'カテゴリ',
    'sb.tagsLabel': 'タグ（カンマ区切り）',
    'sb.tagsPlaceholder': 'password, reset, login',
    'sb.resolutionSteps': '解決手順',
    'sb.resolutionPlaceholder': '段階的な解決手順を入力してください',
    'sb.updateSolution': 'ソリューションを更新',
    'sb.addSolutionBtn': 'ソリューションを追加',
    'sb.deleteSolution': 'ソリューションを削除',
    'sb.deleteSolutionDesc': 'このソリューションを削除してもよろしいですか？この操作は元に戻せません。',
    'sb.toast.loadFailed': 'ソリューションの読み込みに失敗しました',
    'sb.toast.missingInfo': '情報が不足しています',
    'sb.toast.missingInfoDesc': 'タイトルと説明を入力してください',
    'sb.toast.submitted': 'ソリューションを送信しました',
    'sb.toast.submittedDesc': 'ソリューションは承認のためマネージャーに転送されました',
    'sb.toast.addFailed': 'ソリューションの追加に失敗しました',
    'sb.toast.exportStarted': 'エクスポートを開始しました',
    'sb.toast.exportStartedDesc': 'ソリューションを {format} 形式でエクスポートしています',
    'sb.toast.exportComplete': 'エクスポートが完了しました',
    'sb.toast.exportCompleteDesc': 'ソリューションを {format} 形式で正常にエクスポートしました',
    'sb.toast.exportFailed': 'エクスポートに失敗しました',
    'sb.toast.exportFailedDesc': 'ソリューションのエクスポートに失敗しました',
    'sb.toast.noFile': 'ファイルが選択されていません',
    'sb.toast.noFileDesc': 'インポートするCSVファイルを選択してください',
    'sb.toast.importStarted': 'インポートを開始しました',
    'sb.toast.importStartedDesc': 'CSVファイルを処理しています...',
    'sb.toast.importComplete': 'インポートが完了しました',
    'sb.toast.importCompleteDesc': 'ソリューションを正常にインポートしました',
    'sb.toast.importFailed': 'インポートに失敗しました',
    'sb.toast.importFailedDesc': 'CSVからのソリューションのインポートに失敗しました',
    'sb.toast.invalidFile': '無効なファイル',
    'sb.toast.invalidFileDesc': '有効なCSVファイルを選択してください',
    'sb.toast.reportGenerated': 'レポートを生成しました',
    'sb.toast.reportGeneratedDesc': 'パフォーマンスレポートを正常に生成しました',
    'sb.toast.reportFailed': 'レポートの生成に失敗しました',
    'sb.toast.reportFailedDesc': 'パフォーマンスレポートの生成に失敗しました',
    'sb.toast.updated': 'ソリューションを更新しました',
    'sb.toast.updatedDesc': 'ソリューションを正常に更新しました',
    'sb.toast.updateFailed': 'ソリューションの更新に失敗しました',
    'sb.toast.deleted': 'ソリューションを削除しました',
    'sb.toast.deletedDesc': 'ソリューションを正常に削除しました',
    'sb.toast.deleteFailed': 'ソリューションの削除に失敗しました',
    'sb.kb.title': 'AIナレッジビルダー',
    'sb.kb.subtitle': 'ビジネス情報を入力 → 50件のドラフトFAQを生成 → レビューして承認します。ドラフトは承認されるまで内部限定です。',
    'sb.kb.newSession': '新規セッション',
    'sb.kb.buildTitle': 'AIナレッジベースを構築',
    'sb.kb.buildBlurb': 'ご自身のビジネスについて分かる範囲でお答えください。50件のドラフト／推奨FAQを生成します。マネージャーまたは管理者が承認するまで、顧客に公開されることはありません。',
    'sb.kb.start': 'ナレッジビルダーを開始',
    'sb.kb.tab.intake': '情報入力',
    'sb.kb.tab.validate': '検証',
    'sb.kb.tab.review': '生成とレビュー',
    'sb.kb.tab.coverage': 'カバレッジ',
    'sb.kb.tab.gaps': 'ギャップ',
    'sb.kb.skipHint': '不明な項目はスキップして構いません。不明な項目が創作されることはありません。',
    'sb.kb.saveProgress': '進捗を保存',
    'sb.kb.blankUnknown': '不明な場合は空欄のままにしてください',
    'sb.kb.blankUnknownTextarea': '不明な場合は空欄のままにしてください。創作されることはありません',
    'sb.kb.unknownSkip': '不明／スキップ',
    'sb.kb.sec.identity': 'ビジネスの識別情報',
    'sb.kb.sec.profile': 'ビジネスプロフィール',
    'sb.kb.sec.operations': '営業・運営',
    'sb.kb.sec.payments': '支払い',
    'sb.kb.sec.delivery': '配送・配達',
    'sb.kb.sec.booking': '予約',
    'sb.kb.sec.returns': '返品・返金',
    'sb.kb.sec.support': 'サポート',
    'sb.kb.sec.policies': 'ポリシー',
    'sb.kb.sec.extras': '顧客からの質問とメモ',
    'sb.kb.f.business_name': 'ビジネス名',
    'sb.kb.f.address': '住所',
    'sb.kb.f.city': '市区町村',
    'sb.kb.f.province': '都道府県／州',
    'sb.kb.f.country': '国',
    'sb.kb.f.phone': '電話番号',
    'sb.kb.f.email': 'メールアドレス',
    'sb.kb.f.website': 'ウェブサイト',
    'sb.kb.f.social_media': 'ソーシャルメディア',
    'sb.kb.f.business_description': 'ビジネスの説明',
    'sb.kb.f.products': '販売している製品',
    'sb.kb.f.services': '提供しているサービス',
    'sb.kb.f.target_customers': '対象顧客',
    'sb.kb.f.industries_served': '対応している業界',
    'sb.kb.f.differentiator': '差別化要因',
    'sb.kb.f.mission_vision': 'ミッション／ビジョン',
    'sb.kb.f.opening_days': '営業日',
    'sb.kb.f.operating_hours': '営業時間',
    'sb.kb.f.holiday_hours': '祝日の営業時間',
    'sb.kb.f.appointment_required': '予約が必要',
    'sb.kb.f.walk_ins_accepted': '予約なしの来店可',
    'sb.kb.f.delivery_available': '配達対応可',
    'sb.kb.f.pickup_available': '店頭受け取り可',
    'sb.kb.f.online_orders': 'オンライン注文',
    'sb.kb.f.service_area': 'サービス提供エリア',
    'sb.kb.f.payment_methods': '利用可能な支払い方法',
    'sb.kb.f.delivery_locations': '配達可能地域',
    'sb.kb.f.delivery_fees': '配達料金',
    'sb.kb.f.delivery_timelines': '配達所要日数',
    'sb.kb.f.same_day_delivery': '当日配達',
    'sb.kb.f.international_shipping': '海外発送',
    'sb.kb.f.booking_method': '予約の受付方法',
    'sb.kb.f.cancellation_policy': 'キャンセルポリシー',
    'sb.kb.f.rescheduling_policy': '予約変更ポリシー',
    'sb.kb.f.late_arrival_policy': '遅刻時のポリシー',
    'sb.kb.f.ordering_method': 'オンライン注文の方法',
    'sb.kb.f.return_policy': '返品ポリシー',
    'sb.kb.f.refund_policy': '返金ポリシー',
    'sb.kb.f.exchange_policy': '交換ポリシー',
    'sb.kb.f.contact_methods': '主な連絡方法',
    'sb.kb.f.support_phone': 'サポート電話番号',
    'sb.kb.f.support_whatsapp': 'WhatsApp',
    'sb.kb.f.support_email': 'サポートメールアドレス',
    'sb.kb.f.support_website': 'サポートウェブサイト',
    'sb.kb.f.support_in_person': '対面サポート',
    'sb.kb.f.privacy_policy': 'プライバシーポリシー',
    'sb.kb.f.warranty_policy': '保証ポリシー',
    'sb.kb.f.terms_of_service': '利用規約',
    'sb.kb.f.service_guarantees': 'サービス保証',
    'sb.kb.f.policies': '一般ポリシーの概要',
    'sb.kb.f.common_questions': '顧客からよく寄せられる質問',
    'sb.kb.f.special_instructions': '顧客が常に知っておくべき事項',
    'sb.kb.pay.cash': '現金',
    'sb.kb.pay.debit': 'デビットカード',
    'sb.kb.pay.credit': 'クレジットカード',
    'sb.kb.pay.bankTransfer': '銀行振込',
    'sb.kb.pay.applePay': 'Apple Pay',
    'sb.kb.pay.googlePay': 'Google Pay',
    'sb.kb.pay.payPal': 'PayPal',
    'sb.kb.pay.mobileMoney': 'モバイルマネー',
    'sb.kb.pay.interac': 'Interac e-Transfer',
    'sb.kb.pay.other': 'その他',
    'sb.kb.revalidate': '再検証',
    'sb.kb.runValidationHint': '準備状況を確認するには、情報入力タブから検証を実行してください。',
    'sb.kb.readiness': '準備状況',
    'sb.kb.readinessHint': '生成がブロックされることはありません。ビジネス固有の情報が不足している場合は、推奨FAQになるか、「このポリシーについてはビジネスにご確認ください。」という回答になります。創作されることはありません。',
    'sb.kb.val.missingFields': '不足している項目',
    'sb.kb.val.missingFieldsEmpty': '必須項目の不足はありません。',
    'sb.kb.val.contradictions': '矛盾点',
    'sb.kb.val.contradictionsEmpty': '矛盾は見つかりませんでした。',
    'sb.kb.val.vague': '曖昧な回答',
    'sb.kb.val.vagueEmpty': '曖昧な回答はありません。',
    'sb.kb.val.followups': 'フォローアップの質問',
    'sb.kb.val.followupsEmpty': 'フォローアップはありません。',
    'sb.kb.completeMore': '← さらに項目を入力する',
    'sb.kb.continueGenerate': '生成に進む →',
    'sb.kb.generate50': '50件のドラフトFAQを生成',
    'sb.kb.generate50Sub': '実際のバックエンドを通じて検証とAI生成を実行します。ドラフトは承認されるまで内部限定です。',
    'sb.kb.generating': '生成中…',
    'sb.kb.internalVsLive': 'ドラフトと推奨は内部限定です。承認済みはAIチャット／ボイスで公開されます。',
    'sb.kb.showArchived': 'アーカイブ済みを表示',
    'sb.kb.noGeneratedYet': '生成されたFAQはまだありません。上の「50件のドラフトFAQを生成」をクリックしてください。',
    'sb.kb.status.draft': 'ドラフト・内部限定',
    'sb.kb.status.suggested': '推奨・内部限定',
    'sb.kb.status.pending': '承認待ち・内部',
    'sb.kb.status.approved': '承認済み・AIで公開中',
    'sb.kb.status.archived': 'アーカイブ済み',
    'sb.kb.status.rejected': '却下済み',
    'sb.kb.ownerSupplied': 'オーナー提供',
    'sb.kb.aiSuggested': 'AI推奨',
    'sb.kb.totalGenerated': '生成された総数',
    'sb.kb.customerFacing': '顧客向け（承認済み）',
    'sb.kb.internalDraft': '内部（ドラフト／推奨）',
    'sb.kb.categoryCoverage': 'カテゴリカバレッジ',
    'sb.kb.missing': '不足',
    'sb.kb.missingCategories': '不足しているカテゴリ: {cats}。これらを補うには、生成する（または情報入力を追加する）してください。',
    'sb.kb.gapsIntro': 'このビジネスについて記録された、未回答の顧客からの質問です。',
    'sb.kb.noGaps': '記録されたナレッジギャップはありません。',
    'sb.kb.createDraftFaq': 'ドラフトFAQを作成',
    'sb.kb.gapsLoadError': 'ナレッジギャップを読み込めませんでした: {error}',
    'sb.kb.seenTimes': '{count}回確認・{status}',
    'sb.kb.editDraftFaq': 'ドラフトFAQを編集',
    'sb.kb.createDraftFromGap': 'ギャップからドラフトFAQを作成',
    'sb.kb.question': '質問',
    'sb.kb.answer': '回答',
    'sb.kb.keywords': 'キーワード（カンマ区切り）',
    'sb.kb.answerPlaceholder': '回答を記入してください。不明な場合はビジネス固有の情報は記載しないでください。',
    'sb.kb.editInternalHint': '編集してもこの項目は内部限定のままです。公開するには別途承認してください。',
    'sb.kb.createdDraftHint': 'ドラフト（内部）として作成されました。公開するには承認してください。',
    'sb.kb.saveDraft': 'ドラフトを保存',
    'sb.kb.createDraft': 'ドラフトを作成',
    'sb.kb.noBusinessContext': 'アクティブなビジネスコンテキストがありません',
    'sb.kb.selectBusiness': 'ナレッジビルダーを使用するには、ビジネスを選択または連携してください。',
    'sb.kb.toast.noBusiness': 'ビジネスが選択されていません',
    'sb.kb.toast.noBusinessDesc': '開始できません。アクティブなビジネスコンテキストがありません。',
    'sb.kb.toast.started': 'ナレッジビルダーを開始しました',
    'sb.kb.toast.startedDesc': '分かる範囲で入力してください。不明な項目はスキップして構いません。',
    'sb.kb.toast.couldNotStart': 'セッションを開始できませんでした',
    'sb.kb.toast.progressSaved': '進捗を保存しました',
    'sb.kb.toast.savedFields': '{count} 件の項目を保存しました。',
    'sb.kb.toast.nothingToSave': '新たに保存する項目はありません。',
    'sb.kb.toast.saveFailed': '保存に失敗しました',
    'sb.kb.toast.validationFailed': '検証に失敗しました',
    'sb.kb.toast.draftsGenerated': 'ドラフトFAQを生成しました',
    'sb.kb.toast.draftsGeneratedDesc': '{count} 件のドラフト／推奨項目を作成しました（内部限定）。',
    'sb.kb.toast.generationFailed': '生成に失敗しました',
    'sb.kb.toast.providerUnavailable': 'AI生成プロバイダーは現在利用できません。ドラフトは作成されませんでした。後ほど再度お試しください。',
    'sb.kb.toast.loadGenFailed': '生成されたFAQを読み込めませんでした',
    'sb.kb.toast.approved': '承認しました',
    'sb.kb.toast.approvedDesc': 'このFAQはAIチャットとボイスで直ちに公開されます。',
    'sb.kb.toast.approvalFailed': '承認に失敗しました',
    'sb.kb.toast.rejected': '却下しました',
    'sb.kb.toast.rejectedDesc': 'このドラフトはAIで使用されません。',
    'sb.kb.toast.rejectFailed': '却下に失敗しました',
    'sb.kb.toast.archived': 'アーカイブしました',
    'sb.kb.toast.archivedDesc': '項目を無効化しました（AI検索から除外されます）。',
    'sb.kb.toast.archiveFailed': 'アーカイブに失敗しました',
    'sb.kb.toast.draftUpdated': 'ドラフトを更新しました',
    'sb.kb.toast.draftUpdatedDesc': '編集内容を保存しました。承認されるまで内部限定のままです。',
    'sb.kb.toast.updateFailed': '更新に失敗しました',
    'sb.kb.toast.draftCreated': 'ドラフトFAQを作成しました',
    'sb.kb.toast.draftCreatedDesc': 'ドラフト（内部）として作成されました。公開するには承認してください。',
    'sb.kb.toast.couldNotCreate': 'ドラフトを作成できませんでした',
    'sb.live.approvalsImmediate': '承認は直ちに反映されます。再学習による遅延はありません',
    'sb.live.approvalsBlurb': '承認済みのソリューションは、AIチャットとAIボイスで直ちに利用可能になります。Servisfyは各AI対応のたびに承認済みのSolutionBankナレッジをライブで読み取るため、再学習による遅延は必要ありません。',
    'sb.live.aiChatLive': 'AIチャット: 公開中',
    'sb.live.aiVoiceLive': 'AIボイス: 公開中',
    'sb.live.business': 'ビジネス: {name}',
    'sb.live.liveInAi': 'AIで公開中（承認済み）',
    'sb.live.usedByAiNow': '現在AIチャットとボイスで使用中',
    'sb.live.pendingApproval': '承認待ち',
    'sb.live.notYetUsed': 'まだAIで使用されていません',
    'sb.live.draftsSuggested': 'ドラフト／推奨',
    'sb.live.internalOnly': '内部限定（ナレッジビルダー）',
    'sb.live.inactive': '無効',
    'sb.live.rejectedRevoked': '却下／取り消し／アーカイブ済み',
    'sb.live.totalUsage': 'AI総利用回数（承認済み）',
    'sb.live.realUsageSum': '実際の usage_count 合計',
    'sb.live.avgSuccess': '平均成功率',
    'sb.live.noUsageYet': 'まだ利用実績はありません',
    'sb.live.acrossUsed': '使用された承認済みソリューション全体',
    'sb.live.lastApproved': '最終承認',
    'sb.live.effectiveImmediately': '直ちに有効',
    'sb.live.noApprovalsYet': 'まだ承認はありません',
    'sb.live.topUsed': '最も利用されている承認済みソリューション',
    'sb.live.topUsedEmpty': 'AIで使用された承認済みソリューションはまだありません。',
    'sb.live.recentlyApproved': '最近承認されたもの',
    'sb.live.recentlyApprovedEmpty': '承認済みのソリューションはまだありません。有効にするにはレビューから承認してください。',
    'sb.live.lowSuccess': '承認済みだが成功率が低い — レビューが必要な可能性があります',
    'sb.live.lowSuccessEmpty': '成功率の低い承認済みソリューションはありません。👍',
    'sb.live.lowSuccessHint': '成功率が {threshold}% 未満の、AIで使用されている承認済みソリューションです。',
    'sb.live.loading': 'ライブAIナレッジを読み込んでいます…',
    'sb.live.loadError': 'AIナレッジを読み込めませんでした',
    'sb.live.tryAgain': '再試行',
    'sb.live.loadFailedToast': 'AIナレッジの読み込みに失敗しました',
    'sb.live.untitled': 'タイトルなし',
    'sb.live.uncategorized': '未分類',
    'sb.review.title': 'ソリューションバンクのレビュー',
    'sb.review.subtitle': 'ソリューションバンク向けにAIが推奨したソリューションをレビューして承認します',
    'sb.review.pendingReview': 'レビュー待ち',
    'sb.review.approvedTab': '承認済み',
    'sb.review.rejectedTab': '却下済み',
    'sb.review.noPending': 'レビュー待ちのソリューションはありません',
    'sb.review.noPendingSub': 'AIが推奨したソリューションが承認のためここに表示されます',
    'sb.review.noApproved': '承認済みのソリューションはありません',
    'sb.review.noRejected': '却下されたソリューションはありません',
    'sb.review.description': '説明:',
    'sb.review.resolutionSteps': '解決手順:',
    'sb.review.keywords': 'キーワード:',
    'sb.review.submittedBy': '{name} が送信',
    'sb.review.staff': 'スタッフ',
    'sb.review.aiVoice': 'AIボイス',
    'sb.review.aiChat': 'AIチャット',
    'sb.review.ch.humanChat': '人によるチャット',
    'sb.review.ch.voiceCall': '音声通話',
    'sb.review.ch.humanCall': '担当者との通話',
    'sb.review.ch.solutionBank': 'ソリューションバンク',
    'sb.review.ch.unknown': '不明',
    'sb.review.aiGenerated': 'AI生成',
    'sb.review.openaiFallback': 'OpenAI フォールバック',
    'sb.review.sourceConversation': 'ソース: 会話 {id}',
    'sb.review.confHigh': '高 ({pct}%)',
    'sb.review.confMedium': '中 ({pct}%)',
    'sb.review.confLow': '低 ({pct}%)',
    'sb.review.activeInAi': 'AIで有効',
    'sb.review.approvedOn': '承認日 {date}',
    'sb.review.rejectedOn': '却下日 {date}',
    'sb.review.revoke': '取り消し',
    'sb.review.resubmit': '再度レビューに提出',
    'sb.review.unknownDate': '不明',
    'sb.review.loadFailedDesc': '接続を確認して再度お試しください。',
    'sb.review.toast.approved': 'ソリューションを承認しました',
    'sb.review.toast.approvedDesc': 'ソリューションはAIナレッジベースで公開中になりました。',
    'sb.review.toast.approvalFailed': '承認に失敗しました',
    'sb.review.toast.rejected': 'ソリューションを却下しました',
    'sb.review.toast.rejectedDesc': 'ソリューションはAIで使用されません。',
    'sb.review.toast.rejectionFailed': '却下に失敗しました',
    'sb.review.toast.revoked': 'ソリューションを取り消しました',
    'sb.review.toast.revokedDesc': 'AIは今後このソリューションを使用しません。',
    'sb.review.toast.revokeFailed': '取り消しに失敗しました',
    'sb.review.toast.resubmitted': 'ソリューションを再提出しました',
    'sb.review.toast.resubmittedDesc': 'ソリューションは再度レビュー待ちです。',
    'sb.review.toast.resubmitFailed': '再提出に失敗しました',
    'sb.review.toast.deleted': 'ソリューションを削除しました',
    'sb.review.toast.deletedDesc': 'ソリューションを完全に削除しました。',
    'sb.review.toast.deleteFailed': '削除に失敗しました',
  },
  ko: {
    'sb.tab.solutions': '솔루션',
    'sb.tab.knowledgeBuilder': 'AI 지식 빌더',
    'sb.tab.review': '검토',
    'sb.tab.analytics': '분석',
    'sb.tab.liveAi': '실시간 AI 지식',
    'sb.tab.addSolution': '솔루션 추가',
    'sb.tab.manageSolutions': '솔루션 관리',
    'sb.exporting': '내보내는 중...',
    'sb.importing': '가져오는 중...',
    'sb.searchPlaceholder': '솔루션 검색...',
    'sb.filterSolutions': '솔루션 필터',
    'sb.filterSolutionsDesc': '기준에 따라 솔루션을 좁혀 보여줍니다',
    'sb.difficulty': '난이도',
    'sb.allDifficulties': '전체 난이도',
    'sb.easy': '쉬움',
    'sb.medium': '보통',
    'sb.hard': '어려움',
    'sb.reset': '초기화',
    'sb.apply': '적용',
    'sb.allCategories': '전체 카테고리',
    'sb.revoke.action': '철회',
    'sb.revoke.confirm': '승인된 이 솔루션을 철회하시겠습니까? 수정 후 다시 승인될 때까지 AI가 사용하지 않습니다.',
    'sb.revoke.done': '솔루션이 철회되었습니다',
    'sb.revoke.doneDesc': 'AI가 더 이상 사용하지 않습니다. 수정 후 검토에 다시 제출하세요.',
    'sb.revoke.failed': '철회하지 못했습니다',
    'sb.cat.choose': '카테고리를 선택하세요',
    'sb.cat.noneConfigured': '구성된 카테고리가 없습니다',
    'sb.cat.noneConfiguredHint': '솔루션을 작성하기 전에 카테고리 관리에서 카테고리를 추가하세요.',
    'sb.cat.technicalSupport': '기술 지원',
    'sb.cat.billing': '결제',
    'sb.cat.productIssues': '제품 문제',
    'sb.cat.accountManagement': '계정 관리',
    'sb.totalSolutions': '전체 솔루션',
    'sb.avgSuccessRate': '평균 성공률',
    'sb.totalUsage': '전체 사용량',
    'sb.categories': '카테고리',
    'sb.topPerforming': '성과 상위 솔루션',
    'sb.mostUsed': '가장 많이 사용된 솔루션',
    'sb.loadingSolutions': '솔루션을 불러오는 중...',
    'sb.loadingSolutionsSub': '솔루션 뱅크를 불러오는 동안 잠시 기다려 주십시오',
    'sb.noSolutionsFound': '솔루션을 찾을 수 없습니다',
    'sb.noSolutionsHint': '검색 기준을 조정하거나 관리 탭에서 솔루션을 추가해 보십시오',
    'sb.mgmtFunctionsHint': '솔루션 관리 기능은 관리 탭에서 사용할 수 있습니다',
    'sb.successRateBadge': '{rate}% 성공률',
    'sb.usesBadge': '{count}회 사용',
    'sb.usesLower': '{count}회 사용',
    'sb.updated': '업데이트: {date}',
    'sb.quickTips': '간단 팁',
    'sb.tip1': '솔루션 설명은 구체적이고 명확하게 작성하십시오',
    'sb.tip2': '단계별 안내를 포함하십시오',
    'sb.tip3': '관련 키워드로 솔루션에 태그를 지정하십시오',
    'sb.tip4': '복잡한 문제는 관련 솔루션을 참조하십시오',
    'sb.recentActivity': '최근 활동',
    'sb.noRecentActivity': '아직 최근 활동이 없습니다.',
    'sb.solutionsAddedToday': '오늘 추가된 솔루션:',
    'sb.pendingReviewLabel': '검토 대기 중:',
    'sb.successRateLabel': '성공률:',
    'sb.bulkOperations': '일괄 작업',
    'sb.importFromCsv': 'CSV에서 솔루션 가져오기',
    'sb.exportAll': '전체 솔루션 내보내기',
    'sb.generateReport': '성과 보고서 생성',
    'sb.importCsvDesc': 'CSV 파일을 업로드하여 여러 솔루션을 가져옵니다. CSV에는 다음 열이 포함되어야 합니다: title, description, category, tags, resolution, difficulty, success_rate, usage_count.',
    'sb.selectCsv': '업로드할 CSV 파일을 선택하십시오',
    'sb.selectedFile': '선택됨: {name}',
    'sb.importSolutionsBtn': '솔루션 가져오기',
    'sb.exportAllDesc': '전체 솔루션에 사용할 내보내기 형식을 선택하십시오',
    'sb.csvFile': 'CSV 파일',
    'sb.excelFile': 'Excel 파일',
    'sb.jsonData': 'JSON 데이터',
    'sb.pdfReport': 'PDF 보고서',
    'sb.performanceReport': '솔루션 뱅크 성과 보고서',
    'sb.performanceReportDesc': 'AI가 생성한 인사이트 및 성과 분석',
    'sb.aiAnalysisReport': 'AI 분석 보고서',
    'sb.downloadReport': '보고서 다운로드',
    'sb.editSolution': '솔루션 편집',
    'sb.editSolutionDesc': '솔루션 정보를 업데이트합니다',
    'sb.addNewSolutionTitle': '새 솔루션 추가',
    'sb.addNewSolutionDesc': '솔루션 뱅크에 새 솔루션을 생성합니다',
    'sb.solutionTitle': '솔루션 제목 *',
    'sb.solutionTitlePlaceholder': '솔루션 제목을 입력하십시오',
    'sb.descriptionLabel': '설명 *',
    'sb.descriptionPlaceholder': '이 솔루션이 해결하는 문제를 설명하십시오',
    'sb.category': '카테고리',
    'sb.tagsLabel': '태그 (쉼표로 구분)',
    'sb.tagsPlaceholder': 'password, reset, login',
    'sb.resolutionSteps': '해결 단계',
    'sb.resolutionPlaceholder': '단계별 해결 안내를 입력하십시오',
    'sb.updateSolution': '솔루션 업데이트',
    'sb.addSolutionBtn': '솔루션 추가',
    'sb.deleteSolution': '솔루션 삭제',
    'sb.deleteSolutionDesc': '이 솔루션을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.',
    'sb.toast.loadFailed': '솔루션을 불러오지 못했습니다',
    'sb.toast.missingInfo': '정보 누락',
    'sb.toast.missingInfoDesc': '제목과 설명을 입력하십시오',
    'sb.toast.submitted': '솔루션 제출됨',
    'sb.toast.submittedDesc': '솔루션이 승인을 위해 관리자에게 전달되었습니다',
    'sb.toast.addFailed': '솔루션을 추가하지 못했습니다',
    'sb.toast.exportStarted': '내보내기 시작됨',
    'sb.toast.exportStartedDesc': '{format} 형식으로 솔루션을 내보내는 중',
    'sb.toast.exportComplete': '내보내기 완료',
    'sb.toast.exportCompleteDesc': '솔루션을 {format} 형식으로 성공적으로 내보냈습니다',
    'sb.toast.exportFailed': '내보내기 실패',
    'sb.toast.exportFailedDesc': '솔루션을 내보내지 못했습니다',
    'sb.toast.noFile': '선택된 파일 없음',
    'sb.toast.noFileDesc': '가져올 CSV 파일을 선택하십시오',
    'sb.toast.importStarted': '가져오기 시작됨',
    'sb.toast.importStartedDesc': 'CSV 파일을 처리하는 중...',
    'sb.toast.importComplete': '가져오기 완료',
    'sb.toast.importCompleteDesc': '솔루션을 성공적으로 가져왔습니다',
    'sb.toast.importFailed': '가져오기 실패',
    'sb.toast.importFailedDesc': 'CSV에서 솔루션을 가져오지 못했습니다',
    'sb.toast.invalidFile': '잘못된 파일',
    'sb.toast.invalidFileDesc': '유효한 CSV 파일을 선택하십시오',
    'sb.toast.reportGenerated': '보고서 생성됨',
    'sb.toast.reportGeneratedDesc': '성과 보고서가 성공적으로 생성되었습니다',
    'sb.toast.reportFailed': '보고서 생성 실패',
    'sb.toast.reportFailedDesc': '성과 보고서를 생성하지 못했습니다',
    'sb.toast.updated': '솔루션 업데이트됨',
    'sb.toast.updatedDesc': '솔루션이 성공적으로 업데이트되었습니다',
    'sb.toast.updateFailed': '솔루션을 업데이트하지 못했습니다',
    'sb.toast.deleted': '솔루션 삭제됨',
    'sb.toast.deletedDesc': '솔루션이 성공적으로 삭제되었습니다',
    'sb.toast.deleteFailed': '솔루션을 삭제하지 못했습니다',
    'sb.kb.title': 'AI 지식 빌더',
    'sb.kb.subtitle': '비즈니스 정보를 입력하여 → 50개의 초안 FAQ를 생성하고 → 검토 및 승인합니다. 초안은 승인 전까지 내부용으로 유지됩니다.',
    'sb.kb.newSession': '새 세션',
    'sb.kb.buildTitle': 'AI 지식 베이스 구축',
    'sb.kb.buildBlurb': '비즈니스에 대해 알고 있는 내용을 입력하십시오. 50개의 초안/제안 FAQ를 생성합니다. 관리자 또는 관리자가 승인하기 전까지는 고객에게 공개되지 않습니다.',
    'sb.kb.start': '지식 빌더 시작',
    'sb.kb.tab.intake': '정보 입력',
    'sb.kb.tab.validate': '검증',
    'sb.kb.tab.review': '생성 및 검토',
    'sb.kb.tab.coverage': '범위',
    'sb.kb.tab.gaps': '공백',
    'sb.kb.skipHint': '모르는 항목은 건너뛰십시오 — 알 수 없는 필드는 임의로 만들어 내지 않습니다.',
    'sb.kb.saveProgress': '진행 상황 저장',
    'sb.kb.blankUnknown': '알 수 없으면 비워 두십시오',
    'sb.kb.blankUnknownTextarea': '알 수 없으면 비워 두십시오 — 임의로 만들어 내지 않습니다',
    'sb.kb.unknownSkip': '알 수 없음 / 건너뛰기',
    'sb.kb.sec.identity': '비즈니스 정체성',
    'sb.kb.sec.profile': '비즈니스 프로필',
    'sb.kb.sec.operations': '운영',
    'sb.kb.sec.payments': '결제',
    'sb.kb.sec.delivery': '배송 / 배달',
    'sb.kb.sec.booking': '예약',
    'sb.kb.sec.returns': '반품 / 환불',
    'sb.kb.sec.support': '지원',
    'sb.kb.sec.policies': '정책',
    'sb.kb.sec.extras': '고객 질문 및 참고 사항',
    'sb.kb.f.business_name': '상호명',
    'sb.kb.f.address': '주소',
    'sb.kb.f.city': '도시',
    'sb.kb.f.province': '도 / 주',
    'sb.kb.f.country': '국가',
    'sb.kb.f.phone': '전화',
    'sb.kb.f.email': '이메일',
    'sb.kb.f.website': '웹사이트',
    'sb.kb.f.social_media': '소셜 미디어',
    'sb.kb.f.business_description': '비즈니스 설명',
    'sb.kb.f.products': '판매 제품',
    'sb.kb.f.services': '제공 서비스',
    'sb.kb.f.target_customers': '대상 고객',
    'sb.kb.f.industries_served': '서비스 대상 산업',
    'sb.kb.f.differentiator': '차별화 요소',
    'sb.kb.f.mission_vision': '미션 / 비전',
    'sb.kb.f.opening_days': '영업일',
    'sb.kb.f.operating_hours': '영업 시간',
    'sb.kb.f.holiday_hours': '공휴일 영업 시간',
    'sb.kb.f.appointment_required': '예약 필요',
    'sb.kb.f.walk_ins_accepted': '방문 접수 가능',
    'sb.kb.f.delivery_available': '배달 가능',
    'sb.kb.f.pickup_available': '픽업 가능',
    'sb.kb.f.online_orders': '온라인 주문',
    'sb.kb.f.service_area': '서비스 지역',
    'sb.kb.f.payment_methods': '허용 결제 수단',
    'sb.kb.f.delivery_locations': '배달 지역',
    'sb.kb.f.delivery_fees': '배달 요금',
    'sb.kb.f.delivery_timelines': '배달 소요 시간',
    'sb.kb.f.same_day_delivery': '당일 배달',
    'sb.kb.f.international_shipping': '해외 배송',
    'sb.kb.f.booking_method': '예약 방법',
    'sb.kb.f.cancellation_policy': '취소 정책',
    'sb.kb.f.rescheduling_policy': '일정 변경 정책',
    'sb.kb.f.late_arrival_policy': '지각 정책',
    'sb.kb.f.ordering_method': '온라인 주문 방법',
    'sb.kb.f.return_policy': '반품 정책',
    'sb.kb.f.refund_policy': '환불 정책',
    'sb.kb.f.exchange_policy': '교환 정책',
    'sb.kb.f.contact_methods': '주요 연락 수단',
    'sb.kb.f.support_phone': '지원 전화',
    'sb.kb.f.support_whatsapp': 'WhatsApp',
    'sb.kb.f.support_email': '지원 이메일',
    'sb.kb.f.support_website': '지원 웹사이트',
    'sb.kb.f.support_in_person': '대면 지원',
    'sb.kb.f.privacy_policy': '개인정보 처리방침',
    'sb.kb.f.warranty_policy': '보증 정책',
    'sb.kb.f.terms_of_service': '서비스 이용약관',
    'sb.kb.f.service_guarantees': '서비스 보장',
    'sb.kb.f.policies': '일반 정책 요약',
    'sb.kb.f.common_questions': '고객이 자주 묻는 질문',
    'sb.kb.f.special_instructions': '고객이 항상 알아야 할 사항',
    'sb.kb.pay.cash': '현금',
    'sb.kb.pay.debit': '체크카드',
    'sb.kb.pay.credit': '신용카드',
    'sb.kb.pay.bankTransfer': '계좌 이체',
    'sb.kb.pay.applePay': 'Apple Pay',
    'sb.kb.pay.googlePay': 'Google Pay',
    'sb.kb.pay.payPal': 'PayPal',
    'sb.kb.pay.mobileMoney': '모바일 머니',
    'sb.kb.pay.interac': 'Interac e-Transfer',
    'sb.kb.pay.other': '기타',
    'sb.kb.revalidate': '재검증',
    'sb.kb.runValidationHint': '준비 상태를 확인하려면 정보 입력 탭에서 검증을 실행하십시오.',
    'sb.kb.readiness': '준비 상태',
    'sb.kb.readinessHint': '생성은 절대 차단되지 않습니다. 누락된 비즈니스별 정보는 제안 FAQ가 되거나 "이 정책은 업체에 확인해 주십시오." 라는 답변이 되며, 임의로 만들어 내지 않습니다.',
    'sb.kb.val.missingFields': '누락된 필드',
    'sb.kb.val.missingFieldsEmpty': '필수 항목 중 누락된 것이 없습니다.',
    'sb.kb.val.contradictions': '모순',
    'sb.kb.val.contradictionsEmpty': '모순이 발견되지 않았습니다.',
    'sb.kb.val.vague': '모호한 답변',
    'sb.kb.val.vagueEmpty': '모호한 답변이 없습니다.',
    'sb.kb.val.followups': '후속 질문',
    'sb.kb.val.followupsEmpty': '후속 질문이 없습니다.',
    'sb.kb.completeMore': '← 필드를 더 입력하기',
    'sb.kb.continueGenerate': '생성으로 계속 →',
    'sb.kb.generate50': '50개 초안 FAQ 생성',
    'sb.kb.generate50Sub': '실제 백엔드를 통해 검증 및 AI 생성을 실행합니다. 초안은 승인 전까지 내부용으로만 사용됩니다.',
    'sb.kb.generating': '생성 중…',
    'sb.kb.internalVsLive': '초안 및 제안 = 내부용 전용. 승인됨 = AI 채팅/음성에서 실시간 사용.',
    'sb.kb.showArchived': '보관된 항목 표시',
    'sb.kb.noGeneratedYet': '아직 생성된 FAQ가 없습니다. 위의 50개 초안 FAQ 생성을 클릭하십시오.',
    'sb.kb.status.draft': '초안 · 내부용 전용',
    'sb.kb.status.suggested': '제안됨 · 내부용 전용',
    'sb.kb.status.pending': '승인 대기 중 · 내부',
    'sb.kb.status.approved': '승인됨 · AI에서 실시간 사용',
    'sb.kb.status.archived': '보관됨',
    'sb.kb.status.rejected': '거부됨',
    'sb.kb.ownerSupplied': '소유자 제공',
    'sb.kb.aiSuggested': 'AI 제안',
    'sb.kb.totalGenerated': '총 생성 수',
    'sb.kb.customerFacing': '고객 대상 (승인됨)',
    'sb.kb.internalDraft': '내부 (초안/제안)',
    'sb.kb.categoryCoverage': '카테고리 범위',
    'sb.kb.missing': '누락',
    'sb.kb.missingCategories': '누락된 카테고리: {cats}. 이를 채우려면 생성하거나 정보를 더 입력하십시오.',
    'sb.kb.gapsIntro': '이 업체에 대해 기록된 미답변 고객 질문입니다.',
    'sb.kb.noGaps': '기록된 지식 공백이 없습니다.',
    'sb.kb.createDraftFaq': '초안 FAQ 생성',
    'sb.kb.gapsLoadError': '지식 공백을 불러올 수 없습니다: {error}',
    'sb.kb.seenTimes': '{count}회 확인됨 · {status}',
    'sb.kb.editDraftFaq': '초안 FAQ 편집',
    'sb.kb.createDraftFromGap': '공백에서 초안 FAQ 생성',
    'sb.kb.question': '질문',
    'sb.kb.answer': '답변',
    'sb.kb.keywords': '키워드 (쉼표로 구분)',
    'sb.kb.answerPlaceholder': '답변을 작성하십시오. 알 수 없는 업체별 정보는 제외하십시오.',
    'sb.kb.editInternalHint': '편집해도 내부용으로 유지됩니다. 실시간으로 적용하려면 별도로 승인하십시오.',
    'sb.kb.createdDraftHint': '초안(내부용)으로 생성되었습니다. 실시간으로 적용하려면 승인하십시오.',
    'sb.kb.saveDraft': '초안 저장',
    'sb.kb.createDraft': '초안 생성',
    'sb.kb.noBusinessContext': '활성 비즈니스 컨텍스트가 없습니다',
    'sb.kb.selectBusiness': '지식 빌더를 사용하려면 비즈니스를 선택하거나 연결하십시오.',
    'sb.kb.toast.noBusiness': '선택된 비즈니스 없음',
    'sb.kb.toast.noBusinessDesc': '시작할 수 없습니다 — 활성 비즈니스 컨텍스트가 없습니다.',
    'sb.kb.toast.started': '지식 빌더가 시작되었습니다',
    'sb.kb.toast.startedDesc': '알고 있는 내용을 입력하십시오 — 모르는 항목은 건너뛸 수 있습니다.',
    'sb.kb.toast.couldNotStart': '세션을 시작할 수 없습니다',
    'sb.kb.toast.progressSaved': '진행 상황이 저장되었습니다',
    'sb.kb.toast.savedFields': '{count}개 필드가 저장되었습니다.',
    'sb.kb.toast.nothingToSave': '새로 저장할 내용이 없습니다.',
    'sb.kb.toast.saveFailed': '저장 실패',
    'sb.kb.toast.validationFailed': '검증 실패',
    'sb.kb.toast.draftsGenerated': '초안 FAQ가 생성되었습니다',
    'sb.kb.toast.draftsGeneratedDesc': '{count}개 초안/제안 항목이 생성되었습니다 (내부용 전용).',
    'sb.kb.toast.generationFailed': '생성 실패',
    'sb.kb.toast.providerUnavailable': 'AI 생성 제공자를 현재 사용할 수 없습니다. 초안이 생성되지 않았습니다. 나중에 다시 시도해 주십시오.',
    'sb.kb.toast.loadGenFailed': '생성된 FAQ를 불러올 수 없습니다',
    'sb.kb.toast.approved': '승인됨',
    'sb.kb.toast.approvedDesc': '이 FAQ는 이제 AI 채팅 및 음성에서 즉시 실시간으로 사용됩니다.',
    'sb.kb.toast.approvalFailed': '승인 실패',
    'sb.kb.toast.rejected': '거부됨',
    'sb.kb.toast.rejectedDesc': '이 초안은 AI에서 사용되지 않습니다.',
    'sb.kb.toast.rejectFailed': '거부 실패',
    'sb.kb.toast.archived': '보관됨',
    'sb.kb.toast.archivedDesc': '항목이 보관되었습니다 (AI 검색에서 제외됨).',
    'sb.kb.toast.archiveFailed': '보관 실패',
    'sb.kb.toast.draftUpdated': '초안 업데이트됨',
    'sb.kb.toast.draftUpdatedDesc': '편집 내용이 저장되었습니다. 승인 전까지 내부용으로 유지됩니다.',
    'sb.kb.toast.updateFailed': '업데이트 실패',
    'sb.kb.toast.draftCreated': '초안 FAQ가 생성되었습니다',
    'sb.kb.toast.draftCreatedDesc': '초안(내부용)으로 생성되었습니다. 실시간으로 적용하려면 승인하십시오.',
    'sb.kb.toast.couldNotCreate': '초안을 생성할 수 없습니다',
    'sb.live.approvalsImmediate': '승인은 즉시 적용됩니다 — 재학습 지연이 없습니다',
    'sb.live.approvalsBlurb': '승인된 솔루션은 AI 채팅 및 AI 음성에서 즉시 사용할 수 있습니다. Servisfy는 각 AI 상호작용마다 승인된 SolutionBank 지식을 실시간으로 읽기 때문에 재학습 지연이 필요하지 않습니다.',
    'sb.live.aiChatLive': 'AI 채팅: 실시간',
    'sb.live.aiVoiceLive': 'AI 음성: 실시간',
    'sb.live.business': '비즈니스: {name}',
    'sb.live.liveInAi': 'AI에서 실시간 (승인됨)',
    'sb.live.usedByAiNow': '현재 AI 채팅 및 음성에서 사용 중',
    'sb.live.pendingApproval': '승인 대기 중',
    'sb.live.notYetUsed': '아직 AI에서 사용되지 않음',
    'sb.live.draftsSuggested': '초안 / 제안',
    'sb.live.internalOnly': '내부용 전용 (지식 빌더)',
    'sb.live.inactive': '비활성',
    'sb.live.rejectedRevoked': '거부됨 / 철회됨 / 보관됨',
    'sb.live.totalUsage': '총 AI 사용량 (승인됨)',
    'sb.live.realUsageSum': '실제 usage_count 합계',
    'sb.live.avgSuccess': '평균 성공률',
    'sb.live.noUsageYet': '아직 기록된 사용량이 없습니다',
    'sb.live.acrossUsed': '사용된 승인 솔루션 전체 기준',
    'sb.live.lastApproved': '마지막 승인',
    'sb.live.effectiveImmediately': '즉시 적용됨',
    'sb.live.noApprovalsYet': '아직 승인된 항목이 없습니다',
    'sb.live.topUsed': '가장 많이 사용된 승인 솔루션',
    'sb.live.topUsedEmpty': '아직 AI에서 사용된 승인 솔루션이 없습니다.',
    'sb.live.recentlyApproved': '최근 승인됨',
    'sb.live.recentlyApprovedEmpty': '아직 승인된 솔루션이 없습니다. 활성화하려면 검토에서 승인하십시오.',
    'sb.live.lowSuccess': '승인되었으나 성공률이 낮음 — 검토가 필요할 수 있습니다',
    'sb.live.lowSuccessEmpty': '성공률이 낮은 승인 솔루션이 없습니다. 👍',
    'sb.live.lowSuccessHint': 'AI가 사용한 승인 솔루션 중 성공률이 {threshold}% 미만인 항목입니다.',
    'sb.live.loading': '실시간 AI 지식을 불러오는 중…',
    'sb.live.loadError': 'AI 지식을 불러올 수 없습니다',
    'sb.live.tryAgain': '다시 시도',
    'sb.live.loadFailedToast': 'AI 지식을 불러오지 못했습니다',
    'sb.live.untitled': '제목 없음',
    'sb.live.uncategorized': '분류되지 않음',
    'sb.review.title': '솔루션 뱅크 검토',
    'sb.review.subtitle': '솔루션 뱅크를 위한 AI 제안 솔루션을 검토하고 승인합니다',
    'sb.review.pendingReview': '검토 대기 중',
    'sb.review.approvedTab': '승인됨',
    'sb.review.rejectedTab': '거부됨',
    'sb.review.noPending': '검토할 대기 중인 솔루션이 없습니다',
    'sb.review.noPendingSub': 'AI 제안 솔루션이 승인을 위해 여기에 표시됩니다',
    'sb.review.noApproved': '승인된 솔루션이 없습니다',
    'sb.review.noRejected': '거부된 솔루션이 없습니다',
    'sb.review.description': '설명:',
    'sb.review.resolutionSteps': '해결 단계:',
    'sb.review.keywords': '키워드:',
    'sb.review.submittedBy': '{name} 제출',
    'sb.review.staff': '직원',
    'sb.review.aiVoice': 'AI 음성',
    'sb.review.aiChat': 'AI 채팅',
    'sb.review.ch.humanChat': '상담원 채팅',
    'sb.review.ch.voiceCall': '음성 통화',
    'sb.review.ch.humanCall': '상담원 통화',
    'sb.review.ch.solutionBank': '솔루션 뱅크',
    'sb.review.ch.unknown': '알 수 없음',
    'sb.review.aiGenerated': 'AI 생성',
    'sb.review.openaiFallback': 'OpenAI 대체',
    'sb.review.sourceConversation': '출처: 대화 {id}',
    'sb.review.confHigh': '높음 ({pct}%)',
    'sb.review.confMedium': '보통 ({pct}%)',
    'sb.review.confLow': '낮음 ({pct}%)',
    'sb.review.activeInAi': 'AI에서 활성',
    'sb.review.approvedOn': '{date} 승인됨',
    'sb.review.rejectedOn': '{date} 거부됨',
    'sb.review.revoke': '철회',
    'sb.review.resubmit': '검토 재제출',
    'sb.review.unknownDate': '알 수 없음',
    'sb.review.loadFailedDesc': '연결을 확인한 후 다시 시도하십시오.',
    'sb.review.toast.approved': '솔루션 승인됨',
    'sb.review.toast.approvedDesc': '솔루션이 이제 AI 지식 베이스에서 실시간으로 사용됩니다.',
    'sb.review.toast.approvalFailed': '승인 실패',
    'sb.review.toast.rejected': '솔루션 거부됨',
    'sb.review.toast.rejectedDesc': '솔루션이 AI에서 사용되지 않습니다.',
    'sb.review.toast.rejectionFailed': '거부 실패',
    'sb.review.toast.revoked': '솔루션 철회됨',
    'sb.review.toast.revokedDesc': 'AI가 더 이상 이 솔루션을 사용하지 않습니다.',
    'sb.review.toast.revokeFailed': '철회 실패',
    'sb.review.toast.resubmitted': '솔루션 재제출됨',
    'sb.review.toast.resubmittedDesc': '솔루션이 다시 검토 대기 중입니다.',
    'sb.review.toast.resubmitFailed': '재제출 실패',
    'sb.review.toast.deleted': '솔루션 삭제됨',
    'sb.review.toast.deletedDesc': '솔루션이 영구적으로 삭제되었습니다.',
    'sb.review.toast.deleteFailed': '삭제 실패',
  },
  zh: {
    'sb.tab.solutions': '解决方案',
    'sb.tab.knowledgeBuilder': 'AI 知识构建器',
    'sb.tab.review': '审核',
    'sb.tab.analytics': '分析',
    'sb.tab.liveAi': '实时 AI 知识',
    'sb.tab.addSolution': '添加解决方案',
    'sb.tab.manageSolutions': '管理解决方案',
    'sb.exporting': '正在导出…',
    'sb.importing': '正在导入…',
    'sb.searchPlaceholder': '搜索解决方案…',
    'sb.filterSolutions': '筛选解决方案',
    'sb.filterSolutionsDesc': '按条件缩小解决方案范围',
    'sb.difficulty': '难度',
    'sb.allDifficulties': '所有难度',
    'sb.easy': '简单',
    'sb.medium': '中等',
    'sb.hard': '困难',
    'sb.reset': '重置',
    'sb.apply': '应用',
    'sb.allCategories': '所有类别',
    'sb.revoke.action': '撤销',
    'sb.revoke.confirm': '撤销此已批准的解决方案？在更正并重新批准之前，AI 将不再使用它。',
    'sb.revoke.done': '解决方案已撤销',
    'sb.revoke.doneDesc': 'AI 不再使用它。请编辑后重新提交审核。',
    'sb.revoke.failed': '无法撤销',
    'sb.cat.choose': '选择一个类别',
    'sb.cat.noneConfigured': '未配置任何类别',
    'sb.cat.noneConfiguredHint': '在创建解决方案之前，请在类别管理中添加类别。',
    'sb.cat.technicalSupport': '技术支持',
    'sb.cat.billing': '账单',
    'sb.cat.productIssues': '产品问题',
    'sb.cat.accountManagement': '账户管理',
    'sb.totalSolutions': '解决方案总数',
    'sb.avgSuccessRate': '平均成功率',
    'sb.totalUsage': '使用总量',
    'sb.categories': '类别',
    'sb.topPerforming': '表现最佳的解决方案',
    'sb.mostUsed': '使用最多的解决方案',
    'sb.loadingSolutions': '正在加载解决方案…',
    'sb.loadingSolutionsSub': '正在获取您的解决方案库，请稍候',
    'sb.noSolutionsFound': '未找到解决方案',
    'sb.noSolutionsHint': '请尝试调整搜索条件，或使用管理选项卡添加解决方案',
    'sb.mgmtFunctionsHint': '解决方案管理功能位于管理选项卡中',
    'sb.successRateBadge': '成功率 {rate}%',
    'sb.usesBadge': '{count} 次使用',
    'sb.usesLower': '{count} 次使用',
    'sb.updated': '更新时间：{date}',
    'sb.quickTips': '快速提示',
    'sb.tip1': '解决方案描述要具体清晰',
    'sb.tip2': '包含分步说明',
    'sb.tip3': '为解决方案添加相关关键词标签',
    'sb.tip4': '对复杂问题引用相关解决方案',
    'sb.recentActivity': '近期活动',
    'sb.noRecentActivity': '暂无近期活动。',
    'sb.solutionsAddedToday': '今日新增解决方案：',
    'sb.pendingReviewLabel': '待审核：',
    'sb.successRateLabel': '成功率：',
    'sb.bulkOperations': '批量操作',
    'sb.importFromCsv': '从 CSV 导入解决方案',
    'sb.exportAll': '导出全部解决方案',
    'sb.generateReport': '生成绩效报告',
    'sb.importCsvDesc': '上传 CSV 文件以导入多个解决方案。该 CSV 应包含以下列：title, description, category, tags, resolution, difficulty, success_rate, usage_count。',
    'sb.selectCsv': '选择要上传的 CSV 文件',
    'sb.selectedFile': '已选择：{name}',
    'sb.importSolutionsBtn': '导入解决方案',
    'sb.exportAllDesc': '为全部解决方案选择您偏好的导出格式',
    'sb.csvFile': 'CSV 文件',
    'sb.excelFile': 'Excel 文件',
    'sb.jsonData': 'JSON 数据',
    'sb.pdfReport': 'PDF 报告',
    'sb.performanceReport': '解决方案库绩效报告',
    'sb.performanceReportDesc': 'AI 生成的洞察与绩效分析',
    'sb.aiAnalysisReport': 'AI 分析报告',
    'sb.downloadReport': '下载报告',
    'sb.editSolution': '编辑解决方案',
    'sb.editSolutionDesc': '更新解决方案信息',
    'sb.addNewSolutionTitle': '添加新解决方案',
    'sb.addNewSolutionDesc': '为您的解决方案库创建新的解决方案',
    'sb.solutionTitle': '解决方案标题 *',
    'sb.solutionTitlePlaceholder': '输入解决方案标题',
    'sb.descriptionLabel': '描述 *',
    'sb.descriptionPlaceholder': '描述此解决方案所处理的问题',
    'sb.category': '类别',
    'sb.tagsLabel': '标签（以逗号分隔）',
    'sb.tagsPlaceholder': '密码, 重置, 登录',
    'sb.resolutionSteps': '解决步骤',
    'sb.resolutionPlaceholder': '输入分步解决说明',
    'sb.updateSolution': '更新解决方案',
    'sb.addSolutionBtn': '添加解决方案',
    'sb.deleteSolution': '删除解决方案',
    'sb.deleteSolutionDesc': '您确定要删除此解决方案吗？此操作无法撤销。',
    'sb.toast.loadFailed': '加载解决方案失败',
    'sb.toast.missingInfo': '信息缺失',
    'sb.toast.missingInfoDesc': '请填写标题和描述',
    'sb.toast.submitted': '解决方案已提交',
    'sb.toast.submittedDesc': '解决方案已转交经理审批',
    'sb.toast.addFailed': '添加解决方案失败',
    'sb.toast.exportStarted': '导出已开始',
    'sb.toast.exportStartedDesc': '正在以 {format} 格式导出解决方案',
    'sb.toast.exportComplete': '导出完成',
    'sb.toast.exportCompleteDesc': '解决方案已成功导出为 {format} 格式',
    'sb.toast.exportFailed': '导出失败',
    'sb.toast.exportFailedDesc': '导出解决方案失败',
    'sb.toast.noFile': '未选择文件',
    'sb.toast.noFileDesc': '请选择要导入的 CSV 文件',
    'sb.toast.importStarted': '导入已开始',
    'sb.toast.importStartedDesc': '正在处理 CSV 文件…',
    'sb.toast.importComplete': '导入完成',
    'sb.toast.importCompleteDesc': '解决方案已成功导入',
    'sb.toast.importFailed': '导入失败',
    'sb.toast.importFailedDesc': '从 CSV 导入解决方案失败',
    'sb.toast.invalidFile': '无效文件',
    'sb.toast.invalidFileDesc': '请选择有效的 CSV 文件',
    'sb.toast.reportGenerated': '报告已生成',
    'sb.toast.reportGeneratedDesc': '绩效报告已成功生成',
    'sb.toast.reportFailed': '报告生成失败',
    'sb.toast.reportFailedDesc': '生成绩效报告失败',
    'sb.toast.updated': '解决方案已更新',
    'sb.toast.updatedDesc': '解决方案已成功更新',
    'sb.toast.updateFailed': '更新解决方案失败',
    'sb.toast.deleted': '解决方案已删除',
    'sb.toast.deletedDesc': '解决方案已成功删除',
    'sb.toast.deleteFailed': '删除解决方案失败',
    'sb.kb.title': 'AI 知识构建器',
    'sb.kb.subtitle': '录入您的业务信息 → 生成 50 条草稿 FAQ → 审核并批准。草稿在批准前仅供内部使用。',
    'sb.kb.newSession': '新建会话',
    'sb.kb.buildTitle': '构建您的 AI 知识库',
    'sb.kb.buildBlurb': '回答您所了解的业务信息。我们将生成 50 条草稿/建议 FAQ。在经理或管理员批准之前，任何内容都不会对客户上线。',
    'sb.kb.start': '启动知识构建器',
    'sb.kb.tab.intake': '录入',
    'sb.kb.tab.validate': '验证',
    'sb.kb.tab.review': '生成与审核',
    'sb.kb.tab.coverage': '覆盖范围',
    'sb.kb.tab.gaps': '缺口',
    'sb.kb.skipHint': '不清楚的内容可以跳过——未知字段绝不会被杜撰。',
    'sb.kb.saveProgress': '保存进度',
    'sb.kb.blankUnknown': '如不清楚请留空',
    'sb.kb.blankUnknownTextarea': '如不清楚请留空——不会被杜撰',
    'sb.kb.unknownSkip': '未知 / 跳过',
    'sb.kb.sec.identity': '业务标识',
    'sb.kb.sec.profile': '业务概况',
    'sb.kb.sec.operations': '运营',
    'sb.kb.sec.payments': '付款',
    'sb.kb.sec.delivery': '运送 / 配送',
    'sb.kb.sec.booking': '预约',
    'sb.kb.sec.returns': '退货 / 退款',
    'sb.kb.sec.support': '支持',
    'sb.kb.sec.policies': '政策',
    'sb.kb.sec.extras': '客户问题与备注',
    'sb.kb.f.business_name': '业务名称',
    'sb.kb.f.address': '地址',
    'sb.kb.f.city': '城市',
    'sb.kb.f.province': '省 / 州',
    'sb.kb.f.country': '国家',
    'sb.kb.f.phone': '电话',
    'sb.kb.f.email': '电子邮箱',
    'sb.kb.f.website': '网站',
    'sb.kb.f.social_media': '社交媒体',
    'sb.kb.f.business_description': '业务描述',
    'sb.kb.f.products': '销售的产品',
    'sb.kb.f.services': '提供的服务',
    'sb.kb.f.target_customers': '目标客户',
    'sb.kb.f.industries_served': '服务的行业',
    'sb.kb.f.differentiator': '差异化优势',
    'sb.kb.f.mission_vision': '使命 / 愿景',
    'sb.kb.f.opening_days': '营业日',
    'sb.kb.f.operating_hours': '营业时间',
    'sb.kb.f.holiday_hours': '节假日营业时间',
    'sb.kb.f.appointment_required': '需要预约',
    'sb.kb.f.walk_ins_accepted': '接受即到即办',
    'sb.kb.f.delivery_available': '提供配送',
    'sb.kb.f.pickup_available': '提供自提',
    'sb.kb.f.online_orders': '在线订购',
    'sb.kb.f.service_area': '服务区域',
    'sb.kb.f.payment_methods': '接受的付款方式',
    'sb.kb.f.delivery_locations': '配送地点',
    'sb.kb.f.delivery_fees': '配送费用',
    'sb.kb.f.delivery_timelines': '配送时限',
    'sb.kb.f.same_day_delivery': '当日配送',
    'sb.kb.f.international_shipping': '国际运送',
    'sb.kb.f.booking_method': '预约方式',
    'sb.kb.f.cancellation_policy': '取消政策',
    'sb.kb.f.rescheduling_policy': '改期政策',
    'sb.kb.f.late_arrival_policy': '迟到政策',
    'sb.kb.f.ordering_method': '在线下单方式',
    'sb.kb.f.return_policy': '退货政策',
    'sb.kb.f.refund_policy': '退款政策',
    'sb.kb.f.exchange_policy': '换货政策',
    'sb.kb.f.contact_methods': '主要联系方式',
    'sb.kb.f.support_phone': '支持电话',
    'sb.kb.f.support_whatsapp': 'WhatsApp',
    'sb.kb.f.support_email': '支持邮箱',
    'sb.kb.f.support_website': '支持网站',
    'sb.kb.f.support_in_person': '现场支持',
    'sb.kb.f.privacy_policy': '隐私政策',
    'sb.kb.f.warranty_policy': '保修政策',
    'sb.kb.f.terms_of_service': '服务条款',
    'sb.kb.f.service_guarantees': '服务保证',
    'sb.kb.f.policies': '通用政策摘要',
    'sb.kb.f.common_questions': '客户常问的问题',
    'sb.kb.f.special_instructions': '客户应始终知晓的事项',
    'sb.kb.pay.cash': '现金',
    'sb.kb.pay.debit': '借记卡',
    'sb.kb.pay.credit': '信用卡',
    'sb.kb.pay.bankTransfer': '银行转账',
    'sb.kb.pay.applePay': 'Apple Pay',
    'sb.kb.pay.googlePay': 'Google Pay',
    'sb.kb.pay.payPal': 'PayPal',
    'sb.kb.pay.mobileMoney': '移动支付',
    'sb.kb.pay.interac': 'Interac e-Transfer',
    'sb.kb.pay.other': '其他',
    'sb.kb.revalidate': '重新验证',
    'sb.kb.runValidationHint': '从录入选项卡运行验证以查看就绪情况。',
    'sb.kb.readiness': '就绪情况',
    'sb.kb.readinessHint': '生成永远不会被阻止。缺失的业务专属事实会成为一条建议 FAQ，或给出答复"请与商家确认此政策。"——绝不会被杜撰。',
    'sb.kb.val.missingFields': '缺失字段',
    'sb.kb.val.missingFieldsEmpty': '没有缺失任何必填内容。',
    'sb.kb.val.contradictions': '矛盾之处',
    'sb.kb.val.contradictionsEmpty': '未发现矛盾之处。',
    'sb.kb.val.vague': '含糊的答复',
    'sb.kb.val.vagueEmpty': '没有含糊的答复。',
    'sb.kb.val.followups': '后续问题',
    'sb.kb.val.followupsEmpty': '没有后续问题。',
    'sb.kb.completeMore': '← 完善更多字段',
    'sb.kb.continueGenerate': '继续生成 →',
    'sb.kb.generate50': '生成 50 条草稿 FAQ',
    'sb.kb.generate50Sub': '通过真实后端运行验证 + AI 生成。草稿在批准前仅供内部使用。',
    'sb.kb.generating': '正在生成…',
    'sb.kb.internalVsLive': '草稿与建议 = 仅供内部。已批准 = 在 AI Chat/Voice 中上线。',
    'sb.kb.showArchived': '显示已归档',
    'sb.kb.noGeneratedYet': '尚未生成 FAQ。请点击上方的“生成 50 条草稿 FAQ”。',
    'sb.kb.status.draft': '草稿 · 仅供内部',
    'sb.kb.status.suggested': '建议 · 仅供内部',
    'sb.kb.status.pending': '待批准 · 内部',
    'sb.kb.status.approved': '已批准 · 已为 AI 上线',
    'sb.kb.status.archived': '已归档',
    'sb.kb.status.rejected': '已拒绝',
    'sb.kb.ownerSupplied': '商家提供',
    'sb.kb.aiSuggested': 'AI 建议',
    'sb.kb.totalGenerated': '生成总数',
    'sb.kb.customerFacing': '面向客户（已批准）',
    'sb.kb.internalDraft': '内部（草稿/建议）',
    'sb.kb.categoryCoverage': '类别覆盖范围',
    'sb.kb.missing': '缺失',
    'sb.kb.missingCategories': '缺失的类别：{cats}。请生成（或补充更多录入信息）以覆盖它们。',
    'sb.kb.gapsIntro': '为此业务记录的未解答客户问题。',
    'sb.kb.noGaps': '未记录任何知识缺口。',
    'sb.kb.createDraftFaq': '创建草稿 FAQ',
    'sb.kb.gapsLoadError': '无法加载知识缺口：{error}',
    'sb.kb.seenTimes': '出现 {count}× · {status}',
    'sb.kb.editDraftFaq': '编辑草稿 FAQ',
    'sb.kb.createDraftFromGap': '根据缺口创建草稿 FAQ',
    'sb.kb.question': '问题',
    'sb.kb.answer': '答复',
    'sb.kb.keywords': '关键词（以逗号分隔）',
    'sb.kb.answerPlaceholder': '撰写答复。如不清楚，请勿填写商家专属信息。',
    'sb.kb.editInternalHint': '编辑后仍为内部内容。需另行批准才能上线。',
    'sb.kb.createdDraftHint': '已作为草稿（内部）创建。批准后即可上线。',
    'sb.kb.saveDraft': '保存草稿',
    'sb.kb.createDraft': '创建草稿',
    'sb.kb.noBusinessContext': '无活动的业务上下文',
    'sb.kb.selectBusiness': '选择或关联一个业务以使用知识构建器。',
    'sb.kb.toast.noBusiness': '未选择业务',
    'sb.kb.toast.noBusinessDesc': '无法启动——无活动的业务上下文。',
    'sb.kb.toast.started': '知识构建器已启动',
    'sb.kb.toast.startedDesc': '填写您所了解的信息——不清楚的内容可以跳过。',
    'sb.kb.toast.couldNotStart': '无法启动会话',
    'sb.kb.toast.progressSaved': '进度已保存',
    'sb.kb.toast.savedFields': '已保存 {count} 个字段。',
    'sb.kb.toast.nothingToSave': '没有新的内容可保存。',
    'sb.kb.toast.saveFailed': '保存失败',
    'sb.kb.toast.validationFailed': '验证失败',
    'sb.kb.toast.draftsGenerated': '草稿 FAQ 已生成',
    'sb.kb.toast.draftsGeneratedDesc': '已创建 {count} 条草稿/建议项（仅供内部）。',
    'sb.kb.toast.generationFailed': '生成失败',
    'sb.kb.toast.providerUnavailable': 'AI 生成服务提供方当前不可用。未创建任何草稿。请稍后重试。',
    'sb.kb.toast.loadGenFailed': '无法加载已生成的 FAQ',
    'sb.kb.toast.approved': '已批准',
    'sb.kb.toast.approvedDesc': '此 FAQ 现已立即在 AI Chat 和 Voice 中上线。',
    'sb.kb.toast.approvalFailed': '批准失败',
    'sb.kb.toast.rejected': '已拒绝',
    'sb.kb.toast.rejectedDesc': 'AI 将不会使用此草稿。',
    'sb.kb.toast.rejectFailed': '拒绝失败',
    'sb.kb.toast.archived': '已归档',
    'sb.kb.toast.archivedDesc': '该项已停用（从 AI 检索中排除）。',
    'sb.kb.toast.archiveFailed': '归档失败',
    'sb.kb.toast.draftUpdated': '草稿已更新',
    'sb.kb.toast.draftUpdatedDesc': '您的编辑已保存。批准前仍为内部内容。',
    'sb.kb.toast.updateFailed': '更新失败',
    'sb.kb.toast.draftCreated': '草稿 FAQ 已创建',
    'sb.kb.toast.draftCreatedDesc': '已作为草稿（内部）创建。批准后即可上线。',
    'sb.kb.toast.couldNotCreate': '无法创建草稿',
    'sb.live.approvalsImmediate': '批准立即生效——无需重新训练延迟',
    'sb.live.approvalsBlurb': '已批准的解决方案可立即供 AI Chat 和 AI Voice 使用。由于 Servisfy 在每次 AI 交互时都会实时读取已批准的 SolutionBank 知识，因此无需重新训练延迟。',
    'sb.live.aiChatLive': 'AI Chat：已上线',
    'sb.live.aiVoiceLive': 'AI Voice：已上线',
    'sb.live.business': '业务：{name}',
    'sb.live.liveInAi': '已在 AI 中上线（已批准）',
    'sb.live.usedByAiNow': '现由 AI Chat 和 Voice 使用',
    'sb.live.pendingApproval': '待批准',
    'sb.live.notYetUsed': '尚未被 AI 使用',
    'sb.live.draftsSuggested': '草稿 / 建议',
    'sb.live.internalOnly': '仅供内部（知识构建器）',
    'sb.live.inactive': '未启用',
    'sb.live.rejectedRevoked': '已拒绝 / 已撤销 / 已归档',
    'sb.live.totalUsage': 'AI 使用总量（已批准）',
    'sb.live.realUsageSum': '真实 usage_count 总和',
    'sb.live.avgSuccess': '平均成功率',
    'sb.live.noUsageYet': '尚未记录任何使用',
    'sb.live.acrossUsed': '在已使用的已批准解决方案中',
    'sb.live.lastApproved': '最近批准',
    'sb.live.effectiveImmediately': '立即生效',
    'sb.live.noApprovalsYet': '尚无批准',
    'sb.live.topUsed': '使用最多的已批准解决方案',
    'sb.live.topUsedEmpty': '尚无已批准的解决方案被 AI 使用。',
    'sb.live.recentlyApproved': '近期批准',
    'sb.live.recentlyApprovedEmpty': '尚无已批准的解决方案。请在审核中批准以启用。',
    'sb.live.lowSuccess': '已批准但成功率偏低——可能需要审核',
    'sb.live.lowSuccessEmpty': '没有成功率偏低的已批准解决方案。👍',
    'sb.live.lowSuccessHint': '被 AI 使用且成功率低于 {threshold}% 的已批准解决方案。',
    'sb.live.loading': '正在加载实时 AI 知识…',
    'sb.live.loadError': '无法加载 AI 知识',
    'sb.live.tryAgain': '重试',
    'sb.live.loadFailedToast': '加载 AI 知识失败',
    'sb.live.untitled': '无标题',
    'sb.live.uncategorized': '未分类',
    'sb.review.title': '解决方案库审核',
    'sb.review.subtitle': '审核并批准为解决方案库提供的 AI 建议解决方案',
    'sb.review.pendingReview': '待审核',
    'sb.review.approvedTab': '已批准',
    'sb.review.rejectedTab': '已拒绝',
    'sb.review.noPending': '没有待审核的解决方案',
    'sb.review.noPendingSub': 'AI 建议的解决方案将显示在此处以供批准',
    'sb.review.noApproved': '没有已批准的解决方案',
    'sb.review.noRejected': '没有已拒绝的解决方案',
    'sb.review.description': '描述：',
    'sb.review.resolutionSteps': '解决步骤：',
    'sb.review.keywords': '关键词：',
    'sb.review.submittedBy': '由 {name} 提交',
    'sb.review.staff': '员工',
    'sb.review.aiVoice': 'AI Voice',
    'sb.review.aiChat': 'AI Chat',
    'sb.review.ch.humanChat': '人工聊天',
    'sb.review.ch.voiceCall': '语音通话',
    'sb.review.ch.humanCall': '人工通话',
    'sb.review.ch.solutionBank': '解决方案库',
    'sb.review.ch.unknown': '未知',
    'sb.review.aiGenerated': 'AI 生成',
    'sb.review.openaiFallback': 'OpenAI 备用',
    'sb.review.sourceConversation': '来源：对话 {id}',
    'sb.review.confHigh': '高（{pct}%）',
    'sb.review.confMedium': '中（{pct}%）',
    'sb.review.confLow': '低（{pct}%）',
    'sb.review.activeInAi': '在 AI 中启用',
    'sb.review.approvedOn': '批准于 {date}',
    'sb.review.rejectedOn': '拒绝于 {date}',
    'sb.review.revoke': '撤销',
    'sb.review.resubmit': '重新提交审核',
    'sb.review.unknownDate': '未知',
    'sb.review.loadFailedDesc': '请检查您的连接并重试。',
    'sb.review.toast.approved': '解决方案已批准',
    'sb.review.toast.approvedDesc': '解决方案现已在 AI 知识库中上线。',
    'sb.review.toast.approvalFailed': '批准失败',
    'sb.review.toast.rejected': '解决方案已拒绝',
    'sb.review.toast.rejectedDesc': 'AI 将不会使用此解决方案。',
    'sb.review.toast.rejectionFailed': '拒绝失败',
    'sb.review.toast.revoked': '解决方案已撤销',
    'sb.review.toast.revokedDesc': 'AI 将不再使用此解决方案。',
    'sb.review.toast.revokeFailed': '撤销失败',
    'sb.review.toast.resubmitted': '解决方案已重新提交',
    'sb.review.toast.resubmittedDesc': '解决方案再次处于待审核状态。',
    'sb.review.toast.resubmitFailed': '重新提交失败',
    'sb.review.toast.deleted': '解决方案已删除',
    'sb.review.toast.deletedDesc': '解决方案已永久移除。',
    'sb.review.toast.deleteFailed': '删除失败',
  },
  ar: {
    'sb.tab.solutions': 'الحلول',
    'sb.tab.knowledgeBuilder': 'منشئ المعرفة بالذكاء الاصطناعي',
    'sb.tab.review': 'المراجعة',
    'sb.tab.analytics': 'التحليلات',
    'sb.tab.liveAi': 'المعرفة المباشرة للذكاء الاصطناعي',
    'sb.tab.addSolution': 'إضافة حل',
    'sb.tab.manageSolutions': 'إدارة الحلول',
    'sb.exporting': 'جارٍ التصدير...',
    'sb.importing': 'جارٍ الاستيراد...',
    'sb.searchPlaceholder': 'البحث في الحلول...',
    'sb.filterSolutions': 'تصفية الحلول',
    'sb.filterSolutionsDesc': 'تضييق نطاق الحلول حسب المعايير',
    'sb.difficulty': 'مستوى الصعوبة',
    'sb.allDifficulties': 'جميع مستويات الصعوبة',
    'sb.easy': 'سهل',
    'sb.medium': 'متوسط',
    'sb.hard': 'صعب',
    'sb.reset': 'إعادة تعيين',
    'sb.apply': 'تطبيق',
    'sb.allCategories': 'جميع الفئات',
    'sb.revoke.action': 'إلغاء الاعتماد',
    'sb.revoke.confirm': 'إلغاء اعتماد هذا الحل؟ لن يستخدمه الذكاء الاصطناعي حتى يتم تصحيحه واعتماده من جديد.',
    'sb.revoke.done': 'تم إلغاء اعتماد الحل',
    'sb.revoke.doneDesc': 'لم يعد الذكاء الاصطناعي يستخدمه. عدّله ثم أعد إرساله للمراجعة.',
    'sb.revoke.failed': 'تعذّر إلغاء الاعتماد',
    'sb.cat.choose': 'اختر فئة',
    'sb.cat.noneConfigured': 'لا توجد فئات مُهيأة',
    'sb.cat.noneConfiguredHint': 'أضف فئات في إدارة الفئات قبل إنشاء الحلول.',
    'sb.cat.technicalSupport': 'الدعم الفني',
    'sb.cat.billing': 'الفوترة',
    'sb.cat.productIssues': 'مشكلات المنتجات',
    'sb.cat.accountManagement': 'إدارة الحسابات',
    'sb.totalSolutions': 'إجمالي الحلول',
    'sb.avgSuccessRate': 'متوسط معدل النجاح',
    'sb.totalUsage': 'إجمالي الاستخدام',
    'sb.categories': 'الفئات',
    'sb.topPerforming': 'الحلول الأعلى أداءً',
    'sb.mostUsed': 'الحلول الأكثر استخدامًا',
    'sb.loadingSolutions': 'جارٍ تحميل الحلول...',
    'sb.loadingSolutionsSub': 'يرجى الانتظار بينما نجلب بنك الحلول الخاص بك',
    'sb.noSolutionsFound': 'لم يتم العثور على حلول',
    'sb.noSolutionsHint': 'حاول تعديل معايير البحث أو استخدم علامة تبويب الإدارة لإضافة الحلول',
    'sb.mgmtFunctionsHint': 'وظائف إدارة الحلول متاحة في علامة تبويب الإدارة',
    'sb.successRateBadge': 'معدل نجاح {rate}%',
    'sb.usesBadge': '{count} استخدام',
    'sb.usesLower': '{count} استخدام',
    'sb.updated': 'آخر تحديث: {date}',
    'sb.quickTips': 'نصائح سريعة',
    'sb.tip1': 'كن محددًا وواضحًا في أوصاف الحلول',
    'sb.tip2': 'ضمّن تعليمات خطوة بخطوة',
    'sb.tip3': 'صنّف الحلول بكلمات مفتاحية ذات صلة',
    'sb.tip4': 'أشر إلى الحلول ذات الصلة للمشكلات المعقدة',
    'sb.recentActivity': 'النشاط الأخير',
    'sb.noRecentActivity': 'لا يوجد نشاط حديث بعد.',
    'sb.solutionsAddedToday': 'الحلول المضافة اليوم:',
    'sb.pendingReviewLabel': 'بانتظار المراجعة:',
    'sb.successRateLabel': 'معدل النجاح:',
    'sb.bulkOperations': 'العمليات المجمّعة',
    'sb.importFromCsv': 'استيراد الحلول من ملف CSV',
    'sb.exportAll': 'تصدير جميع الحلول',
    'sb.generateReport': 'إنشاء تقرير الأداء',
    'sb.importCsvDesc': 'حمّل ملف CSV لاستيراد حلول متعددة. يجب أن يحتوي ملف CSV على الأعمدة: title, description, category, tags, resolution, difficulty, success_rate, usage_count.',
    'sb.selectCsv': 'اختر ملف CSV للتحميل',
    'sb.selectedFile': 'المحدد: {name}',
    'sb.importSolutionsBtn': 'استيراد الحلول',
    'sb.exportAllDesc': 'اختر صيغة التصدير المفضلة لديك لجميع الحلول',
    'sb.csvFile': 'ملف CSV',
    'sb.excelFile': 'ملف Excel',
    'sb.jsonData': 'بيانات JSON',
    'sb.pdfReport': 'تقرير PDF',
    'sb.performanceReport': 'تقرير أداء بنك الحلول',
    'sb.performanceReportDesc': 'رؤى وتحليلات أداء مُنشأة بالذكاء الاصطناعي',
    'sb.aiAnalysisReport': 'تقرير تحليل الذكاء الاصطناعي',
    'sb.downloadReport': 'تنزيل التقرير',
    'sb.editSolution': 'تعديل الحل',
    'sb.editSolutionDesc': 'تحديث معلومات الحل',
    'sb.addNewSolutionTitle': 'إضافة حل جديد',
    'sb.addNewSolutionDesc': 'إنشاء حل جديد لبنك الحلول الخاص بك',
    'sb.solutionTitle': 'عنوان الحل *',
    'sb.solutionTitlePlaceholder': 'أدخل عنوان الحل',
    'sb.descriptionLabel': 'الوصف *',
    'sb.descriptionPlaceholder': 'صف المشكلة التي يعالجها هذا الحل',
    'sb.category': 'الفئة',
    'sb.tagsLabel': 'الوسوم (مفصولة بفواصل)',
    'sb.tagsPlaceholder': 'كلمة المرور، إعادة تعيين، تسجيل الدخول',
    'sb.resolutionSteps': 'خطوات الحل',
    'sb.resolutionPlaceholder': 'أدخل تعليمات الحل خطوة بخطوة',
    'sb.updateSolution': 'تحديث الحل',
    'sb.addSolutionBtn': 'إضافة حل',
    'sb.deleteSolution': 'حذف الحل',
    'sb.deleteSolutionDesc': 'هل أنت متأكد من رغبتك في حذف هذا الحل؟ لا يمكن التراجع عن هذا الإجراء.',
    'sb.toast.loadFailed': 'فشل تحميل الحلول',
    'sb.toast.missingInfo': 'معلومات ناقصة',
    'sb.toast.missingInfoDesc': 'يرجى تعبئة العنوان والوصف',
    'sb.toast.submitted': 'تم إرسال الحل',
    'sb.toast.submittedDesc': 'تم إرسال الحل إلى المدير للموافقة عليه',
    'sb.toast.addFailed': 'فشل إضافة الحل',
    'sb.toast.exportStarted': 'بدأ التصدير',
    'sb.toast.exportStartedDesc': 'جارٍ تصدير الحلول بصيغة {format}',
    'sb.toast.exportComplete': 'اكتمل التصدير',
    'sb.toast.exportCompleteDesc': 'تم تصدير الحلول بنجاح بصيغة {format}',
    'sb.toast.exportFailed': 'فشل التصدير',
    'sb.toast.exportFailedDesc': 'فشل تصدير الحلول',
    'sb.toast.noFile': 'لم يتم تحديد ملف',
    'sb.toast.noFileDesc': 'يرجى تحديد ملف CSV للاستيراد',
    'sb.toast.importStarted': 'بدأ الاستيراد',
    'sb.toast.importStartedDesc': 'جارٍ معالجة ملف CSV...',
    'sb.toast.importComplete': 'اكتمل الاستيراد',
    'sb.toast.importCompleteDesc': 'تم استيراد الحلول بنجاح',
    'sb.toast.importFailed': 'فشل الاستيراد',
    'sb.toast.importFailedDesc': 'فشل استيراد الحلول من ملف CSV',
    'sb.toast.invalidFile': 'ملف غير صالح',
    'sb.toast.invalidFileDesc': 'يرجى تحديد ملف CSV صالح',
    'sb.toast.reportGenerated': 'تم إنشاء التقرير',
    'sb.toast.reportGeneratedDesc': 'تم إنشاء تقرير الأداء بنجاح',
    'sb.toast.reportFailed': 'فشل إنشاء التقرير',
    'sb.toast.reportFailedDesc': 'فشل إنشاء تقرير الأداء',
    'sb.toast.updated': 'تم تحديث الحل',
    'sb.toast.updatedDesc': 'تم تحديث الحل بنجاح',
    'sb.toast.updateFailed': 'فشل تحديث الحل',
    'sb.toast.deleted': 'تم حذف الحل',
    'sb.toast.deletedDesc': 'تم حذف الحل بنجاح',
    'sb.toast.deleteFailed': 'فشل حذف الحل',
    'sb.kb.title': 'منشئ المعرفة بالذكاء الاصطناعي',
    'sb.kb.subtitle': 'أدخل معلومات نشاطك التجاري ← أنشئ 50 سؤالًا شائعًا مسودةً ← راجع ووافق. تبقى المسودات داخلية حتى تتم الموافقة عليها.',
    'sb.kb.newSession': 'جلسة جديدة',
    'sb.kb.buildTitle': 'ابنِ قاعدة معرفة الذكاء الاصطناعي الخاصة بك',
    'sb.kb.buildBlurb': 'أجب عمّا تعرفه عن نشاطك التجاري. سننشئ 50 سؤالًا شائعًا مسودةً/مقترحًا. لن يُنشر أي شيء للعملاء حتى يوافق عليه مدير أو مسؤول.',
    'sb.kb.start': 'بدء منشئ المعرفة',
    'sb.kb.tab.intake': 'الإدخال',
    'sb.kb.tab.validate': 'التحقق',
    'sb.kb.tab.review': 'الإنشاء والمراجعة',
    'sb.kb.tab.coverage': 'التغطية',
    'sb.kb.tab.gaps': 'الفجوات',
    'sb.kb.skipHint': 'تخطَّ أي شيء غير معروف — لا يتم اختلاق الحقول غير المعروفة أبدًا.',
    'sb.kb.saveProgress': 'حفظ التقدم',
    'sb.kb.blankUnknown': 'اتركه فارغًا إذا كان غير معروف',
    'sb.kb.blankUnknownTextarea': 'اتركه فارغًا إذا كان غير معروف — لن يتم اختلاقه',
    'sb.kb.unknownSkip': 'غير معروف / تخطٍّ',
    'sb.kb.sec.identity': 'هوية النشاط التجاري',
    'sb.kb.sec.profile': 'ملف النشاط التجاري',
    'sb.kb.sec.operations': 'العمليات',
    'sb.kb.sec.payments': 'المدفوعات',
    'sb.kb.sec.delivery': 'الشحن / التوصيل',
    'sb.kb.sec.booking': 'الحجز',
    'sb.kb.sec.returns': 'الإرجاع / استرداد الأموال',
    'sb.kb.sec.support': 'الدعم',
    'sb.kb.sec.policies': 'السياسات',
    'sb.kb.sec.extras': 'أسئلة العملاء والملاحظات',
    'sb.kb.f.business_name': 'اسم النشاط التجاري',
    'sb.kb.f.address': 'العنوان',
    'sb.kb.f.city': 'المدينة',
    'sb.kb.f.province': 'المحافظة / الولاية',
    'sb.kb.f.country': 'الدولة',
    'sb.kb.f.phone': 'الهاتف',
    'sb.kb.f.email': 'البريد الإلكتروني',
    'sb.kb.f.website': 'الموقع الإلكتروني',
    'sb.kb.f.social_media': 'وسائل التواصل الاجتماعي',
    'sb.kb.f.business_description': 'وصف النشاط التجاري',
    'sb.kb.f.products': 'المنتجات المباعة',
    'sb.kb.f.services': 'الخدمات المقدَّمة',
    'sb.kb.f.target_customers': 'العملاء المستهدفون',
    'sb.kb.f.industries_served': 'القطاعات المخدومة',
    'sb.kb.f.differentiator': 'عامل التمييز',
    'sb.kb.f.mission_vision': 'الرسالة / الرؤية',
    'sb.kb.f.opening_days': 'أيام العمل',
    'sb.kb.f.operating_hours': 'ساعات العمل',
    'sb.kb.f.holiday_hours': 'ساعات العمل في العطلات',
    'sb.kb.f.appointment_required': 'الموعد مطلوب',
    'sb.kb.f.walk_ins_accepted': 'قبول الزيارات دون موعد',
    'sb.kb.f.delivery_available': 'التوصيل متاح',
    'sb.kb.f.pickup_available': 'الاستلام متاح',
    'sb.kb.f.online_orders': 'الطلبات عبر الإنترنت',
    'sb.kb.f.service_area': 'نطاق الخدمة',
    'sb.kb.f.payment_methods': 'طرق الدفع المقبولة',
    'sb.kb.f.delivery_locations': 'مواقع التوصيل',
    'sb.kb.f.delivery_fees': 'رسوم التوصيل',
    'sb.kb.f.delivery_timelines': 'مواعيد التوصيل',
    'sb.kb.f.same_day_delivery': 'التوصيل في نفس اليوم',
    'sb.kb.f.international_shipping': 'الشحن الدولي',
    'sb.kb.f.booking_method': 'كيفية حجز المواعيد',
    'sb.kb.f.cancellation_policy': 'سياسة الإلغاء',
    'sb.kb.f.rescheduling_policy': 'سياسة إعادة الجدولة',
    'sb.kb.f.late_arrival_policy': 'سياسة التأخر في الوصول',
    'sb.kb.f.ordering_method': 'كيفية تقديم الطلبات عبر الإنترنت',
    'sb.kb.f.return_policy': 'سياسة الإرجاع',
    'sb.kb.f.refund_policy': 'سياسة استرداد الأموال',
    'sb.kb.f.exchange_policy': 'سياسة الاستبدال',
    'sb.kb.f.contact_methods': 'طرق التواصل الأساسية',
    'sb.kb.f.support_phone': 'هاتف الدعم',
    'sb.kb.f.support_whatsapp': 'WhatsApp',
    'sb.kb.f.support_email': 'البريد الإلكتروني للدعم',
    'sb.kb.f.support_website': 'الموقع الإلكتروني للدعم',
    'sb.kb.f.support_in_person': 'الدعم الشخصي',
    'sb.kb.f.privacy_policy': 'سياسة الخصوصية',
    'sb.kb.f.warranty_policy': 'سياسة الضمان',
    'sb.kb.f.terms_of_service': 'شروط الخدمة',
    'sb.kb.f.service_guarantees': 'ضمانات الخدمة',
    'sb.kb.f.policies': 'ملخص السياسات العامة',
    'sb.kb.f.common_questions': 'الأسئلة الشائعة التي يطرحها العملاء',
    'sb.kb.f.special_instructions': 'ما يجب أن يعرفه العملاء دائمًا',
    'sb.kb.pay.cash': 'نقدًا',
    'sb.kb.pay.debit': 'بطاقة خصم',
    'sb.kb.pay.credit': 'بطاقة ائتمان',
    'sb.kb.pay.bankTransfer': 'تحويل بنكي',
    'sb.kb.pay.applePay': 'Apple Pay',
    'sb.kb.pay.googlePay': 'Google Pay',
    'sb.kb.pay.payPal': 'PayPal',
    'sb.kb.pay.mobileMoney': 'المحفظة عبر الهاتف',
    'sb.kb.pay.interac': 'Interac e-Transfer',
    'sb.kb.pay.other': 'أخرى',
    'sb.kb.revalidate': 'إعادة التحقق',
    'sb.kb.runValidationHint': 'شغّل التحقق من علامة تبويب الإدخال لعرض مدى الجاهزية.',
    'sb.kb.readiness': 'الجاهزية',
    'sb.kb.readinessHint': 'لا يتم حظر الإنشاء أبدًا. تتحول الحقائق الخاصة بالنشاط التجاري المفقودة إلى سؤال شائع مقترح أو إلى الإجابة "يرجى تأكيد هذه السياسة مع النشاط التجاري." — ولا يتم اختلاقها أبدًا.',
    'sb.kb.val.missingFields': 'الحقول المفقودة',
    'sb.kb.val.missingFieldsEmpty': 'لا يوجد أي عنصر مطلوب مفقود.',
    'sb.kb.val.contradictions': 'التناقضات',
    'sb.kb.val.contradictionsEmpty': 'لم يتم العثور على تناقضات.',
    'sb.kb.val.vague': 'الإجابات الغامضة',
    'sb.kb.val.vagueEmpty': 'لا توجد إجابات غامضة.',
    'sb.kb.val.followups': 'أسئلة المتابعة',
    'sb.kb.val.followupsEmpty': 'لا توجد أسئلة متابعة.',
    'sb.kb.completeMore': '← أكمل المزيد من الحقول',
    'sb.kb.continueGenerate': 'المتابعة إلى الإنشاء ←',
    'sb.kb.generate50': 'إنشاء 50 سؤالًا شائعًا مسودةً',
    'sb.kb.generate50Sub': 'يشغّل التحقق والإنشاء بالذكاء الاصطناعي عبر الخادم الحقيقي. تبقى المسودات داخلية فقط حتى تتم الموافقة عليها.',
    'sb.kb.generating': 'جارٍ الإنشاء…',
    'sb.kb.internalVsLive': 'المسودة والمقترح = داخلي فقط. المعتمد = مباشر للدردشة/الصوت بالذكاء الاصطناعي.',
    'sb.kb.showArchived': 'عرض المؤرشف',
    'sb.kb.noGeneratedYet': 'لا توجد أسئلة شائعة مُنشأة بعد. انقر على إنشاء 50 سؤالًا شائعًا مسودةً أعلاه.',
    'sb.kb.status.draft': 'مسودة · داخلي فقط',
    'sb.kb.status.suggested': 'مقترح · داخلي فقط',
    'sb.kb.status.pending': 'بانتظار الموافقة · داخلي',
    'sb.kb.status.approved': 'معتمد · مباشر للذكاء الاصطناعي',
    'sb.kb.status.archived': 'مؤرشف',
    'sb.kb.status.rejected': 'مرفوض',
    'sb.kb.ownerSupplied': 'مُقدَّم من المالك',
    'sb.kb.aiSuggested': 'مقترح بالذكاء الاصطناعي',
    'sb.kb.totalGenerated': 'إجمالي ما تم إنشاؤه',
    'sb.kb.customerFacing': 'موجَّه للعملاء (معتمد)',
    'sb.kb.internalDraft': 'داخلي (مسودة/مقترح)',
    'sb.kb.categoryCoverage': 'تغطية الفئات',
    'sb.kb.missing': 'مفقود',
    'sb.kb.missingCategories': 'الفئات المفقودة: {cats}. أنشئ (أو أضف المزيد من الإدخال) لتغطيتها.',
    'sb.kb.gapsIntro': 'أسئلة العملاء غير المُجابة المسجَّلة لهذا النشاط التجاري.',
    'sb.kb.noGaps': 'لم يتم تسجيل أي فجوات معرفية.',
    'sb.kb.createDraftFaq': 'إنشاء سؤال شائع مسودةً',
    'sb.kb.gapsLoadError': 'تعذّر تحميل الفجوات المعرفية: {error}',
    'sb.kb.seenTimes': 'شوهد {count}× · {status}',
    'sb.kb.editDraftFaq': 'تعديل مسودة السؤال الشائع',
    'sb.kb.createDraftFromGap': 'إنشاء مسودة سؤال شائع من الفجوة',
    'sb.kb.question': 'السؤال',
    'sb.kb.answer': 'الإجابة',
    'sb.kb.keywords': 'الكلمات المفتاحية (مفصولة بفواصل)',
    'sb.kb.answerPlaceholder': 'اكتب الإجابة. اترك التفاصيل الخاصة بالمستأجر خارجها إذا كانت غير معروفة.',
    'sb.kb.editInternalHint': 'يبقي التعديل هذا العنصر داخليًا. وافق عليه بشكل منفصل لجعله مباشرًا.',
    'sb.kb.createdDraftHint': 'تم إنشاؤه كمسودة (داخلي). وافق عليه ليصبح مباشرًا.',
    'sb.kb.saveDraft': 'حفظ المسودة',
    'sb.kb.createDraft': 'إنشاء مسودة',
    'sb.kb.noBusinessContext': 'لا يوجد سياق نشاط تجاري نشط',
    'sb.kb.selectBusiness': 'حدد أو اربط نشاطًا تجاريًا لاستخدام منشئ المعرفة.',
    'sb.kb.toast.noBusiness': 'لم يتم تحديد نشاط تجاري',
    'sb.kb.toast.noBusinessDesc': 'تعذّر البدء — لا يوجد سياق نشاط تجاري نشط.',
    'sb.kb.toast.started': 'بدأ منشئ المعرفة',
    'sb.kb.toast.startedDesc': 'املأ ما تعرفه — يمكنك تخطي أي شيء غير معروف.',
    'sb.kb.toast.couldNotStart': 'تعذّر بدء الجلسة',
    'sb.kb.toast.progressSaved': 'تم حفظ التقدم',
    'sb.kb.toast.savedFields': 'تم حفظ {count} حقل/حقول.',
    'sb.kb.toast.nothingToSave': 'لا يوجد جديد لحفظه.',
    'sb.kb.toast.saveFailed': 'فشل الحفظ',
    'sb.kb.toast.validationFailed': 'فشل التحقق',
    'sb.kb.toast.draftsGenerated': 'تم إنشاء الأسئلة الشائعة المسودة',
    'sb.kb.toast.draftsGeneratedDesc': 'تم إنشاء {count} عنصر مسودة/مقترح (داخلي فقط).',
    'sb.kb.toast.generationFailed': 'فشل الإنشاء',
    'sb.kb.toast.providerUnavailable': 'مزوّد الإنشاء بالذكاء الاصطناعي غير متاح في الوقت الحالي. لم يتم إنشاء أي مسودات. يرجى المحاولة مرة أخرى لاحقًا.',
    'sb.kb.toast.loadGenFailed': 'تعذّر تحميل الأسئلة الشائعة المُنشأة',
    'sb.kb.toast.approved': 'تمت الموافقة',
    'sb.kb.toast.approvedDesc': 'أصبح هذا السؤال الشائع مباشرًا الآن للدردشة والصوت بالذكاء الاصطناعي فورًا.',
    'sb.kb.toast.approvalFailed': 'فشلت الموافقة',
    'sb.kb.toast.rejected': 'مرفوض',
    'sb.kb.toast.rejectedDesc': 'لن يستخدم الذكاء الاصطناعي هذه المسودة.',
    'sb.kb.toast.rejectFailed': 'فشل الرفض',
    'sb.kb.toast.archived': 'مؤرشف',
    'sb.kb.toast.archivedDesc': 'تم سحب العنصر (مستبعَد من استرجاع الذكاء الاصطناعي).',
    'sb.kb.toast.archiveFailed': 'فشلت الأرشفة',
    'sb.kb.toast.draftUpdated': 'تم تحديث المسودة',
    'sb.kb.toast.draftUpdatedDesc': 'تم حفظ تعديلك. لا يزال داخليًا حتى تتم الموافقة عليه.',
    'sb.kb.toast.updateFailed': 'فشل التحديث',
    'sb.kb.toast.draftCreated': 'تم إنشاء مسودة السؤال الشائع',
    'sb.kb.toast.draftCreatedDesc': 'تم إنشاؤه كمسودة (داخلي). وافق عليه ليصبح مباشرًا.',
    'sb.kb.toast.couldNotCreate': 'تعذّر إنشاء المسودة',
    'sb.live.approvalsImmediate': 'تسري الموافقات فورًا — دون أي تأخير لإعادة التدريب',
    'sb.live.approvalsBlurb': 'تتوفر الحلول المعتمدة للدردشة والصوت بالذكاء الاصطناعي فورًا. لا حاجة لأي تأخير لإعادة التدريب لأن Servisfy يقرأ معرفة بنك الحلول المعتمدة مباشرةً في كل تفاعل مع الذكاء الاصطناعي.',
    'sb.live.aiChatLive': 'دردشة الذكاء الاصطناعي: مباشر',
    'sb.live.aiVoiceLive': 'صوت الذكاء الاصطناعي: مباشر',
    'sb.live.business': 'النشاط التجاري: {name}',
    'sb.live.liveInAi': 'مباشر في الذكاء الاصطناعي (معتمد)',
    'sb.live.usedByAiNow': 'مستخدَم من الدردشة والصوت بالذكاء الاصطناعي الآن',
    'sb.live.pendingApproval': 'بانتظار الموافقة',
    'sb.live.notYetUsed': 'لم يُستخدم بعد من الذكاء الاصطناعي',
    'sb.live.draftsSuggested': 'المسودات / المقترحة',
    'sb.live.internalOnly': 'داخلي فقط (منشئ المعرفة)',
    'sb.live.inactive': 'غير نشط',
    'sb.live.rejectedRevoked': 'مرفوض / مسحوب / مؤرشف',
    'sb.live.totalUsage': 'إجمالي استخدام الذكاء الاصطناعي (معتمد)',
    'sb.live.realUsageSum': 'مجموع usage_count الفعلي',
    'sb.live.avgSuccess': 'متوسط معدل النجاح',
    'sb.live.noUsageYet': 'لم يُسجَّل أي استخدام بعد',
    'sb.live.acrossUsed': 'عبر الحلول المعتمدة المستخدمة',
    'sb.live.lastApproved': 'آخر موافقة',
    'sb.live.effectiveImmediately': 'ساري المفعول فورًا',
    'sb.live.noApprovalsYet': 'لا توجد موافقات بعد',
    'sb.live.topUsed': 'الحلول المعتمدة الأكثر استخدامًا',
    'sb.live.topUsedEmpty': 'لم يستخدم الذكاء الاصطناعي أي حلول معتمدة بعد.',
    'sb.live.recentlyApproved': 'تمت الموافقة عليها مؤخرًا',
    'sb.live.recentlyApprovedEmpty': 'لا توجد حلول معتمدة بعد. وافق من المراجعة للتفعيل.',
    'sb.live.lowSuccess': 'معتمد لكن بمعدل نجاح منخفض — قد يحتاج إلى مراجعة',
    'sb.live.lowSuccessEmpty': 'لا توجد حلول معتمدة بمعدل نجاح منخفض. 👍',
    'sb.live.lowSuccessHint': 'الحلول المعتمدة المستخدمة من الذكاء الاصطناعي بمعدل نجاح أقل من {threshold}%.',
    'sb.live.loading': 'جارٍ تحميل المعرفة المباشرة للذكاء الاصطناعي…',
    'sb.live.loadError': 'تعذّر تحميل معرفة الذكاء الاصطناعي',
    'sb.live.tryAgain': 'حاول مرة أخرى',
    'sb.live.loadFailedToast': 'فشل تحميل معرفة الذكاء الاصطناعي',
    'sb.live.untitled': 'بلا عنوان',
    'sb.live.uncategorized': 'غير مصنَّف',
    'sb.review.title': 'مراجعة بنك الحلول',
    'sb.review.subtitle': 'راجع ووافق على الحلول المقترحة بالذكاء الاصطناعي لبنك الحلول',
    'sb.review.pendingReview': 'بانتظار المراجعة',
    'sb.review.approvedTab': 'معتمد',
    'sb.review.rejectedTab': 'مرفوض',
    'sb.review.noPending': 'لا توجد حلول بانتظار المراجعة',
    'sb.review.noPendingSub': 'ستظهر الحلول المقترحة بالذكاء الاصطناعي هنا للموافقة عليها',
    'sb.review.noApproved': 'لا توجد حلول معتمدة',
    'sb.review.noRejected': 'لا توجد حلول مرفوضة',
    'sb.review.description': 'الوصف:',
    'sb.review.resolutionSteps': 'خطوات الحل:',
    'sb.review.keywords': 'الكلمات المفتاحية:',
    'sb.review.submittedBy': 'مُقدَّم من {name}',
    'sb.review.staff': 'موظف',
    'sb.review.aiVoice': 'صوت الذكاء الاصطناعي',
    'sb.review.aiChat': 'دردشة الذكاء الاصطناعي',
    'sb.review.ch.humanChat': 'محادثة بشرية',
    'sb.review.ch.voiceCall': 'مكالمة صوتية',
    'sb.review.ch.humanCall': 'مكالمة مع موظف',
    'sb.review.ch.solutionBank': 'بنك الحلول',
    'sb.review.ch.unknown': 'غير معروف',
    'sb.review.aiGenerated': 'مُنشأ بالذكاء الاصطناعي',
    'sb.review.openaiFallback': 'احتياطي OpenAI',
    'sb.review.sourceConversation': 'المصدر: المحادثة {id}',
    'sb.review.confHigh': 'مرتفع ({pct}%)',
    'sb.review.confMedium': 'متوسط ({pct}%)',
    'sb.review.confLow': 'منخفض ({pct}%)',
    'sb.review.activeInAi': 'نشط في الذكاء الاصطناعي',
    'sb.review.approvedOn': 'تمت الموافقة {date}',
    'sb.review.rejectedOn': 'تم الرفض {date}',
    'sb.review.revoke': 'إلغاء',
    'sb.review.resubmit': 'إعادة الإرسال للمراجعة',
    'sb.review.unknownDate': 'غير معروف',
    'sb.review.loadFailedDesc': 'تحقق من اتصالك وحاول مرة أخرى.',
    'sb.review.toast.approved': 'تمت الموافقة على الحل',
    'sb.review.toast.approvedDesc': 'أصبح الحل الآن مباشرًا في قاعدة معرفة الذكاء الاصطناعي.',
    'sb.review.toast.approvalFailed': 'فشلت الموافقة',
    'sb.review.toast.rejected': 'تم رفض الحل',
    'sb.review.toast.rejectedDesc': 'لن يستخدم الذكاء الاصطناعي هذا الحل.',
    'sb.review.toast.rejectionFailed': 'فشل الرفض',
    'sb.review.toast.revoked': 'تم إلغاء الحل',
    'sb.review.toast.revokedDesc': 'لن يستخدم الذكاء الاصطناعي هذا الحل بعد الآن.',
    'sb.review.toast.revokeFailed': 'فشل الإلغاء',
    'sb.review.toast.resubmitted': 'تمت إعادة إرسال الحل',
    'sb.review.toast.resubmittedDesc': 'الحل بانتظار المراجعة مرة أخرى.',
    'sb.review.toast.resubmitFailed': 'فشلت إعادة الإرسال',
    'sb.review.toast.deleted': 'تم حذف الحل',
    'sb.review.toast.deletedDesc': 'تمت إزالة الحل نهائيًا.',
    'sb.review.toast.deleteFailed': 'فشل الحذف',
  },
};

export default solutionBankTranslations;
